import {
  AUFGABEN_GROESSEN,
  brauchtAbnahme,
  darfErledigen,
  darfZielstatusTragen,
  entstanden,
  GROESSE_LABELS,
  istEigeneQuelle,
  istGast,
  istMitOwner,
  nurVorraete,
  type Aufgabengroesse,
  ZEITFENSTER,
  ZEITFENSTER_KURZ,
  ZEITFENSTER_LABELS,
  TASK_EVENT_TYPE_LABELS,
  type TaskEventType,
  TASK_PRIORITIES,
  TASK_STATUS_FLOW,
  vorgabeZuTag,
  WIEDERVORLAGE_TAGE,
  TASK_STATUS_LABELS,
  type TaskPriority,
  type TaskSource,
  type TaskStatus,
  zaehltAlsEntwickler,
  zeitfensterStand,
  unteraufgabenTitel,
} from '@netcon-work/shared'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { Camera, ChevronDown, ChevronRight, ExternalLink, Paperclip } from 'lucide-react'
import { useRef, useState, type ReactNode } from 'react'
import { useAufgabeOeffnen } from './AufgabeKontext'
import { AufgabeZusammenfuehren } from './AufgabeZusammenfuehren'
import { BenachrichtigenZeile } from './BenachrichtigenZeile'
import { PersonenOptionen } from './PersonenOptionen'
import { MarkierKnoepfe, SonnenMarke } from './Markierung'
import { PinKnopf } from './PinKnopf'
import { notizFrage, StatusNotiz } from './StatusNotiz'
import { anhangHochladen, anhangVerweis, dateienAus, ziehtDateien } from '../lib/anhang'
import { cn } from '../lib/cn'
import { dauerAngabe, dauerHerkunftText, dauerText, useWeltdauern } from '../lib/dauer'
import { Markdown } from '../lib/markdown'
import { KiLoesung } from './KiLoesung'
import { LesemodusKnopf } from './Lesemodus'
import {
  nebenBestandVerwerfen,
  type PlanungEingabe,
  usePlanungSetzen,
  usePoolsSetzen,
  useStatusSetzen,
} from '../lib/mutationen'
import {
  aufgabeDetailQuery,
  trpcFehlerCode,
  aufgabeRefsQuery,
  aufgabeVerlaufQuery,
  meilensteineQuery,
  poolsDerWeltQuery,
  sitzungQuery,
  syncZustandQuery,
  verteilEntwuerfeQuery,
  vorhabenDerWeltQuery,
  workspacesQuery,
} from '../lib/queries'
import { trpc } from '../lib/trpc'
import { MarkdownFeld } from './MarkdownFeld'
import { AufklappSelect, InlineWert } from './ui/InlineWert'
import { hintergrundSchliesst } from './ui/hintergrund'
import { Button, PrioBadge, SourceBadge } from './ui/primitives'
import { useErwaehnbarePersonen, usePersonenDieserWelt, usePersonenFuerAbnahme } from '../lib/welt'
import { NotizenZurAufgabeAbschnitt } from './NotizenVerknuepft'

/**
 * Aufgabendetail (Konzept Kap. 4.7, Phase 4 AP 4.5).
 *
 * Baut die schlanke Lesefassung aus Phase 2 aus, statt eine zweite Ansicht
 * daneben zu stellen: dieselbe Stelle, mehr Inhalt.
 *
 * ## Was hier bearbeitbar ist und was nicht
 *
 * **Unteraufgaben, Kommentare und Pools gehen an jeder Aufgabe** – auch an
 * importierten. Sie gibt es im Quellsystem nicht, ein GitLab-Issue in
 * Unterschritte zu gliedern ändert dort nichts ([E-23](../../../../docs/ENTSCHEIDUNGEN.md)).
 *
 * **Titel, Beschreibung und Priorität** sind bei importierten Aufgaben gesperrt –
 * der nächste Abgleich würde sie überschreiben. Die Ansicht sagt das mit einem
 * Satz, statt Felder stumm auszugrauen: ein gesperrtes Feld ohne Begründung liest
 * sich wie ein Fehler.
 *
 * **Der Status ist immer änderbar**, weil er in `work_status` landet und nicht in
 * die Quellspalte ([E-24](../../../../docs/ENTSCHEIDUNGEN.md)). Weicht er vom Quellstatus
 * ab, steht das dran – und die Zeile „Quellsystem" sagt, ob das Rückschreiben wartet,
 * gehindert ist oder gescheitert.
 */
export function AufgabeDetail({
  aufgabeId,
  schliessen,
}: {
  aufgabeId: string
  schliessen: () => void
}) {
  const {
    data: aufgabe,
    isLoading,
    error: ladeFehler,
    refetch: nochmalLaden,
  } = useQuery(aufgabeDetailQuery(aufgabeId))
  const { data: refs = [] } = useQuery(aufgabeRefsQuery(aufgabeId))

  /**
   * Uneinig heißt: **eine Quelle sagt erledigt, die andere nicht.** Verschiedene offene
   * Zustände (`bereit` gegen `in_arbeit`) sind kein Widerspruch, sondern zwei Systeme, die
   * unterschiedlich fein zählen – das jedes Mal zu melden wäre Lärm.
   */
  const uneinig = (() => {
    const mitStatus = refs.filter((r) => r.sourceStatus)
    if (mitStatus.length < 2) return false
    const fertig = mitStatus.filter((r) => r.sourceStatus === 'erledigt').length
    return fertig > 0 && fertig < mitStatus.length
  })()
  const { data: verlauf = [] } = useQuery(aufgabeVerlaufQuery(aufgabeId))

  /**
   * Die Pool-Beschriftungen für den Verlauf – der Zwischenspeicher ist beim Öffnen des
   * Detailfensters längst gefüllt, das kostet keine zweite Abfrage. Fehlt ein Pool (gelöscht,
   * abgeschaltet), bleibt seine Kennung stehen: „gezogen aus etwas, das es nicht mehr gibt"
   * ist die richtige Auskunft, eine leere Stelle wäre es nicht.
   */
  const { data: allePools = [] } = useQuery(poolsDerWeltQuery(aufgabe?.workspaceId))
  const poolName = (id: string): string => allePools.find((p) => p.id === id)?.label ?? id

  /**
   * Dasselbe für die Arbeitswelten – der Verlauf zeigt beim Umzug „Entwicklung → Marketing"
   * und nicht zwei Kennungen. Fehlt eine (stillgelegt, umbenannt), bleibt ihre Kennung stehen.
   */
  const { data: meineWelten = [] } = useQuery(workspacesQuery)
  const weltName = (id: string): string => meineWelten.find((w) => w.id === id)?.label ?? id

  /**
   * **Der Verlauf endet auf „erledigt", die Aufgabe steht trotzdem offen** (18.08.2026).
   *
   * Dieselbe Prüfung wie `verlaufUneinig` in `tasks.all`, hier aus dem bereits geladenen
   * Verlauf gerechnet statt als zweite Unterabfrage: die Ereignisse liegen absteigend vor,
   * das erste mit Zielstatus ist das jüngste.
   *
   * `status === quellStatus` heißt: Work hat nichts übersteuert. Wer eine erledigte Aufgabe
   * bewusst zurückholt, hat keinen Widerspruch erzeugt, sondern gesteuert – und bekäme
   * sonst einen roten Hinweis auf seine eigene Entscheidung.
   */
  const verlaufWiderspricht = (() => {
    if (!aufgabe || aufgabe.status === 'erledigt' || aufgabe.status !== aufgabe.quellStatus) {
      return false
    }
    const letzter = verlauf.find((e) => darfZielstatusTragen(e.type as TaskEventType))
    if (!letzter) return false
    const nach = (letzter.payload as { nach?: unknown } | null)?.nach
    const ziel = typeof nach === 'string' ? nach : letzter.type === 'closed' ? 'erledigt' : null
    return ziel === 'erledigt'
  })()

  /**
   * **War es überhaupt die Quelle?** (24.08.2026,
   * [E-215](../../../../../docs/ENTSCHEIDUNGEN.md))
   *
   * Nils an `GitLab !880`: *„Ich hatte den doch eigentlich auf erledigt gesetzt?"* Das Band
   * darüber sagte „die Quelle wurde ohne Vermerk wieder aufgemacht" – und das war falsch.
   * **Work** hatte sie wieder aufgemacht, beim Rückschreiben zwei Tage nach dem Abschluss,
   * und der Vermerk stand drei Zeilen tiefer im selben Verlauf.
   *
   * Der Grund für den Irrtum: die Prüfung oben sieht nur Ereignisse, die einen Zielstatus
   * tragen dürfen. Der Rückschreib-Vermerk ist `updated`, das Wiederöffnen selbst ist als
   * eigenes Echo vorbelegt – beide fallen heraus, und übrig bleibt der Abschluss vom 17.08.
   *
   * Hier wird deshalb dieselbe Lücke von der anderen Seite geschlossen: liegt **nach** dem
   * letzten Zustandsereignis ein Rückschreiben, das geöffnet hat, dann ist die Erklärung
   * bekannt und hat einen Namen.
   */
  const wiederAufgemachtVonWork = (() => {
    if (!verlaufWiderspricht) return null
    const letzterZustand = verlauf.find((e) => darfZielstatusTragen(e.type as TaskEventType))
    const oeffnung = verlauf.find((e) => {
      if (e.type !== 'updated' || !e.payload || typeof e.payload !== 'object') return false
      const p = e.payload as { rueckgeschrieben?: unknown; wiederGeoeffnet?: unknown }
      return typeof p.rueckgeschrieben === 'string' && p.wiederGeoeffnet === true
    })
    if (!oeffnung || !letzterZustand) return null
    return new Date(oeffnung.occurredAt) > new Date(letzterZustand.occurredAt)
      ? new Date(oeffnung.occurredAt)
      : null
  })()

  const queryClient = useQueryClient()

  /**
   * **Wer im Kommentar mit `@` angesprochen werden kann** (Nils, 18.08.2026).
   *
   * Nur, wer sich anmelden kann. In der Personenliste stehen auch die, die aus GitLab und
   * NetCon entstanden sind – darunter Melder aus Kundentickets. Sie anzubieten hieße,
   * jemanden anzusprechen, der die Antwort nie sieht. Der Server prüft dasselbe noch
   * einmal.
   */
  /**
   * **Nur wer in dieser Arbeitswelt ist** (Z-07, 26.08.2026). Wer nicht Mitglied ist, sähe
   * die Aufgabe nie – ihn anzubieten hieße, eine Zuweisung ins Leere zu erlauben.
   */
  /**
   * **Die Welt der Aufgabe, nicht die aktive** (28.08.2026). Aus „Mein Tag" heraus ist das
   * fast nie dieselbe – und nach einem Umhängen wechselt sie sogar, während man hinsieht.
   */
  const { personen: allePersonen } = usePersonenDieserWelt(aufgabe?.workspaceId)
  /**
   * **Erwähnbar ist, wer erreichbar ist** (20.08.2026, nach Nils' Probelauf: *„da steht nur
   * Gerd und Nils, keine anderen Personen, Gäste usw. Wollte Davin fragen."*) – **in jeder
   * Welt** (16.09.2026, E-355: *„Martina finde ich nicht als @-Person"*). Die Liste kam bis
   * dahin aus `allePersonen` oben, also nur aus den Mitgliedern dieser Welt – und Gäste sind
   * nirgends Mitglied. Warum das für die Erwähnung die falsche Grenze ist, steht am Hook.
   */
  const erwaehnbar = useErwaehnbarePersonen()

  const [neueUnteraufgabe, setNeueUnteraufgabe] = useState('')
  /** Erledigte Unteraufgaben zugeklappt (E-341). */
  const [erledigteOffen, setErledigteOffen] = useState(false)
  const [neuerKommentar, setNeuerKommentar] = useState('')
  const [fehler, setFehler] = useState<string | null>(null)

  /**
   * **Nachgeladen wird diese eine Aufgabe – nicht der Bestand** (21.08.2026, nach Nils'
   * Messung: *„Ich finde das Aufgabenpopup sehr langsam, wenn man da was klickt … 2–3
   * Sekunden Verzögerung."*).
   *
   * Hier stand `['tasks']`, `['pools']`, `['analytics']`. `['tasks']` trifft `tasks.all`,
   * den **Gesamtbestand**: 994 ms, 3 MB, 3.177 Aufgaben ([`AppShell.tsx`](./AppShell.tsx)).
   * Dazu kam, dass die WebSocket-Nachricht `aufgabe.geaendert` dieselben Schlüssel ein
   * zweites Mal für ungültig erklärt – der Bestand wurde je Klick **zweimal** geholt, und
   * weil `invalidateQueries` laufende Abfragen abbricht (`cancelRefetch`), fing die zweite
   * Runde die erste ab und begann von vorn.
   *
   * Genau davor warnt [`mutationen.ts`](../lib/mutationen.ts) seit Phase 4 („Beides
   * zusammen holt den kompletten Aufgabenbestand zweimal"). Für die drei optimistischen
   * Aktionen wurde die Regel befolgt, für die Felder hier nicht.
   *
   * Also nur noch, was **dieses Fenster** zeigt und der Socket nicht mitbringt. Bestand,
   * Pools und Auswertungen kommen über `aufgabe.geaendert`; ist der Socket getrennt, lädt
   * die Shell beim Wiederverbinden alles nach – dieselbe Zusage wie bei den optimistischen
   * Aktionen, und dieselbe Lücke, falls jemand sie entfernt.
   */
  const neuLaden = () => {
    void queryClient.invalidateQueries({ queryKey: aufgabeDetailQuery(aufgabeId).queryKey })
    void queryClient.invalidateQueries({ queryKey: aufgabeVerlaufQuery(aufgabeId).queryKey })
  }
  const beiFehler = (e: Error) => setFehler(e.message)

  /**
   * **Liegt die Aufgabe in einem Regal?** (20.08.2026)
   *
   * Gefragt wird nicht nach der Kennung `parkplatz`, sondern nach `ziehbar` – ein zweites
   * Regal wäre sonst ein zweiter Sonderfall im Code, und genau das vermeidet
   * [E-191](../../../../docs/ENTSCHEIDUNGEN.md) an jeder anderen Stelle auch.
   */
  const geparkt = allePools.find((p) => !p.ziehbar && aufgabe?.poolIds.includes(p.id))

  /**
   * Der Statuswechsel ist optimistisch (AP 4.7) und lädt deshalb **nicht** nach:
   * die Marke springt sofort, der WebSocket bestätigt. Bei den Unteraufgaben und
   * Kommentaren darunter bleibt es beim Nachladen – ihre Kennungen erzeugt der
   * Server, und ein vorgetäuschter Kommentar ohne Kennung ließe sich nicht
   * löschen.
   */
  const status = useStatusSetzen()
  /**
   * **Welche Frage nach dem Statuswechsel aufgeht** – `null`, solange keine ansteht.
   * Der Klick wirkt zuerst, das Feld ist das Angebot danach (siehe `StatusNotiz`).
   */
  const [notizOffen, setNotizOffen] = useState<{ frage: string } | null>(null)

  const unterAnlegen = useMutation({
    mutationFn: (title: string) =>
      trpc.tasks.unteraufgabeAnlegen.mutate({ taskId: aufgabeId, title }),
    onSuccess: () => {
      setNeueUnteraufgabe('')
      setFehler(null)
      neuLaden()
    },
    onError: beiFehler,
  })
  /** Aus einem Kommentar (E-339): der Kommentar wird Beschreibung, der Titel kommt sofort und die KI bessert nach. */
  const unterAusKommentar = useMutation({
    mutationFn: (kommentarId: string) =>
      trpc.tasks.unteraufgabeAusKommentar.mutate({ taskId: aufgabeId, kommentarId }),
    onSuccess: () => {
      setFehler(null)
      neuLaden()
    },
    onError: beiFehler,
  })
  const unterUmschalten = useMutation({
    mutationFn: (v: { id: string; isDone: boolean }) => trpc.tasks.unteraufgabeUmschalten.mutate(v),
    onSuccess: neuLaden,
    onError: beiFehler,
  })
  const unterLoeschen = useMutation({
    mutationFn: (id: string) => trpc.tasks.unteraufgabeLoeschen.mutate({ id }),
    onSuccess: neuLaden,
    onError: beiFehler,
  })
  /**
   * **Auf welchen Kommentar geantwortet wird** ([E-322](../../../../docs/ENTSCHEIDUNGEN.md#e-322),
   * 04.09.2026).
   *
   * Nils: *„Hier würde ich gerne getrennt auf Simon und Davin antworten, dass es dann unter den
   * Kommentaren bei denen steht."*
   *
   * **Ein Formular, kein zweites unter jeder Zeile.** Ein Eingabefeld je Kommentar wäre bei
   * zwanzig Kommentaren zwanzig Felder – und bei jedem die Frage, welches gerade gemeint ist.
   * Stattdessen zeigt das eine Feld unten an, an wen die Antwort geht, und man kann es
   * abwählen.
   */
  const [antwortAuf, setAntwortAuf] = useState<{ id: string; an: string } | null>(null)

  const kommentieren = useMutation({
    mutationFn: (body: string) =>
      trpc.tasks.kommentarSchreiben.mutate({
        taskId: aufgabeId,
        body,
        ...(antwortAuf ? { antwortAuf: antwortAuf.id } : {}),
      }),
    onSuccess: () => {
      setNeuerKommentar('')
      setAntwortAuf(null)
      setFehler(null)
      neuLaden()
    },
    onError: beiFehler,
  })

  const [ueberZone, setUeberZone] = useState(false)
  const [laedtHoch, setLaedtHoch] = useState(false)
  /**
   * **Der Knopf ist ein Etikett vor einem versteckten `<input type="file">`** (24.08.2026).
   * Ein Dateiauswahl-Dialog lässt sich nur aus einer echten Anwenderaktion heraus öffnen;
   * `ref.click()` aus dem `onClick` eines Knopfes ist der übliche Weg, weil sich das
   * `<input>` selbst nicht so gestalten lässt, dass es zu den übrigen Knöpfen passt.
   */
  const dateiFeld = useRef<HTMLInputElement>(null)
  const fotoFeld = useRef<HTMLInputElement>(null)

  /**
   * **Der Anhang wird hochgeladen und sein Verweis in den Entwurf geschrieben** – nicht
   * sofort als Kommentar abgeschickt.
   *
   * Wer ein Bild einfügt, will meistens noch etwas dazuschreiben („so sieht es aus, wenn
   * …"). Ein Kommentar, der beim Loslassen der Maustaste entsteht, nimmt ihm die
   * Gelegenheit – und ein Bild ohne Satz beantwortet selten eine Frage.
   *
   * Ob daraus `![](anhang:…)` wird oder `[Angebot.pdf](anhang:…)`, entscheidet
   * `anhangVerweis` am Medientyp, **den der Server zurückgibt** – dort wurde er am Inhalt
   * bestimmt, hier stünde nur die Endung.
   */
  async function hochladen(dateien: File[]): Promise<void> {
    setLaedtHoch(true)
    try {
      for (const datei of dateien) {
        const anhang = await anhangHochladen({ aufgabeId }, datei)
        setNeuerKommentar((bisher) => `${bisher}${bisher ? '\n\n' : ''}${anhangVerweis(anhang)}`)
      }
      setFehler(null)
    } catch (fehler) {
      setFehler(fehler instanceof Error ? fehler.message : 'Hochladen fehlgeschlagen')
    } finally {
      setLaedtHoch(false)
    }
  }

  const { data: sitzung } = useQuery(sitzungQuery)
  const rolle = sitzung?.role ?? 'entwickler'
  const fachbereich = istMitOwner(rolle)
  /**
   * **Ein Gast liest und kommentiert – sonst nichts** (18.08.2026,
   * [E-130](../../../../docs/ENTSCHEIDUNGEN.md)). Er sieht diese Ansicht nur für Aufgaben,
   * an denen er beteiligt ist; verboten wird es serverseitig (`mitarbeitProcedure`,
   * `teamProcedure`). Hier verschwinden die Bedienelemente, damit er nicht auf Knöpfe
   * drückt, die ihm eine Fehlermeldung zurückgeben.
   */
  const gast = istGast(rolle)
  /** Alles Ändernde an einem Ort: Planung, Beschreibung, Bearbeiter, Unteraufgaben. */
  const darfAendern = !fachbereich && !gast

  /**
   * Was an Statuswechseln überhaupt angeboten wird. Für das Team der ganze Fluss, für den
   * Fachbereich die Abnahme – für den Gast nie etwas.
   */
  /**
   * **Wer abnimmt – zusammengefallen aus Aufgabe und Vorhaben** (18.08.2026). Die Angabe
   * kommt fertig vom Server, damit Oberfläche und Serverprüfung nicht zwei Regeln haben.
   */
  const abnahmeLage = aufgabe
    ? {
        abnahmeDurchId: aufgabe.abnahmeDurchId,
        assigneeId: aufgabe.assigneeId,
        status: aufgabe.status,
      }
    : null
  const abnahme = abnahmeLage ? brauchtAbnahme(abnahmeLage, sitzung?.id) : false

  const statusAuswahl: readonly TaskStatus[] = gast
    ? []
    : !fachbereich
      ? /**
         * **„Erledigt" steht nur da, wenn es auch geht** (18.08.2026, Nils: *„die die
         * Abnahme erfordern, auf Review/Abnahme nur setzen kann"*).
         *
         * Bis hierher zeigte die Leiste dem Bearbeiter den ganzen Fluss und ließ den Server
         * den Klick abweisen – dieselbe Falle, die im Absatz über der Leiste für den
         * Fachbereich ausdrücklich vermieden wird. `darfErledigen` ist wortgleich zur
         * Prüfung in `tasks.statusSetzen`.
         */
        TASK_STATUS_FLOW.filter(
          (s) => s !== 'erledigt' || !abnahmeLage || darfErledigen(abnahmeLage, sitzung?.id),
        )
      : aufgabe?.status === 'review'
        ? (['erledigt', 'in_arbeit'] as const)
        : []

  /**
   * **Das Kommentarformular – eines, aber nicht immer an derselben Stelle**
   * ([E-329](../../../../docs/ENTSCHEIDUNGEN.md#e-329), 04.09.2026).
   *
   * Nils: *„Das Antworten in den Kommentaren ist unübersichtlich, das sollte direkt unter dem
   * Kommentar schön dargestellt passieren. Bei vielen Kommentaren muss man nach unten scrollen
   * usw. Da sieht man auch nicht wirklich gut, dass man auf den Kommentar antwortet."*
   *
   * ## Warum es trotzdem nur eines ist
   *
   * [E-322](../../../../docs/ENTSCHEIDUNGEN.md#e-322) hatte gegen ein Feld je Kommentar
   * entschieden – *„bei zwanzig Kommentaren zwanzig Felder, und bei jedem die Frage, welches
   * gerade gemeint ist"*. Das Argument steht weiter. Nur war die Folgerung daraus zu grob: aus
   * „nicht zwanzig" wurde „eines ganz unten", und damit lag das Feld bei einem langen Verlauf
   * außerhalb des Bildes, während die Zeile, auf die es sich bezog, oben stand. Ein Hinweis
   * *„Antwort an Davin"*, den man erst nach dem Scrollen sieht, beantwortet die Frage
   * *„antworte ich hier gerade jemandem?"* zu spät.
   *
   * **Ein Formular, zwei mögliche Plätze.** Unten – außer beim Antworten, dann unmittelbar
   * unter dem Kommentar, dem es gilt. Zwanzig Felder gibt es nie, und welches gemeint ist,
   * fragt sich auch nicht: es ist immer nur eines da.
   *
   * Der Entwurf überlebt den Umzug, weil er im Zustand dieser Ansicht liegt (`neuerKommentar`)
   * und nicht im Formular selbst. Verloren geht nur der Tastaturfokus – den holt sich das
   * Textfeld beim Antworten zurück.
   */
  /**
   * Kommentar (falls geschrieben) abschicken und die Aufgabe bis `tag` warten lassen (E-357).
   * Der Status zuerst: er ist optimistisch, der Kommentar nicht – so springt die Leiste sofort.
   */
  function wiederVorlegen(tag: string) {
    setFehler(null)
    status.mutate({ id: aufgabeId, status: 'warten', wartenBis: tag }, { onError: beiFehler })
    if (neuerKommentar.trim()) kommentieren.mutate(neuerKommentar.trim())
  }

  /**
   * **Das Formular weglegen** (01.10.2026, Christian Hunhoff: *„das schließen von Kommentaren,
   * wenn man eigentlich nichts ändern will oder hinzufügen will"*).
   *
   * Bis hierher gab es keinen Rückweg: Ein Klick auf „antworten" ließ das Formular unter den
   * Kommentar springen ([E-329](../../../../docs/ENTSCHEIDUNGEN.md#e-329)), und die einzige
   * Gegenhandlung hieß *„als eigenen Kommentar schreiben"* – die schiebt es nach unten, statt
   * es zuzumachen. Der Bearbeiten-Modus eines Kommentars hatte sein „Abbrechen" seit jeher;
   * das Antwort-Formular nicht.
   *
   * **Beides zusammen, nicht nur das Ziel:** Ein Formular, das offen bleibt und nur sein „an
   * wen" verliert, hat die Frage „will ich hier noch etwas schreiben" nicht beantwortet.
   */
  function formularWeglegen() {
    setAntwortAuf(null)
    setNeuerKommentar('')
  }

  function kommentarFormular() {
    return (
      <form
        onSubmit={(e) => {
          e.preventDefault()
          if (neuerKommentar.trim()) kommentieren.mutate(neuerKommentar.trim())
        }}
        className="mt-2"
        /**
         * **Ziehen und Einfügen fangen am Formular ab, nicht am Textfeld.**
         *
         * Wer ein Bild auf einen Kommentarbereich zieht, zielt selten genau auf das
         * Eingabefeld – er zielt auf „hier". Ein Ablagebereich, den man treffen
         * muss, ist einer, den man verfehlt.
         */
        onDragOver={(e) => {
          /**
           * **Gefragt wird nach `types`, nicht nach dem Inhalt** – der ist in dieser
           * Phase noch gesperrt. Ohne `preventDefault()` ist das Formular gar kein
           * gültiges Ziel und `onDrop` unten liefe nie (siehe `ziehtDateien`).
           */
          if (ziehtDateien(e.dataTransfer)) {
            e.preventDefault()
            setUeberZone(true)
          }
        }}
        onDragLeave={() => setUeberZone(false)}
        onDrop={(e) => {
          const dateien = dateienAus(e.dataTransfer)
          if (dateien.length === 0) return
          e.preventDefault()
          setUeberZone(false)
          hochladen(dateien)
        }}
        onPaste={(e) => {
          /**
           * **Der wichtigere Weg.** Ein Screenshot entsteht mit ⌘⇧4 und liegt in der
           * Zwischenablage – nicht als Datei auf dem Schreibtisch.
           */
          const dateien = dateienAus(e.clipboardData)
          if (dateien.length === 0) return
          e.preventDefault()
          hochladen(dateien)
        }}
      >
        {/**
         * **Wohin die Antwort geht, steht über dem Feld** (E-322).
         *
         * Am Formular und nicht als zwanzig Eingabefelder in der Liste: eines je
         * Kommentar wäre bei jedem die Frage, welches gerade gemeint ist. Und
         * abwählbar, denn ein Antwortziel, das man nicht mehr loswird, ist eine Falle.
         */}
        {antwortAuf && (
          <div className="mb-1.5 flex items-center gap-2 rounded-lg bg-accent-soft px-2.5 py-1 text-[11.5px] text-accent-deep">
            <span>
              Antwort an <strong>{antwortAuf.an}</strong>
            </span>
            <button
              type="button"
              onClick={() => setAntwortAuf(null)}
              className="ml-auto cursor-pointer hover:underline"
            >
              als eigenen Kommentar schreiben
            </button>
          </div>
        )}

        <div
          className={ueberZone ? 'rounded-lg outline-2 outline-offset-2 outline-accent' : undefined}
        >
          <MarkdownFeld
            wert={neuerKommentar}
            setzen={setNeuerKommentar}
            zeilen={2}
            erwaehnbar={erwaehnbar}
            /** Springt das Formular nach oben unter einen Kommentar, muss der Zeiger mit (E-329). */
            fokusBeimEinhaengen={antwortAuf !== null}
            platzhalter={
              antwortAuf
                ? `Antwort an ${antwortAuf.an} – @ erwähnt jemanden, Datei hierher ziehen …`
                : 'Kommentar schreiben – @ erwähnt jemanden, Datei hierher ziehen oder einfügen …'
            }
            beiAbsenden={() => {
              if (neuerKommentar.trim()) kommentieren.mutate(neuerKommentar.trim())
            }}
            /**
             * **Der Platzhalter versprach es, das Feld konnte es nicht** (23.09.2026, von Davin
             * gemeldet: *„Inline-Bild kann ich hier nicht einfügen.."*).
             *
             * `hochladen` steht seit jeher eine Ebene höher und schreibt den Verweis genau in
             * dieses Feld – nur die beiden Props fehlten. Ein Platzhalter, der zum Ziehen
             * auffordert, und ein Feld, das eine fallengelassene Datei verschluckt, sind
             * schlimmer als gar kein Angebot: Man probiert es dreimal und hält sich selbst für
             * den Fehler.
             */
            beiDateien={(dateien) => void hochladen(dateien)}
            laedtHoch={laedtHoch}
            beiAbbrechen={formularWeglegen}
          />
        </div>
        <div className="mt-1.5 flex flex-wrap items-center gap-2">
          <Button type="submit" variant="primary" size="sm" disabled={kommentieren.isPending}>
            Kommentieren
          </Button>
          {/**
           * **„Abbrechen" erscheint erst, wenn es etwas abzubrechen gibt** (01.10.2026).
           *
           * Steht nichts im Feld und gilt die Antwort niemandem, ist das Formular der
           * Normalzustand am Ende der Unterhaltung – ein Knopf, der dann „nichts" wegräumt,
           * wäre ein Knopf ohne Wirkung und die zweite Frage in einer Leiste, die schon
           * „Kommentieren", „Datei" und „Foto" trägt.
           *
           * Für den leeren Antwort-Fall tut Esc dasselbe (siehe `MarkdownFeld`); der Knopf ist
           * der Weg für den, der schon geschrieben hat – dort verwirft Esc bewusst nichts.
           */}
          {(neuerKommentar.trim() !== '' || antwortAuf) && (
            <Button type="button" variant="ghost" size="sm" onClick={formularWeglegen}>
              Abbrechen
            </Button>
          )}
          {/**
           * **Der Knopf, der bis zum 24.08.2026 fehlte**
           * ([E-217](../../../../docs/ENTSCHEIDUNGEN.md#e-217)). Ziehen und Einfügen
           * waren die einzigen Wege – beide unsichtbar. Nils: *„Mir fällt gerade
           * auf, dass ich zu Vorhaben oder Aufgaben keine Dateien hochladen kann."*
           * Ein Weg, den man kennen muss, ist für den, der ihn nicht kennt, keiner.
           *
           * `type="button"`: in einem `<form>` ist `submit` die Voreinstellung, und
           * ein Klick auf „Datei" schickte sonst den Kommentar ab.
           */}
          <Button
            type="button"
            variant="ghost"
            size="sm"
            className="inline-flex items-center gap-1"
            disabled={laedtHoch}
            onClick={() => dateiFeld.current?.click()}
          >
            <Paperclip className="size-3.5" aria-hidden /> Datei anhängen
          </Button>
          {/**
           * **Foto aufnehmen – nur auf dem Telefon** (Z-16). `capture="environment"` öffnet
           * die Rückkamera statt der Dateiauswahl: das Whiteboard, der Bildschirm beim Kunden,
           * direkt in den Kommentar. Am Rechner gibt es dafür nichts, deshalb `sm:hidden`.
           */}
          <Button
            type="button"
            variant="ghost"
            size="sm"
            className="inline-flex items-center gap-1 sm:hidden"
            disabled={laedtHoch}
            onClick={() => fotoFeld.current?.click()}
          >
            <Camera className="size-3.5" aria-hidden /> Foto
          </Button>
          <input
            ref={fotoFeld}
            type="file"
            accept="image/*"
            capture="environment"
            className="hidden"
            onChange={(e) => {
              const dateien = [...(e.target.files ?? [])]
              e.target.value = ''
              if (dateien.length > 0) hochladen(dateien)
            }}
          />
          <input
            ref={dateiFeld}
            type="file"
            multiple
            className="hidden"
            onChange={(e) => {
              const dateien = [...(e.target.files ?? [])]
              /**
               * **Das Feld wird zurückgesetzt.** Sonst meldet der Browser beim
               * zweiten Anhängen **derselben** Datei kein `change` – der Wert hat
               * sich ja nicht geändert –, und der Klick bliebe folgenlos.
               */
              e.target.value = ''
              if (dateien.length > 0) hochladen(dateien)
            }}
          />
          <span className="text-[11px] text-muted">
            {laedtHoch
              ? 'Wird hochgeladen …'
              : 'Markdown · @ erwähnt · ⌘↵ sendet · Datei ziehen oder einfügen'}
          </span>
        </div>
        {/**
         * **Wieder vorlegen – direkt dort, wo man den Zwischenstand aufschreibt**
         * ([E-357](../../../../docs/ENTSCHEIDUNGEN.md#e-357), 16.09.2026).
         *
         * Nils: *„Ich habe häufig Aufgaben, wo ich nach Zwischenschritten «erneut erinnern»
         * möchte. Erinnere mich in 1, 2, 3, 4, 5 Tagen, dass ich die Aufgabe wieder im Vorlauf
         * habe."*
         *
         * Vorher waren das drei Handgriffe an drei Stellen: kommentieren, „Wartet auf Antwort"
         * klicken, das Datum wählen. Jetzt einer: der Kommentar (falls geschrieben) geht ab, die
         * Aufgabe wartet bis zum Tag und steht dann ganz vorn im Vorlauf. Kein neuer
         * Mechanismus – es ist genau die Wiedervorlage aus E-315, nur ohne Umweg.
         */}
        {aufgabe && darfAendern && aufgabe.status !== 'erledigt' && (
          <div className="mt-1.5 flex flex-wrap items-center gap-1.5 text-[11.5px]">
            <span
              className="text-muted"
              title="Setzt „Wartet auf Antwort“ – am Tag steht die Aufgabe ganz vorn im Vorlauf."
            >
              ↻ Wieder vorlegen in
            </span>
            {WIEDERVORLAGE_TAGE.map((t) => (
              <button
                key={t}
                type="button"
                onClick={() => wiederVorlegen(vorgabeZuTag(t, new Date()))}
                aria-label={`In ${t} ${t === 1 ? 'Tag' : 'Tagen'} wieder vorlegen`}
                className="tap cursor-pointer rounded-md border border-line2 px-2 py-[2px] text-ink2 hover:bg-[#f3f2ef]"
              >
                {t}
              </button>
            ))}
            <span className="text-muted">Tagen</span>
            <input
              type="date"
              aria-label="Wieder vorlegen am"
              min={vorgabeZuTag(1, new Date())}
              value=""
              onChange={(e) => {
                if (e.target.value) wiederVorlegen(e.target.value)
              }}
              className="rounded-md border border-line2 bg-surface px-1.5 py-0.5 text-[11.5px]"
            />
            {aufgabe.status === 'warten' && aufgabe.wartenBis && (
              <span className="text-muted">
                · liegt wieder vor am{' '}
                {new Date(`${aufgabe.wartenBis}T12:00:00`).toLocaleDateString('de-DE')}
              </span>
            )}
          </div>
        )}
      </form>
    )
  }

  return (
    <div
      /**
       * **Auf dem Telefon ein Vollbild** (Z-16): kein Rand, keine Rundung, die ganze Höhe.
       * Die 3 px Rand und 4 vh oben waren Platz für nichts – dahinter stand die Verdunklung.
       */
      className="fixed inset-x-0 bottom-0 unter-leiste z-50 flex items-start justify-center bg-[rgb(23_22_20/0.38)] max-sm:p-0 sm:p-6 sm:pt-[6vh]"
      {...hintergrundSchliesst(schliessen)}
      role="dialog"
      aria-modal="true"
      aria-label="Aufgabendetail"
    >
      <div className="flex w-full min-w-0 max-w-[820px] flex-col rounded-card border border-line bg-surface shadow-card max-sm:h-full max-sm:max-h-none max-sm:rounded-none max-sm:border-0 sm:max-h-[min(88vh,100%)]">
        {/**
         * **Auf dem Telefon die Knöpfe über den Titel** (Nils, 16.10.2026: *„Der Text Titel ist
         * untereinander"*).
         *
         * Link, neuer Tab, Sonne, Nadel und ✕ brauchen zusammen gut 230 px. Neben ihnen blieben
         * dem Titel auf einem 390 px breiten Telefon rund 110 px – ein Wort pro Zeile. Unter
         * `sm` stehen die Knöpfe deshalb als eigene Leiste oben, der Titel bekommt die ganze
         * Breite darunter.
         */}
        <div className="flex items-start gap-2 border-b border-line px-[18px] py-3.5 max-sm:flex-col-reverse max-sm:items-stretch max-sm:gap-1 max-sm:px-4 max-sm:pt-2 max-sm:pb-3">
          <div className="min-w-0 flex-1">
            {aufgabe && darfAendern && aufgabe.quellfelderAenderbar ? (
              <TitelZeile
                taskId={aufgabe.id}
                titel={aufgabe.title}
                neuLaden={neuLaden}
                beiFehler={beiFehler}
              />
            ) : (
              <h3 className="text-sm font-bold break-words max-sm:text-[17px] max-sm:leading-snug">
                {isLoading
                  ? 'Lädt …'
                  : (aufgabe?.title ??
                    (ladeFehler && trpcFehlerCode(ladeFehler) === undefined
                      ? 'Server gerade nicht erreichbar'
                      : 'Aufgabe nicht gefunden'))}
              </h3>
            )}
            {!aufgabe && !isLoading && ladeFehler && trpcFehlerCode(ladeFehler) === undefined && (
              <button
                type="button"
                onClick={() => void nochmalLaden()}
                className="mt-1 cursor-pointer text-[11px] text-accent-deep hover:underline"
              >
                Erneut versuchen
              </button>
            )}
            {aufgabe && (
              <div className="mt-1.5 flex flex-wrap items-center gap-1.5">
                <span className="rounded-md bg-[#f3f2ef] px-2 py-px text-[10.5px] font-semibold text-ink2">
                  {TASK_STATUS_LABELS[aufgabe.status] ?? aufgabe.status}
                </span>
                {/**
                 * **„Wunsch" steht oben, nicht in der Meta-Spalte** (Nils, 03.08.2026:
                 * *„die sollten IM Popup auch sichtbar sein"*).
                 *
                 * Es ändert, wie man alles darunter liest: ein Wunsch hat keinen Melder,
                 * der auf Antwort wartet, und keine Dringlichkeit aus einem Ausfall. Wer
                 * das erst nach der Beschreibung erfährt, hat sie schon falsch gelesen.
                 */}
                {aufgabe.istWunsch && (
                  <span
                    className="rounded-md bg-accent-soft px-2 py-px text-[10.5px] font-semibold text-accent-deep"
                    title="Aus der Warteschlange Entwicklung-Wünsche-Ideen – kein Ticket"
                  >
                    Wunsch
                  </span>
                )}
                {aufgabe.priority && <PrioBadge priority={aufgabe.priority} />}
                {refs.map((r) => (
                  <a
                    key={r.id}
                    href={r.externalUrl ?? undefined}
                    target="_blank"
                    rel="noreferrer"
                    className="hover:underline"
                  >
                    {/**
                     * **Der Status steht an der Marke, wenn er abweicht** (03.08.2026).
                     *
                     * Nils: *„Wo sehe ich denn, wo die hier dann noch offen ist?"* Zuerst
                     * stand es als eigene Zeile darunter – und die ist genau da, wo niemand
                     * sucht. Die Frage lautet „was sagt **diese** Quelle", also gehört die
                     * Antwort an **diese** Marke.
                     *
                     * Nur bei Abweichung vom Gesamtstatus: `NetCon #93551 · offen` an einer
                     * erledigten Aufgabe ist eine Auskunft, `GitLab !809 · erledigt` an
                     * derselben wäre Wiederholung.
                     */}
                    <SourceBadge
                      source={r.sourceId}
                      label={
                        r.sourceStatus && aufgabe && r.sourceStatus !== aufgabe.status
                          ? `${r.externalKey} · ${TASK_STATUS_LABELS[r.sourceStatus as TaskStatus] ?? r.sourceStatus}`
                          : r.externalKey
                      }
                    />
                  </a>
                ))}
              </div>
            )}

            {/**
             * **Die Erklärung, nicht der Fundort.** Wo es steht, sagt jetzt die Marke; diese
             * Zeile sagt, was zu tun ist – und dass Work es nicht selbst tut.
             */}
            {uneinig && (
              <p className="mt-1.5 text-[11.5px] text-pool-sofort">
                Die Quellen sind sich uneinig – Work ändert daran nichts. Das Ticket schließt ein
                Mensch.
              </p>
            )}

            {/**
             * **Genau hier ist Nils gestolpert** (18.08.2026): *„Hier ist der Status irgendwie
             * zweideutig. Einmal steht da geschlossen, aber oben ‚bereit'."*
             *
             * Beides stimmt – nur steht der Grund nirgends. Die Quelle wurde nach dem
             * Abschluss wieder aufgemacht, **ohne** das zu melden, und Work schreibt nur
             * Ereignisse, die die Quelle meldet. Der Verlauf endet deshalb auf „Geschlossen".
             *
             * Diese Zeile ist der Ersatz für das fehlende Ereignis: sie sagt nicht, wann es
             * passierte – das weiß niemand –, sondern **dass** der Verlauf hier nicht
             * weitererzählt. Ohne sie sucht man den Fehler in der falschen Richtung.
             */}
            {verlaufWiderspricht &&
              (wiederAufgemachtVonWork ? (
                /**
                 * **Der Fall hat einen Urheber, und es sind wir** (E-215). Das ist keine
                 * Nebenbemerkung: Wer „die Quelle wurde wieder aufgemacht" liest, sucht in
                 * GitLab nach jemandem, der es getan hat – und findet niemanden.
                 */
                <p className="mt-1.5 text-[11.5px] text-pool-sofort">
                  Der Verlauf endet auf „erledigt", die Aufgabe steht trotzdem offen –{' '}
                  <strong>Work</strong> hat die Quelle beim Rückschreiben am{' '}
                  {wiederAufgemachtVonWork.toLocaleDateString('de-DE')} wieder aufgemacht. Das tut
                  Work seit dem 24.08.2026 nicht mehr.
                </p>
              ) : (
                <p className="mt-1.5 text-[11.5px] text-pool-sofort">
                  Der Verlauf endet auf „erledigt", die Aufgabe steht trotzdem offen – die Quelle
                  wurde ohne Vermerk wieder aufgemacht.
                </p>
              ))}
          </div>
          <div className="ml-auto flex shrink-0 items-center gap-1 max-sm:-mr-2 max-sm:justify-end">
            <TeilenKnopf taskId={aufgabeId} />
            {aufgabe && aufgabe.status !== 'erledigt' && <SonnenMarke taskId={aufgabe.id} />}
            {aufgabe && <PinKnopf taskId={aufgabe.id} />}
            <button
              type="button"
              onClick={schliessen}
              className="tap cursor-pointer rounded-lg px-2 py-1 text-ink2 hover:bg-[#f3f2ef] max-sm:px-3"
              aria-label="Schließen"
            >
              ✕
            </button>
          </div>
        </div>

        {/*
          Ein Feld für beide Quellen: die Mutationen mit Nachladen halten ihre
          Meldung in `fehler`, der optimistische Statuswechsel trägt sie selbst –
          nach einem Rücksetzer muss dastehen, warum die Marke zurückgesprungen ist.
        */}
        {(fehler ?? status.error) && (
          <p
            role="alert"
            className="mx-[18px] mt-3 rounded-lg bg-[#fdeaea] px-2.5 py-2 text-[12.5px] text-[#b02a2a]"
          >
            {fehler ?? status.error?.message}
          </p>
        )}

        {/**
         * **Zwei Spalten erst ab dem großen Umbruch** (Nils, 26.08.2026, mit einem
         * Screenshot vom Telefon: *„bei Aufgabendetails noch komisch"*).
         *
         * Das Raster stand fest auf `1.6fr 1fr`. Auf einem Telefon bleiben für die rechte
         * Spalte keine 100 px – „Christian Hunhoff" stand dort **senkrecht, ein Buchstabe
         * pro Zeile**. Unter 1024 px liegen die beiden Teile jetzt untereinander; das ist
         * derselbe Umbruchpunkt wie bei der Seitenleiste
         * ([Z-10](../../../../docs/phasen/zusatz-10-mobil.md)).
         */}
        {aufgabe && (
          <div className="grid min-h-0 flex-1 grid-cols-1 gap-[18px] overflow-y-auto px-[18px] py-3.5 max-sm:px-4 lg:grid-cols-[1.6fr_1fr]">
            {/**
             * `min-w-0` ist hier kein Feinschliff, sondern die Bedingung dafür, dass
             * Umbrechen überhaupt wirkt: eine Grid-Spalte ist standardmäßig mindestens so
             * breit wie ihr breitester Inhalt, und ein unteilbares Wort macht daraus eine
             * Spalte von 5.277 Zeichen Breite. Erst mit `min-w-0` darf sie schmaler werden
             * als das, was in ihr steht – und dann greift `break-words`.
             */}
            {/**
             * **Unter `lg` die Fakten nach oben** (Nils, 16.10.2026, vom Telefon: *„sieht sehr
             * Desktop like aus"*).
             *
             * Untereinander gelegt stand die Meta-Spalte **hinter** Beschreibung und allen
             * Kommentaren – Bearbeiter, Fällig und Priorität fand nur, wer ganz nach unten
             * scrollte. Beide Spalten lösen sich unter `lg` mit `contents` auf, ihre vier Teile
             * werden direkte Rasterzellen und bekommen eine Reihenfolge: Status und Planung,
             * Fakten, Inhalt, Verlauf. Ab `lg` bleibt alles, wie es war.
             */}
            <div className="min-w-0 max-lg:contents">
              <div className="min-w-0">
                {/* ── Status ──────────────────────────────────────────── */}
                {/**
                 * **Für den Fachbereich ist das keine Statusleiste, sondern eine Abnahme.**
                 *
                 * Ein Mit-Owner sieht nur die beiden Wege, die auf ihn warten – und nur, wenn
                 * die Aufgabe wirklich auf Abnahme steht. Die volle Leiste anzuzeigen und die
                 * Klicks vom Server abweisen zu lassen wäre die schlechteste Fassung: sie sieht
                 * aus wie ein Angebot und ist eine Falle.
                 */}
                {/**
                 * **Eine geparkte Aufgabe hat keine Statusleiste** (20.08.2026).
                 *
                 * Erste Fassung zeigte beide zugleich, und das sah falsch aus, weil es falsch
                 * war: `zurueckgestellt` steht in keinem der fünf Knöpfe, also war **keiner**
                 * hervorgehoben – eine Leiste, die behauptet, die Aufgabe sei in keinem
                 * Zustand. Und ein Klick darauf hätte den Status gesetzt, ohne sie aus dem
                 * Regal zu nehmen: geparkt und trotzdem „In Arbeit".
                 *
                 * Deshalb hier eine Weiche und keine Ergänzung. Wer geparkt hat, hat genau eine
                 * sinnvolle nächste Handlung, und die steht da.
                 */}
                {geparkt ? (
                  <div className="rounded-lg border border-line bg-[#faf9f7] px-3 py-2.5">
                    <span className="text-[12.5px] font-bold">
                      {geparkt.icon} Geparkt auf dem {geparkt.label}
                    </span>
                    {/**
                     * **Eine Feststellung, kein Knopf.** Der Rückweg steht unten in der
                     * Pool-Zeile – an derselben Stelle wie der Hinweg, und beide hinter dem
                     * einen Klick, der die Zeile aufklappt. Ein zweiter Weg hier oben wäre
                     * bequemer und würde genau das aufgeben, worum es Nils ging: dass in
                     * Reichweite der Statusleiste nichts liegt, was eine Aufgabe verschwinden
                     * lässt.
                     */}
                    <p className="mt-1.5 text-[11.5px] text-muted">
                      Zurückgestellt – sichtbar, aber nicht ziehbar und in keiner Auswertung. Unter{' '}
                      <strong>Pools</strong> lässt sie sich zurückholen; sie landet dann im Eingang.
                    </p>
                  </div>
                ) : (
                  <div className="flex flex-wrap items-center gap-1.5">
                    {statusAuswahl.map((s) => (
                      <button
                        key={s}
                        type="button"
                        disabled={s === aufgabe.status}
                        onClick={() => {
                          setFehler(null)
                          const frage = notizFrage(
                            s,
                            aufgabe.status,
                            Boolean(abnahmeLage?.abnahmeDurchId),
                          )
                          /**
                           * **Beim Warten kommt eine Wiedervorlage mit**
                           * ([E-315](../../../../docs/ENTSCHEIDUNGEN.md#e-315), 02.09.2026).
                           *
                           * Vorbelegt auf **nächste Woche** und nicht leer: „Wartet auf Antwort"
                           * ohne Wiedervorlage ist ein Parkplatz mit besserem Namen. Wer keine
                           * will, nimmt sie unten weg – aber nicht aus Versehen.
                           */
                          status.mutate({
                            id: aufgabeId,
                            status: s,
                            ...(s === 'warten' ? { wartenBis: vorgabeZuTag(7, new Date()) } : {}),
                          })
                          setNotizOffen(frage ? { frage } : null)
                        }}
                        /* `tap`: 40 px auf Tastgeräten (Z-10.5) – der Status ist die Handlung. */
                        className={`tap cursor-pointer rounded-lg px-[10px] py-[4px] text-[11.5px] font-semibold disabled:cursor-default max-sm:px-3 max-sm:text-[13px] ${
                          s === aufgabe.status
                            ? 'bg-ink text-white'
                            : 'border border-line2 text-ink2 hover:bg-[#f3f2ef]'
                        }`}
                      >
                        {TASK_STATUS_LABELS[s]}
                      </button>
                    ))}

                    {/**
                     * **Bis wann gewartet wird – änderbar an Ort und Stelle**
                     * ([E-315](../../../../docs/ENTSCHEIDUNGEN.md#e-315), 02.09.2026).
                     *
                     * Steht neben der Leiste und nicht bei den Planungsfeldern: es gehört zum
                     * Status, nicht zur Planung, und es gilt nur, solange dieser Status steht.
                     */}
                    {aufgabe.status === 'warten' && (
                      <WartenBis
                        taskId={aufgabeId}
                        wartenBis={aufgabe.wartenBis ?? null}
                        darfAendern={darfAendern}
                        beiFehler={beiFehler}
                      />
                    )}

                    {/**
                     * **Das Feld sitzt unter der Leiste, nicht in einem Fenster.** Es gehört zu
                     * dem Klick, der gerade passiert ist – ein Dialog in der Bildschirmmitte
                     * würde ihn zu einem eigenen Vorgang machen.
                     */}
                    {notizOffen && (
                      <div className="w-full">
                        <StatusNotiz
                          aufgabeId={aufgabeId}
                          frage={notizOffen.frage}
                          schliessen={() => setNotizOffen(null)}
                        />
                      </div>
                    )}

                    {/**
                     * **Hier steht bewusst kein „Parken"** (Nils, 20.08.2026: *„parken darf
                     * nicht zu einfach klickbar sein. Da darf man nicht aus Versehen
                     * draufklicken … besser untergebracht, dass man nicht auf Erledigt drücken
                     * will, aber aus Versehen auf Parken."*).
                     *
                     * Erste Fassung hatte den Knopf genau hier – einen Daumenbreit neben
                     * „Erledigt". Die beiden Handlungen sind aber **nicht gleich teuer**: ein
                     * verrutschter Klick auf Parken lässt die Aufgabe aus Trichter, Leitstand,
                     * Auslastung und KI-Vorschlägen verschwinden, und wer sie sucht, sucht sie
                     * dort, wo sie nicht mehr ist.
                     *
                     * Geparkt wird deshalb über die **Pool-Zeile**, und die klappt erst auf
                     * Klick auf. Zwei bewusste Schritte statt einem beiläufigen – siehe
                     * `PoolsZeile`.
                     */}
                  </div>
                )}

                {/**
                 * **Warum „Erledigt" fehlt** – der Satz gehört zum weggelassenen Knopf.
                 *
                 * Etwas wortlos auszublenden ist die zweitschlechteste Fassung nach der Falle:
                 * wer den Knopf gestern noch hatte, sucht ihn und findet keine Erklärung.
                 * Deshalb steht hier, wer stattdessen dran ist – und im zweiten Fall, warum es
                 * trotz Abnahmestand nicht der Bearbeiter selbst sein kann.
                 */}
                {!fachbereich && !gast && abnahme && aufgabe.status !== 'erledigt' && (
                  <p className="mt-2 text-[11.5px] text-muted">
                    {aufgabe.status === 'review' ? (
                      <>
                        Wartet auf die Abnahme durch{' '}
                        <strong>{aufgabe.abnahmeDurchName ?? 'den Mit-Owner'}</strong> – die eigene
                        Aufgabe nimmt man nicht selbst ab.
                      </>
                    ) : (
                      <>
                        <strong>{aufgabe.abnahmeDurchName ?? 'Der Mit-Owner'}</strong> nimmt diese
                        Aufgabe ab. Der Weg zu „Erledigt" führt über „Review/Abnahme".
                      </>
                    )}
                  </p>
                )}

                {/**
                 * **Der Kasten stellt fest, er begründet nicht** (20.08.2026).
                 *
                 * Bis hierher stand „das ist kein Fehler – Phase 5 schreibt diesen Unterschied
                 * zurück". Das war am 30.07. richtig und ist es seit der fertigen Phase 5 nicht
                 * mehr: nach erfolgreichem Rückschreiben setzt `rueckschreiben.ts`
                 * `status = work_status`, der Unterschied verschwindet also von selbst. **Steht er
                 * noch da, ist er gerade nicht harmlos** – er wartet, er ist gehindert („Projekt
                 * nicht freigegeben", „kein Label für diesen Status"), oder ein Job ist gescheitert.
                 *
                 * Die Bedingung hier ist deckungsgleich mit `offeneRueckschreibungen`
                 * (`work_status` gesetzt und abweichend). Wo dieser Kasten steht, hat die
                 * `SyncZeile` unten deshalb **immer** etwas zu sagen – und sie sagt es gemessen.
                 * Zwei Sätze zum selben Sachverhalt, von denen einer raten muss, ergeben im Fall
                 * „gehindert" einen Widerspruch auf einem Bildschirm.
                 */}
                {/**
                 * **Beim Parken ist der Unterschied keiner** (20.08.2026).
                 *
                 * Der Kasten fragt „warum weichen Work und Quelle voneinander ab" – und beim
                 * Parken ist die Antwort trivial: weil jemand die Aufgabe gerade weggelegt hat.
                 * Das Ticket steht selbstverständlich weiter auf „Bereit"; es soll auch gar
                 * nicht geschlossen werden, und `zurueckgestellt` wird nirgends
                 * zurückgeschrieben.
                 *
                 * Der Satz „unter Quellsystem steht, ob der Unterschied noch zurückgeschrieben
                 * wird" wäre hier also eine Zusage auf etwas, das nie passiert – und der Kasten
                 * stünde an **jeder** geparkten Aufgabe aus einer Quelle, dauerhaft.
                 */}
                {/**
                 * **Und bei einer Aufgabe aus Work gibt es keine zweite Seite**
                 * ([E-307](../../../../docs/ENTSCHEIDUNGEN.md#e-307), 02.09.2026).
                 *
                 * Nils: *„Das ist eine WORK Aufgabe, wie kann die schon unterschiedliche Statis
                 * haben???"* – Gar nicht. `quellStatus` ist bei einer in Work entstandenen
                 * Aufgabe nur der Wert vom Anlegen; niemand pflegt ihn, weil es keine Quelle
                 * gibt, die ihn pflegen könnte. Sobald jemand den Status ändert, laufen die
                 * beiden Felder auseinander – und der Kasten meldete einen Unterschied zwischen
                 * Work und Work, mit `sourceLabel` „Work" mitten im Satz.
                 *
                 * Der Wert wird **nicht** nachgezogen. Er ist nicht falsch, er ist bedeutungslos:
                 * ihn mitzuschreiben hieße, eine Quelle zu behaupten, die es nicht gibt. Was
                 * gilt, sagt ohnehin `effective_status`.
                 */}
                {!istEigeneQuelle(aufgabe.sourceId) &&
                  aufgabe.status !== aufgabe.quellStatus &&
                  !geparkt && (
                    <p className="mt-2 rounded-lg bg-accent-soft px-2.5 py-1.5 text-[11.5px] text-accent-deep">
                      Work führt <strong>{TASK_STATUS_LABELS[aufgabe.status]}</strong>,{' '}
                      {aufgabe.sourceLabel} steht auf{' '}
                      <strong>
                        {TASK_STATUS_LABELS[aufgabe.quellStatus] ?? aufgabe.quellStatus}
                      </strong>
                      . Work ist maßgeblich; unter <strong>Quellsystem</strong> steht, ob der
                      Unterschied noch zurückgeschrieben wird.
                    </p>
                  )}

                {/**
                 * **„Mein Tag" – wann *ich* daran arbeiten will** (Z-20, 05.10.2026). Oben in den
                 * Planungsfeldern, im selben Segment-Stil wie das Zeitfenster darunter. Nils: *„nicht
                 * um eine Änderung der Fälligkeit, sondern um den gewünschten
                 * Bearbeitungszeitpunkt"* – deshalb steht der Satz darunter, und die Fälligkeit
                 * rechts bleibt, wie sie ist. Nicht an erledigten Aufgaben: dort gibt es nichts
                 * mehr vorzunehmen.
                 */}
                {aufgabe.status !== 'erledigt' && !fachbereich && !gast && (
                  <div className="mt-3 flex flex-wrap items-start gap-2 text-[11.5px]">
                    <span className="w-[86px] shrink-0 pt-[3px] font-semibold text-ink2">
                      Mein Tag
                    </span>
                    <div className="min-w-0">
                      <MarkierKnoepfe taskIds={[aufgabe.id]} mitTag mitDatum />
                      <p className="mt-1 text-[11px] text-muted">
                        Wann du daran arbeiten willst – nur für dich, die Fälligkeit bleibt.
                      </p>
                    </div>
                  </div>
                )}

                <Planungsfelder
                  aufgabeId={aufgabeId}
                  workspaceId={aufgabe.workspaceId ?? null}
                  /**
                   * **In einer persönlichen Welt gibt es diese Fragen nicht**
                   * ([E-232](../../../../docs/ENTSCHEIDUNGEN.md#e-232), angewandt am 04.09.2026).
                   *
                   * Nils, 26.08.2026: *„bei den privaten Workspaces … die Felder, die ja bei
                   * privaten Aufgaben gar nicht gelten dürfen (Wunsch-Entwickler, Mit-Owner)."*
                   * Beim **Anlegen** war das umgesetzt, im **Detail** nicht – dort standen sie
                   * weiter da, mit genau einer möglichen Antwort.
                   */
                  persoenlich={aufgabe.workspaceArt === 'persoenlich'}
                  /**
                   * **Bindet das Zeitfenster, oder bindet etwas Stärkeres?**
                   * ([E-308](../../../../docs/ENTSCHEIDUNGEN.md#e-308)) – sonst stünden an einer
                   * Aufgabe zwei Daten nebeneinander, von denen nur eines gilt.
                   */
                  zeitfensterBindet={
                    (aufgabe.terminWirksam?.quelle ?? 'zeitfenster') === 'zeitfenster'
                  }
                  zeitfenster={aufgabe.zeitfenster ?? null}
                  zeitfensterGeerbt={aufgabe.zeitfensterGeerbt ?? null}
                  zeitfensterAb={aufgabe.zeitfensterAbWirksam ?? null}
                  groesse={aufgabe.groesse ?? null}
                  estimateMinutes={aufgabe.estimateMinutes ?? null}
                  groesseVorschlag={aufgabe.groesseVorschlag ?? null}
                  groesseVorschlagBegruendung={aufgabe.groesseVorschlagBegruendung ?? null}
                  assigneeId={aufgabe.assigneeId ?? null}
                  assigneeName={aufgabe.assigneeName ?? null}
                  mitOwnerPersonId={aufgabe.mitOwnerPersonId ?? null}
                  mitOwnerLeer={aufgabe.mitOwnerLeer ?? false}
                  vorhabenMitOwnerName={aufgabe.vorhabenMitOwnerName ?? null}
                  /**
                   * **`?? []` ist hier kein Zierrat.** Fehlt das Feld – veralteter
                   * Zwischenspeicher nach einem Deploy, oder eine Antwort, die es noch nicht
                   * kennt –, riss die fehlende Liste das **ganze** Aufgabendetail mit
                   * (`Cannot read properties of undefined`). Ein Planungsfeld darf den Dialog
                   * nicht kosten, in dem es steht; gefunden hat es der Test zum Öffnen des
                   * Details, nicht einer zur Planung.
                   */
                  wunschEntwickler={aufgabe.wunschEntwickler ?? []}
                  bearbeiterAlle={aufgabe.bearbeiterAlle ?? []}
                  darfAendern={darfAendern}
                  beiFehler={beiFehler}
                />

                {/** Ein Gast schlägt keine Priorität vor – er ist nicht der Fachbereich, sondern hinzugezogen. */}
                {!gast && (
                  <PrioBereich
                    aufgabeId={aufgabeId}
                    vorschlag={aufgabe.prioVorschlag}
                    aktuell={aufgabe.priority}
                    fachbereich={fachbereich}
                    neuLaden={neuLaden}
                    beiFehler={beiFehler}
                  />
                )}
              </div>

              <div className="min-w-0 max-lg:order-2">
                {/* ── Beschreibung ────────────────────────────────────── */}
                <Beschreibung
                  aufgabeId={aufgabeId}
                  text={aufgabe.description}
                  quellText={aufgabe.quellBeschreibung}
                  quelle={aufgabe.sourceLabel}
                  ausFremderQuelle={aufgabe.sourceId !== 'work'}
                  darfAendern={darfAendern}
                  neuLaden={neuLaden}
                  beiFehler={beiFehler}
                />
                {!aufgabe.quellfelderAenderbar && (
                  <p className="mt-1.5 text-[11px] text-muted">
                    Den Titel führt {aufgabe.sourceLabel} – hier nur lesbar, weil der nächste
                    Abgleich ihn überschreiben würde. Alles andere auf dieser Seite kannst du
                    ändern.
                  </p>
                )}

                {/* ── Unteraufgaben ───────────────────────────────────── */}
                <h4 className="mt-4 mb-1.5 flex items-baseline gap-2 text-xs font-bold">
                  Unteraufgaben
                  {aufgabe.fortschritt.gesamt > 0 && (
                    <span className="tabular text-[11px] font-normal text-muted">
                      {aufgabe.fortschritt.erledigt} / {aufgabe.fortschritt.gesamt}
                    </span>
                  )}
                </h4>

                {aufgabe.fortschritt.gesamt > 0 && (
                  <div className="mb-2 h-[5px] overflow-hidden rounded-full bg-accent-soft">
                    <i
                      className="block h-full rounded-full bg-accent"
                      style={{
                        width: `${(aufgabe.fortschritt.erledigt / aufgabe.fortschritt.gesamt) * 100}%`,
                      }}
                    />
                  </div>
                )}

                {(() => {
                  /**
                   * **Erledigte eingeklappt** (E-341). Bei 14 von 14 abgehakten Unteraufgaben ist die
                   * Liste eine Wand aus Durchgestrichenem; was zählt, steht in „14 / 14" schon im Kopf.
                   */
                  const offene = aufgabe.unteraufgaben.filter((u) => !u.isDone)
                  const erledigte = aufgabe.unteraufgaben.filter((u) => u.isDone)
                  const zeile = (u: (typeof aufgabe.unteraufgaben)[number]) => (
                    <UnteraufgabeZeile
                      key={u.id}
                      u={u}
                      taskId={aufgabe.id}
                      gast={gast}
                      /** Der Strang **dieser** Unteraufgabe – im Strang der Aufgabe stehen sie nicht (E-340). */
                      kommentare={aufgabe.kommentare.filter((k) => k.subtaskId === u.id)}
                      ausKommentar={
                        u.ausKommentarId
                          ? (aufgabe.kommentare.find((k) => k.id === u.ausKommentarId) ?? null)
                          : null
                      }
                      umschalten={(isDone) => unterUmschalten.mutate({ id: u.id, isDone })}
                      loeschen={() => unterLoeschen.mutate(u.id)}
                      personen={allePersonen}
                      neuLaden={neuLaden}
                      beiFehler={beiFehler}
                    />
                  )
                  return (
                    <>
                      {offene.map(zeile)}
                      {erledigte.length > 0 && (
                        <button
                          type="button"
                          onClick={() => setErledigteOffen((v) => !v)}
                          aria-expanded={erledigteOffen}
                          className="mt-1 flex cursor-pointer items-center gap-1 text-[11.5px] text-muted hover:text-accent-deep hover:underline"
                        >
                          {erledigteOffen ? <ChevronDown size={13} /> : <ChevronRight size={13} />}
                          {erledigte.length} erledigte {erledigteOffen ? 'ausblenden' : 'anzeigen'}
                        </button>
                      )}
                      {erledigteOffen && erledigte.map(zeile)}
                    </>
                  )
                })()}

                {/**
                 * **Der Gast hakt nichts ab.** Unterschritte sind die Arbeitsteilung dessen,
                 * der die Aufgabe macht – ein Hinzugezogener liest sie, um zu verstehen, wo es
                 * steht.
                 */}
                <form
                  onSubmit={(e) => {
                    e.preventDefault()
                    if (neueUnteraufgabe.trim()) unterAnlegen.mutate(neueUnteraufgabe.trim())
                  }}
                  className={`mt-1.5 gap-1.5 ${gast ? 'hidden' : 'flex'}`}
                >
                  <input
                    value={neueUnteraufgabe}
                    onChange={(e) => setNeueUnteraufgabe(e.target.value)}
                    placeholder="Unterschritt hinzufügen …"
                    className="flex-1 rounded-[9px] border border-line2 bg-surface px-[11px] py-1.5 text-[12.5px]"
                  />
                  <Button type="submit" variant="ghost" size="sm" disabled={unterAnlegen.isPending}>
                    Hinzufügen
                  </Button>
                </form>

                {/* ── Notizen (Notizbuch, 25.10.2026) – nur, was man lesen darf ── */}
                <NotizenZurAufgabeAbschnitt
                  aufgabeId={aufgabe.id}
                  workspaceId={aufgabe.workspaceId ?? null}
                />

                {/* ── KI-Lösung (Z-21) – nur mit Claude-Zugang ──────────── */}
                <KiLoesung taskId={aufgabe.id} />

                {/* ── Kommentare ──────────────────────────────────────── */}
                <h4 className="mt-4 mb-1.5 text-xs font-bold">Kommentare</h4>
                {aufgabe.kommentare.length === 0 && (
                  <p className="text-[13px] text-muted">Noch keine Kommentare.</p>
                )}
                {/**
                 * **Antworten stehen unter ihrem Kommentar** (E-322).
                 *
                 * Gruppiert wird hier und nicht im Server: die Liste ist ohnehin schon eine
                 * Verschmelzung zweier Tabellen, und die Reihenfolge – chronologisch, Herkunft an
                 * der Zeile – soll dieselbe bleiben. Eine Antwort auf etwas, das nicht (mehr) in
                 * der Liste steht, bleibt eine gewöhnliche Zeile an ihrem Platz in der Zeit.
                 */}
                {(() => {
                  /** Kommentare an Unteraufgaben stehen dort, nicht hier (E-340). */
                  const imStrang = aufgabe.kommentare.filter((k) => !k.subtaskId)
                  const vorhanden = new Set(imStrang.map((k) => k.id))
                  const antworten = new Map<string, typeof aufgabe.kommentare>()
                  for (const k of imStrang) {
                    if (!k.antwortAuf || !vorhanden.has(k.antwortAuf)) continue
                    antworten.set(k.antwortAuf, [...(antworten.get(k.antwortAuf) ?? []), k])
                  }
                  /** Welche Unteraufgabe aus welchem Kommentar entstand – für die Marke am Kommentar. */
                  const unteraufgabeZu = new Map(
                    aufgabe.unteraufgaben
                      .filter((u) => u.ausKommentarId)
                      .map((u) => [
                        u.ausKommentarId as string,
                        { id: u.id, title: u.title, isDone: u.isDone },
                      ]),
                  )

                  return imStrang
                    .filter((k) => !k.antwortAuf || !vorhanden.has(k.antwortAuf))
                    .map((k) => (
                      <div key={k.id}>
                        <Kommentarzeile
                          kommentar={k}
                          unteraufgabe={unteraufgabeZu.get(k.id) ?? null}
                          neuLaden={neuLaden}
                          beiFehler={beiFehler}
                          antwortetGerade={antwortAuf?.id === k.id}
                          alsUnteraufgabe={
                            darfAendern ? (id) => unterAusKommentar.mutate(id) : undefined
                          }
                          antworten={() =>
                            setAntwortAuf({ id: k.id, an: k.authorName ?? 'unbekannt' })
                          }
                        />
                        {(antworten.get(k.id) ?? []).map((a) => (
                          <div key={a.id} className="ml-4 border-l-2 border-line2 pl-3">
                            <Kommentarzeile
                              kommentar={a}
                              unteraufgabe={unteraufgabeZu.get(a.id) ?? null}
                              neuLaden={neuLaden}
                              beiFehler={beiFehler}
                              alsUnteraufgabe={
                                darfAendern ? (id) => unterAusKommentar.mutate(id) : undefined
                              }
                              /**
                               * **Eine Ebene, kein Baum.** Wer auf eine Antwort antwortet, hängt
                               * an derselben obersten Zeile – ab der dritten Ebene liest niemand
                               * mehr, wohin etwas gehört, und die Einrückung frisst die Breite.
                               */
                              antworten={() =>
                                setAntwortAuf({ id: k.id, an: a.authorName ?? 'unbekannt' })
                              }
                            />
                          </div>
                        ))}
                        {/**
                         * **Hier steht das Formular, solange die Antwort diesem Kommentar gilt**
                         * ([E-329](../../../../docs/ENTSCHEIDUNGEN.md#e-329)) – eingerückt wie
                         * die Antworten darüber, damit auch die Form sagt, wohin es gehört.
                         */}
                        {antwortAuf?.id === k.id && (
                          <div className="mt-1 ml-4 border-l-2 border-accent pl-3">
                            {kommentarFormular()}
                          </div>
                        )}
                      </div>
                    ))
                })()}

                {/**
                 * **Unten steht es nur, wenn es oben nicht steht.** Sonst gäbe es das Feld doch
                 * zweimal – genau das, wogegen [E-322](../../../../docs/ENTSCHEIDUNGEN.md#e-322)
                 * entschieden hat.
                 *
                 * Die Bedingung fragt nach dem **sichtbaren Anker**, nicht nach `antwortAuf`
                 * allein: zielt die Antwort auf einen Kommentar, den die Liste gerade nicht
                 * zeigt, hätte sie oben keinen Platz – dann gehört das Formular hierher, mit dem
                 * Hinweis „Antwort an …" darin.
                 */}
                {!antwortAuf ||
                !aufgabe.kommentare.some(
                  (k) =>
                    k.id === antwortAuf.id &&
                    (!k.antwortAuf || !aufgabe.kommentare.some((x) => x.id === k.antwortAuf)),
                )
                  ? kommentarFormular()
                  : null}
              </div>
            </div>

            {/* ── Meta-Spalte (Kap. 4.7) ────────────────────────────── */}
            <div className="min-w-0 max-lg:contents">
              <div className="min-w-0 max-lg:order-1">
                {/**
                 * **In welcher Arbeitswelt diese Aufgabe liegt** (Nils, 26.08.2026: *„genau, bei
                 * den bestehenden Aufgabendetails, da soll man das auch sehen"*).
                 *
                 * Steht ganz oben in der Metaspalte, weil es die Frage vor allen anderen
                 * beantwortet: *wessen Arbeit ist das überhaupt?* Wer aus „Mein Tag" kommt,
                 * sieht drei Welten gemischt – ohne diese Zeile ist an der offenen Aufgabe
                 * nicht zu erkennen, welche es ist.
                 */}
                {aufgabe.workspaceLabel && (
                  <ArbeitsweltZeile
                    taskId={aufgabe.id}
                    workspaceId={aufgabe.workspaceId}
                    label={aufgabe.workspaceLabel}
                    icon={aufgabe.workspaceIcon}
                    art={aufgabe.workspaceArt}
                    darfAendern={darfAendern}
                    istAdmin={sitzung?.role === 'admin'}
                    neuLaden={neuLaden}
                    beiFehler={beiFehler}
                  />
                )}
                <h4 className="mb-1.5 text-xs font-bold">Zuständig & Herkunft</h4>
                <dl className="text-[12px] max-sm:text-[13px]">
                  {/**
                   * **Der Bearbeiter ist hier wechselbar** (Nils, 18.08.2026: *„sollte
                   * übrigens auch inline wechselbar sein von mir"*). Vorher stand hier ein
                   * Name und daneben nichts – wer wechseln wollte, musste die Ansicht
                   * verlassen und den Leitstand suchen.
                   */}
                  <BearbeiterZeile
                    workspaceId={aufgabe.workspaceId ?? null}
                    aufgabeId={aufgabeId}
                    assigneeId={aufgabe.assigneeId}
                    assigneeName={aufgabe.assigneeName}
                    /**
                     * **Weitere Bearbeiter aus der Quelle** (18.08.2026). GitLab führt eine
                     * Liste; gemessen haben 22 von 249 offenen Issues mehr als einen. Der
                     * erste ist der führende und zählt, die übrigen stehen daneben.
                     */
                    weitere={aufgabe.bearbeiterAlle ?? []}
                    darfAendern={darfAendern}
                    neuLaden={neuLaden}
                    beiFehler={beiFehler}
                  />
                  <Zeile
                    bezeichnung="Quelle"
                    wert={
                      aufgabe.istWunsch ? `${aufgabe.sourceLabel} · Wunsch` : aufgabe.sourceLabel
                    }
                  />
                  {/**
                   * Bereich heißt bei GitLab **Projekt**, bei NetCon **Warteschlange** – im
                   * Sinne von CLAUDE.md derselbe Begriff, deshalb eine Zeile.
                   */}
                  <Zeile bezeichnung="Bereich" wert={aufgabe.scopeLabel ?? '–'} />
                  {/**
                   * **Vorhaben, direkt änderbar** (Phase 8).
                   *
                   * Steht bewusst als Auswahl da und nicht als Text mit einem Stift daneben: das
                   * Zuordnen ist hier der häufigste Vorgang, nicht die Ausnahme. Nils, 03.08.2026:
                   * *„wenn nicht, muss es eben von den Entwicklern manuell geholt oder zugeordnet
                   * werden."*
                   *
                   * Und es ist **nicht** der Bereich eine Zeile darüber: ein Bereich ist ein
                   * Repository, ein Vorhaben ein Stück geplanter Arbeit. Sie stehen untereinander,
                   * damit der Unterschied beim Lesen auffällt.
                   */}
                  <VorhabenZeile
                    taskId={aufgabe.id}
                    workspaceId={aufgabe.workspaceId}
                    vorhabenId={aufgabe.vorhabenId ?? null}
                    vorhabenLabel={aufgabe.vorhabenLabel ?? null}
                    darfAendern={darfAendern}
                    neuLaden={neuLaden}
                  />
                  {/**
                   * **Der Meilenstein direkt unter dem Vorhaben** (09.09.2026,
                   * [E-335](../../../../docs/ENTSCHEIDUNGEN.md#e-335)). Nils: *„Wie (und wo)
                   * wird denn die Verbindung zum Meilenstein gemacht? Nur bei der Anlage? Dann
                   * fehlt es im Aufgabendetails-Dialog."* – Es fehlte. Nur an Aufgaben mit
                   * Vorhaben, weil ein Meilenstein immer zu einem gehört.
                   */}
                  {aufgabe.vorhabenId && (
                    <MeilensteinZeile
                      taskId={aufgabe.id}
                      vorhabenId={aufgabe.vorhabenId}
                      meilensteinId={aufgabe.meilensteinId ?? null}
                      meilensteinLabel={aufgabe.meilensteinLabel ?? null}
                      darfAendern={darfAendern}
                      neuLaden={neuLaden}
                      beiFehler={beiFehler}
                    />
                  )}
                  {aufgabe.herkunft && (
                    <Zeile bezeichnung="Kam herein per" wert={aufgabe.herkunft} />
                  )}
                  <SyncZeile taskId={aufgabe.id} />
                  {/**
                   * **Pools, Fälligkeit und Schätzung ändert man hier** (Nils, 19.08.2026:
                   * *„Und Pools, Fällig, Schätzung müssen auch noch editierbar sein"*).
                   *
                   * Alle drei sind **Work-eigen** – es gibt sie in GitLab und NetCon nicht
                   * ([`schreibrecht.ts`](../../../api/src/trpc/schreibrecht.ts)). Sie gehen
                   * deshalb auch an importierten Aufgaben, ohne dass der nächste Abgleich
                   * etwas überschreibt. Bis heute standen sie hier als Text, und wer einen
                   * Pool setzen wollte, musste die Ansicht verlassen und die Aufgabe in einer
                   * Liste wiederfinden.
                   */}
                  <PoolsZeile
                    taskId={aufgabe.id}
                    workspaceId={aufgabe.workspaceId}
                    poolIds={aufgabe.poolIds}
                    darfAendern={darfAendern}
                    neuLaden={neuLaden}
                    beiFehler={beiFehler}
                  />
                  {/**
                   * **Die Priorität ist überall setzbar** (Nils, 28.08.2026: *„Jede Aufgabe soll
                   * eine Prio bekommen KÖNNEN, nicht müssen. So wie Pool überall auswählbar ist
                   * eben Prio."*).
                   *
                   * Zwei Anläufe waren nötig. Erst gab es das Feld gar nicht – der Server konnte
                   * es längst, die Oberfläche rief es nur für Fälligkeit und Aufwand auf. Dann
                   * gab es das Feld, aber nur an Work-Aufgaben, weil `priority` ein Quellfeld ist
                   * und der Importeur es alle fünf Minuten neu schreibt. Bei ~90 % der Aufgaben
                   * blieb die Zeile damit unsichtbar, und die Antwort auf *„warum geht das
                   * nicht"* war jedes Mal dieselbe.
                   *
                   * Seit [E-269](../../../../docs/ENTSCHEIDUNGEN.md) schreibt der Klick
                   * `work_priority`: Work **überlagert** die Quelle, statt gegen sie anzuschreiben
                   * – dasselbe Muster wie bei Status und Beschreibung. Damit gibt es hier nichts
                   * mehr zu unterscheiden.
                   */}
                  <PrioZeile
                    taskId={aufgabe.id}
                    priority={aufgabe.priority}
                    quellPrioritaet={aufgabe.quellPrioritaet}
                    quelle={aufgabe.sourceLabel}
                    darfAendern={darfAendern}
                    neuLaden={neuLaden}
                    beiFehler={beiFehler}
                  />
                  <FaelligZeile
                    taskId={aufgabe.id}
                    dueAt={aufgabe.dueAt}
                    terminWirksam={aufgabe.terminWirksam ?? null}
                    darfAendern={darfAendern}
                    neuLaden={neuLaden}
                    beiFehler={beiFehler}
                  />
                  {/**
                   * **Direkt unter der Fälligkeit** ([Z-13](../../../../docs/phasen/zusatz-13-frist-vorwarnung.md)),
                   * weil sie die Frage beantwortet, die man beim Setzen eines Termins hat:
                   * *und wer erfährt davon?* Eine eigene Rubrik weiter unten hieße, den Wecker
                   * dort zu suchen, wo man ihn gerade nicht braucht.
                   *
                   * **Ohne `darfAendern`**: das Häkchen ändert nichts an der Aufgabe, sondern
                   * legt eine Notiz über den eigenen Lesestand an – dieselbe Sorte Aussage wie
                   * ein Pin oder ein Feed-Haken. Wer die Aufgabe nur sehen darf, darf sich an
                   * ihren Termin erinnern lassen.
                   */}
                  <BenachrichtigenZeile taskId={aufgabe.id} />
                  <SchaetzungZeile
                    taskId={aufgabe.id}
                    minuten={aufgabe.estimateMinutes}
                    darfAendern={darfAendern}
                    neuLaden={neuLaden}
                    beiFehler={beiFehler}
                  />
                  {/**
                   * **„In der Quelle seit" gibt es nur, wenn es eine Quelle gibt** (Nils,
                   * 03.08.2026: *„muss da in der Quelle seit nicht das Anlagedatum rein?"*).
                   *
                   * Bei einer Aufgabe, die in Work entstanden ist, stand dort ein Strich –
                   * und das las sich wie fehlende Daten. Es fehlte nichts: die Frage passte
                   * nicht. Für eine Work-Aufgabe lautet sie „wann angelegt", und die Antwort
                   * steht in `createdAt`.
                   */}
                  <Zeile
                    bezeichnung={aufgabe.sourceCreatedAt ? 'In der Quelle seit' : 'Angelegt'}
                    wert={entstanden(aufgabe).toLocaleDateString('de-DE')}
                  />
                  {aufgabe.workTakenAt && (
                    <Zeile
                      bezeichnung="In Work übernommen"
                      wert={new Date(aufgabe.workTakenAt).toLocaleString('de-DE', {
                        day: '2-digit',
                        month: '2-digit',
                        hour: '2-digit',
                        minute: '2-digit',
                      })}
                    />
                  )}
                </dl>

                {/**
                 * **„Doppelt?" steht unter den Angaben und über dem Verlauf** (Z-11, 28.08.2026).
                 *
                 * Der Ort ist kein Zufall: darüber steht alles, was diese Aufgabe **ist**, darunter
                 * alles, was mit ihr **passiert ist**. Die Frage *„gibt es die schon einmal?"* liegt
                 * genau dazwischen – und sie ist selten genug, dass sie keinen Knopf in der Kopfzeile
                 * verdient, aber folgenreich genug, dass sie nicht in einem Menü verschwinden darf.
                 */}
                {darfAendern && (
                  <DublettenZeile taskId={aufgabe.id} titel={aufgabe.title} neuLaden={neuLaden} />
                )}
              </div>

              <div className="min-w-0 max-lg:order-3">
                <h4 className="mt-4 mb-1.5 text-xs font-bold">Verlauf</h4>
                {verlauf.length === 0 && (
                  <p className="text-[12px] text-muted">Nichts verzeichnet.</p>
                )}
                <div className="max-h-[280px] overflow-y-auto">
                  {verlauf.slice(0, 40).map((e) => {
                    const notiz = verlaufsNotiz(e)
                    return (
                      <div
                        key={e.id}
                        className="flex items-baseline gap-2 border-b border-line py-1 text-[11.5px] last:border-b-0"
                      >
                        <span className="tabular w-[58px] shrink-0 text-muted">
                          {new Date(e.occurredAt).toLocaleDateString('de-DE', {
                            day: '2-digit',
                            month: '2-digit',
                            year: '2-digit',
                          })}
                        </span>
                        <span className="min-w-0 flex-1">
                          {verlaufsText(e, poolName, weltName)}
                          {e.actorName && <span className="ml-1.5 text-muted">{e.actorName}</span>}
                          {/**
                           * **Die Notiz zum Statuswechsel steht unter der Zeile** (21.09.2026,
                           * [E-367](../../../../docs/ENTSCHEIDUNGEN.md#e-367)).
                           *
                           * Nils: *„wenn die in Review stehen, dann den ReviewText auch im Verlauf
                           * unten anzeigen (und auch abnahme texte oder so falls vorhanden)."*
                           *
                           * Geschrieben wurde sie seit [E-231](../../../../docs/ENTSCHEIDUNGEN.md#e-231)
                           * – *„Was soll der Abnehmer wissen?"*, *„Etwas zur Abnahme festhalten?"*,
                           * *„Was fehlt noch?"* –, gelesen aber nur von der **Erinnerungsmail**. Wer
                           * die Aufgabe ansah, fand an der Zeile „In Arbeit → Review/Abnahme" nichts
                           * davon: der Satz, für den jemand extra ein Feld ausgefüllt hat, war nur
                           * für den zu sehen, der zufällig die Mail bekam.
                           *
                           * **Gezeigt wird jede Notiz, nicht nur die aus dem Review**: wegzulassen
                           * wäre nur der Weg, auf dem sie wieder unsichtbar wird. Ob ein Mensch sie
                           * geschrieben hat, entscheidet allein die Optik – siehe `verlaufsNotiz`.
                           */}
                          {notiz && (
                            <span
                              className={`mt-[2px] block italic ${notiz.zitat ? 'text-ink2' : 'text-muted'}`}
                            >
                              {notiz.zitat ? `„${notiz.text}"` : notiz.text}
                            </span>
                          )}
                        </span>
                        <SourceBadge source={e.sourceId as TaskSource} />
                      </div>
                    )
                  })}
                </div>
                {verlauf.length > 40 && (
                  <p className="mt-1 text-[11px] text-muted">
                    … und {verlauf.length - 40} weitere Ereignisse.
                  </p>
                )}
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  )
}

/**
 * **Das Vorhaben einer Aufgabe setzen oder lösen.**
 *
 * Höchstens eines – deshalb eine Auswahlliste und keine Mehrfachauswahl wie bei den Pools.
 * Zwei Vorhaben an einer Aufgabe hießen: „Fortschritt des Vorhabens" wäre nicht mehr zu
 * rechnen, und die Frage „woran arbeite ich hier eigentlich" hätte zwei Antworten.
 */
/**
 * **Die Beschreibung – lesen und ändern** (Phase 9).
 *
 * Nils, 05.08.2026: *„Aufgabenbeschreibung muss auch noch änderbar sein."*
 *
 * ## Was hier passiert, wenn die Aufgabe importiert ist
 *
 * Geschrieben wird **nicht** in die Quelle, sondern daneben: Work legt seine Fassung über
 * die von GitLab oder NetCon (`work_description`). Das ist derselbe Weg wie beim Status und
 * hat denselben Grund – der nächste Abgleich überschreibt die Quellspalte, und eine
 * Änderung, die nach fünf Minuten lautlos verschwindet, ist schlimmer als ein gesperrtes
 * Feld.
 *
 * **Die Folge muss dranstehen**, sonst ist sie eine Überraschung: in GitLab steht weiter der
 * alte Text. Deshalb die Zeile mit „In {Quelle} steht weiter die ursprüngliche Fassung" –
 * und der Knopf, der sie zeigt. Ohne ihn wäre die ursprüngliche Fassung verloren, obwohl
 * sie noch da ist.
 */
function Beschreibung({
  aufgabeId,
  text,
  quellText,
  quelle,
  ausFremderQuelle,
  darfAendern,
  neuLaden,
  beiFehler,
}: {
  aufgabeId: string
  text: string | null
  quellText: string | null
  quelle: string
  /**
   * **Gibt es überhaupt eine fremde Quelle?** (21.09.2026, von Nils gemeldet.)
   *
   * Ohne diese Angabe hieß der Hinweis an einer in Work angelegten Aufgabe: *„In Work
   * bearbeitet. **In Work** steht weiter die ursprüngliche Fassung."* Ein Satz, der sich selbst
   * widerspricht – und dahinter ein echter Denkfehler: `work_description` liegt auch bei einer
   * Work-Aufgabe über `description`, weil der Code für alle Quellen derselbe ist. Sichtbar
   * werden darf dieser Umstand nur dort, wo es tatsächlich ein fremdes System gibt, in dem der
   * alte Text stehen bleibt.
   */
  ausFremderQuelle: boolean
  darfAendern: boolean
  neuLaden: () => void
  beiFehler: (e: Error) => void
}) {
  const [bearbeiten, setBearbeiten] = useState(false)
  const [entwurf, setEntwurf] = useState('')
  const [quelleZeigen, setQuelleZeigen] = useState(false)
  const [laedtHoch, setLaedtHoch] = useState(false)
  /**
   * **Auch hier kann man Personen ansprechen** (18.09.2026,
   * [E-366](../../../../docs/ENTSCHEIDUNGEN.md#e-366)).
   *
   * `MarkdownFeld` bietet die Auswahl nur an, wenn es diese Liste bekommt – bis heute stand
   * hier nichts, mit der Begründung, ein Beschreibungsfeld sei keine Unterhaltung. Nils wollte
   * das Gegenteil: den kompletten Auszug der Beschreibung zur Prüfung an jemanden schicken,
   * ohne ihn darunter noch einmal zu erwähnen.
   */
  const erwaehnbar = useErwaehnbarePersonen()

  /**
   * **Bilder in die Beschreibung** ([E-313](../../../../docs/ENTSCHEIDUNGEN.md#e-313),
   * 02.09.2026).
   *
   * Aus Ticket NCW-6X848M: *„Inline-Bilder in Beschreibungsfeld."* Am Kommentarfeld gab es das
   * längst; hier fehlte es, weil die Ablage am Kommentar**formular** hing statt am Feld.
   *
   * Der Verweis wird an den Entwurf **angehängt**, nicht an der Zeigerstelle eingesetzt: wer
   * ein Bild einfügt, schreibt danach meistens noch einen Satz dazu, und eine Bildmarke mitten
   * im halbfertigen Satz wäre die schlechtere Überraschung.
   *
   * **Was daraus wird, entscheidet der Server**: `anhangVerweis` liest den Medientyp aus seiner
   * Antwort – ein Bild wird `![](anhang:…)` und erscheint im Text, alles andere ein Verweis mit
   * Dateinamen. Hier stünde nur die Endung zur Verfügung, und die lügt.
   */
  async function hochladen(dateien: File[]): Promise<void> {
    setLaedtHoch(true)
    try {
      for (const datei of dateien) {
        const anhang = await anhangHochladen({ aufgabeId }, datei)
        setEntwurf((bisher) => `${bisher}${bisher ? '\n\n' : ''}${anhangVerweis(anhang)}`)
      }
    } catch (fehler) {
      beiFehler(fehler instanceof Error ? fehler : new Error('Hochladen fehlgeschlagen'))
    } finally {
      setLaedtHoch(false)
    }
  }

  const speichern = useMutation({
    mutationFn: () => trpc.tasks.beschreibungSetzen.mutate({ id: aufgabeId, text: entwurf }),
    onSuccess: () => {
      setBearbeiten(false)
      neuLaden()
    },
    onError: beiFehler,
  })

  /** Work hat etwas geändert, wenn die wirksame Fassung von der der Quelle abweicht. */
  /**
   * **Nur bei einer fremden Quelle ein Hinweis.** Bei einer Work-Aufgabe gibt es kein System,
   * in dem die alte Fassung weiterlebt – dort ist der frühere Text schlicht Vergangenheit und
   * steht im Verlauf, nicht in einem blauen Kasten.
   */
  const ueberlagert = ausFremderQuelle && quellText !== null && text !== quellText

  if (bearbeiten) {
    return (
      <>
        <h4 className="mt-4 mb-1.5 text-xs font-bold">Beschreibung</h4>
        <MarkdownFeld
          wert={entwurf}
          setzen={setEntwurf}
          zeilen={10}
          platzhalter="Was ist zu tun? Markdown wird dargestellt. @ spricht jemanden an, Bilder: ziehen oder Strg+V."
          beiAbsenden={() => speichern.mutate()}
          beiDateien={(dateien) => void hochladen(dateien)}
          laedtHoch={laedtHoch}
          erwaehnbar={erwaehnbar}
        />
        {/**
         * **Der Hinweis steht da, weil die Wirkung nicht sichtbar ist.** Eine Erwähnung hier
         * verschickt eine Mail mit dem Text der Beschreibung **und** öffnet die Aufgabe für die
         * Genannten. Wer das nicht weiß, schreibt einen Namen hin und wundert sich – in die
         * eine oder die andere Richtung.
         */}
        <p className="mt-1.5 text-[11.5px] text-muted">
          Wen du mit <b className="font-semibold">@</b> ansprichst, bekommt die Beschreibung per
          Mail und darf die Aufgabe danach öffnen. Nur beim ersten Mal – späteres Ändern verschickt
          nichts erneut.
        </p>
        <div className="mt-1.5 flex flex-wrap items-center gap-1.5">
          <Button
            variant="primary"
            disabled={speichern.isPending}
            onClick={() => speichern.mutate()}
          >
            {speichern.isPending ? 'Speichert …' : 'Speichern'}
          </Button>
          <Button onClick={() => setBearbeiten(false)}>Abbrechen</Button>
          {/**
           * **Leeren heißt zurücksetzen**, nicht löschen. Der Knopf sagt es, damit niemand
           * das Feld leert in der Annahme, danach stünde dort nichts mehr.
           */}
          {ueberlagert && (
            <button
              type="button"
              onClick={() => setEntwurf('')}
              className="cursor-pointer text-[11.5px] text-muted hover:underline"
              title={`Feld leeren und speichern setzt auf die Fassung aus ${quelle} zurück`}
            >
              auf {quelle} zurücksetzen
            </button>
          )}
        </div>
      </>
    )
  }

  return (
    <>
      {/**
       * **Der Knopf steht neben der Überschrift, nicht in ihr.** In ihr wäre er Teil des
       * zugänglichen Namens – die Überschrift hieße „Beschreibung bearbeiten", und wer nach
       * „Beschreibung" sucht, fände sie nicht. Ein Test hat genau das gemeldet.
       */}
      <div className="mt-4 mb-1.5 flex items-baseline gap-2">
        <h4 className="text-xs font-bold">Beschreibung</h4>
        {darfAendern && (
          <button
            type="button"
            onClick={() => {
              setEntwurf(text ?? '')
              setBearbeiten(true)
            }}
            className="cursor-pointer text-[11px] font-normal text-accent-deep hover:underline"
          >
            bearbeiten
          </button>
        )}
      </div>
      {text ? (
        <div className="text-[13px] text-ink2">
          {/**
           * **Markdown wird dargestellt** statt roh gezeigt (Nils, 03.08.2026). HTML aus
           * NetCon geht weiter über `htmlZuText` – dort ist ein Sternchen ein Sternchen,
           * keine Auszeichnung. Die Weiche steckt in `Markdown`.
           */}
          <Markdown text={text} />
          {/** Lange Beschreibungen sind fast immer Mails – dort hilft die Leseansicht am meisten (E-384). */}
          <LesemodusKnopf text={text} titel="Beschreibung" />
        </div>
      ) : (
        <p className="text-[13px] text-muted">Keine Beschreibung hinterlegt.</p>
      )}

      {ueberlagert && (
        <div className="mt-1.5 rounded-lg bg-accent-soft px-2.5 py-1.5 text-[11.5px] text-accent-deep">
          In Work bearbeitet. In {quelle} steht weiter die ursprüngliche Fassung – Work schreibt
          Beschreibungen noch nicht zurück.{' '}
          <button
            type="button"
            onClick={() => setQuelleZeigen((v) => !v)}
            className="cursor-pointer underline"
          >
            {quelleZeigen ? 'ursprüngliche ausblenden' : 'ursprüngliche zeigen'}
          </button>
          {quelleZeigen && (
            <div className="mt-1.5 border-t border-[#c8dcf2] pt-1.5 text-[12.5px] text-ink2">
              <Markdown text={quellText ?? ''} />
            </div>
          )}
        </div>
      )}
    </>
  )
}

/**
 * **Bearbeiter anzeigen und wechseln** (18.08.2026).
 *
 * Ein Auswahlfeld statt eines Dialogs: der Dialog `Zuweisen` gibt es weiter für den
 * Leitstand, wo man aus einer Liste heraus zuweist und dabei Auslastung und Abwesenheit
 * sehen will. Hier steht die Aufgabe schon offen, und die Frage ist eine andere – *wer
 * macht das* –, nicht *wen kann ich noch belasten*.
 *
 * **Nur Entwickler:innen zur Auswahl**, wie bei „kommen in Frage": eine Aufgabe der
 * Administration zuzuweisen ist kein Vorgang, den diese Ansicht anbieten muss.
 */
function BearbeiterZeile({
  aufgabeId,
  assigneeId,
  assigneeName,
  weitere,
  workspaceId,
  darfAendern,
  neuLaden,
  beiFehler,
}: {
  aufgabeId: string
  assigneeId: string | null
  assigneeName: string | null
  weitere: { id: string; name: string }[]
  /**
   * **Die Arbeitswelt der Aufgabe** ([E-323](../../../../docs/ENTSCHEIDUNGEN.md#e-323),
   * 04.09.2026) – ohne sie bot die Liste das ganze Haus an.
   */
  workspaceId: string | null
  darfAendern: boolean
  neuLaden: () => void
  beiFehler: (e: Error) => void
}) {
  /**
   * **Nur, wer diese Aufgabe je zu sehen bekommt** ([E-323](../../../../docs/ENTSCHEIDUNGEN.md#e-323)).
   *
   * Nils, 04.09.2026, mit einem Bild aus „Meine ToDos": *„in Meine ToDos stehen bei Bearbeiter
   * noch andere aus anderen Workspaces."*
   *
   * Die Regel gab es seit Z-07 (`usePersonenDieserWelt`) – samt der Warnung, die genau diesen
   * Fall beschreibt: *„die Zuweisung ginge durch und die Aufgabe verschwände für den
   * Zugewiesenen."* Angewandt war sie im Aufgabendetail nur auf die **Erwähnungen**; die
   * Auswahllisten lasen weiter `personenQuery` roh. In einem persönlichen Workspace – der genau
   * ein Mitglied hat – war das besonders sinnlos.
   */
  const { personen } = usePersonenDieserWelt(workspaceId)
  const zuweisen = useMutation({
    mutationFn: (personId: string | null) =>
      trpc.tasks.zuweisen.mutate({ id: aufgabeId, personId }),
    onSuccess: neuLaden,
    onError: beiFehler,
  })

  const auswahl = personen.filter((p) => p.isActive && zaehltAlsEntwickler(p))

  return (
    <div className="flex items-baseline gap-2 border-b border-line py-[5px] last:border-b-0">
      <dt className="w-[112px] shrink-0 text-muted max-sm:w-[88px]">Bearbeiter</dt>
      <dd className="min-w-0 flex-1">
        <InlineWert
          wert={assigneeName}
          leer="niemand"
          darfAendern={darfAendern}
          bezeichnung="Bearbeiter"
        >
          {(fertig) => (
            <AufklappSelect
              wert={assigneeId ?? ''}
              setzen={(v) => zuweisen.mutate(v || null)}
              fertig={fertig}
              disabled={zuweisen.isPending}
              ariaLabel="Bearbeiter wechseln"
            >
              <option value="">– niemand –</option>
              {/**
               * **Der aktuelle Bearbeiter steht immer drin**, auch wenn er kein Entwickler
               * (mehr) ist – sonst zeigte das Feld „niemand" an einer Aufgabe, die jemanden
               * hat, und der nächste Klick nähme ihn stillschweigend weg.
               */}
              {assigneeId && !auswahl.some((p) => p.id === assigneeId) && (
                <option value={assigneeId}>{assigneeName ?? 'zugewiesen'}</option>
              )}
              {auswahl.map((p) => (
                <option key={p.id} value={p.id}>
                  {p.displayName}
                </option>
              ))}
            </AufklappSelect>
          )}
        </InlineWert>

        {/**
         * **Die weiteren stehen darunter, nicht im Auswahlfeld.** Das Feld beantwortet
         * *„wer ist zuständig"* – eine Frage mit genau einer Antwort, an der WIP-Limit und
         * Durchsatz hängen. Wer noch mitarbeitet, ist eine zweite Auskunft und keine
         * Auswahl ([F-48](../../../../docs/OFFENE-FRAGEN.md)).
         */}
        {weitere.length > 1 && (
          <div className="mt-0.5 text-[11px] text-muted">
            mit{' '}
            {weitere
              .filter((w) => w.id !== assigneeId)
              .map((w) => w.name)
              .join(', ')}
            <span className="ml-1" title="Aus dem Quellsystem – dort sind mehrere eingetragen">
              (aus der Quelle)
            </span>
          </div>
        )}
      </dd>
    </div>
  )
}

/**
 * **Planung an der Aufgabe: Zeitfenster und mögliche Entwickler** (17.08.2026).
 *
 * Nils: *„Mir fehlt noch, dass ich die Aufgaben bearbeiten kann – Texte, mögliche
 * Entwickler, Zeitfenster usw."*
 *
 * ## Warum das geerbte Fenster anders aussieht als das gesetzte
 *
 * Weil sonst jemand eine Angabe löscht, die er nie gemacht hat. Steht das Fenster nur am
 * Vorhaben, zeigt die Ansicht es blass mit dem Zusatz „vom Vorhaben" – und ein Klick auf
 * eine andere Stufe setzt es an **dieser** Aufgabe, ohne das Vorhaben anzufassen.
 *
 * ## „Mögliche Entwickler", nicht „Bearbeiter"
 *
 * Nils schrieb es in der Mehrzahl, und das ist der Unterschied: es ist keine Zuweisung,
 * sondern die Antwort auf *„wer käme dafür in Frage?"*. Beim Ziehen bekommen diese Leute
 * den Vorrang ([E-87](../../../../docs/ENTSCHEIDUNGEN.md)); wer sie übergeht, begründet es.
 */
function Planungsfelder({
  aufgabeId,
  workspaceId,
  persoenlich,
  zeitfensterBindet,
  zeitfenster,
  zeitfensterGeerbt,
  zeitfensterAb,
  groesse,
  estimateMinutes,
  groesseVorschlag,
  groesseVorschlagBegruendung,
  wunschEntwickler,
  bearbeiterAlle,
  assigneeId,
  assigneeName,
  mitOwnerPersonId,
  mitOwnerLeer,
  vorhabenMitOwnerName,
  darfAendern,
  beiFehler,
}: {
  aufgabeId: string
  /** Die Arbeitswelt der Aufgabe – begrenzt jede Personenauswahl (E-323). */
  workspaceId: string | null
  /** Persönliche Welt: „kommen in Frage" und „nimmt ab" entfallen (E-232). */
  persoenlich: boolean
  /** `false`, wenn Fälligkeit, Meilenstein oder Zieltermin früher greifen (E-308). */
  zeitfensterBindet: boolean
  zeitfenster: string | null
  zeitfensterGeerbt: string | null
  /** Der Anker des **wirksamen** Fensters – eigenes, sonst das des Vorhabens. */
  zeitfensterAb: string | Date | null
  groesse: string | null
  /** Die eigene Schätzung – sie schlägt die Größe, und das soll man hier sehen (AP 19.1). */
  estimateMinutes: number | null
  /** Was die KI zur Größe sagt, solange niemand entschieden hat (E-400). */
  groesseVorschlag: string | null
  groesseVorschlagBegruendung: string | null
  wunschEntwickler: { id: string; name: string }[]
  /** Alle Bearbeiter aus der Quelle, führender zuerst – bei GitLab können es mehrere sein. */
  bearbeiterAlle: { id: string; name: string }[]
  assigneeId: string | null
  assigneeName: string | null
  mitOwnerPersonId: string | null
  /** Ausdrücklich „hier nimmt niemand ab" – schlägt den Eintrag des Vorhabens (E-304). */
  mitOwnerLeer: boolean
  vorhabenMitOwnerName: string | null
  darfAendern: boolean
  beiFehler: (e: Error) => void
}) {
  /** Dieselbe Zahl wie im Tagesplan – aus `lib/dauer.ts`, nicht hier noch einmal gerechnet. */
  const dauer = dauerAngabe({ groesse, estimateMinutes, workspaceId }, useWeltdauern())

  /** Dieselbe Grenze wie beim Bearbeiter (E-323): nur Leute aus der Welt dieser Aufgabe. */
  const { personen } = usePersonenDieserWelt(workspaceId)
  /**
   * **Abnehmen darf auch, wer nicht in der Welt ist** (16.09.2026, E-355) – ein Gast aus dem
   * Fachbereich, der die Aufgabe genau deshalb sieht, weil sie auf ihn wartet. Die Welt steht
   * zuerst, die übrigen Erreichbaren folgen als zweite Gruppe.
   */
  const abnahme = usePersonenFuerAbnahme(workspaceId)
  /**
   * **„ändern" merkt sich, wovon es ausging** (Nils, 15.10.2026: *„hier beim Umhängen kann
   * man nicht abbrechen"*).
   *
   * Jeder Klick in der Auswahl speichert sofort (optimistisch, Leitplanke 1) – ein „fertig"
   * allein ließ deshalb keinen Weg zurück. Der Stand beim Öffnen liegt hier; „abbrechen"
   * schreibt ihn zurück. `null` heißt: die Auswahl ist zu.
   */
  const [wunschVorher, setWunschVorher] = useState<{ id: string; name: string }[] | null>(null)
  const wunschOffen = wunschVorher !== null
  const wunschGeaendert =
    wunschVorher !== null &&
    (wunschVorher.length !== wunschEntwickler.length ||
      wunschVorher.some((w) => !wunschEntwickler.some((x) => x.id === w.id)))

  /**
   * **Wer überhaupt in Frage kommt** (Nils, 18.08.2026: *„in «kommen in Frage» bitte die
   * anzeigen, die als «Entwickler» gekennzeichnet sind"*).
   *
   * Vorher stand hier jede aktive Person – auch Administration und Fachbereich. Eine Liste,
   * in der man erst suchen muss, wer programmiert, beantwortet die Frage nicht, die sie
   * stellt.
   */
  const auswahl = personen.filter((p) => p.isActive && zaehltAlsEntwickler(p))

  /**
   * **Optimistisch** (21.08.2026): die Stufe springt beim Klick, nicht nach der Antwort.
   * Die Vorhersage steht in [`mutationen.ts`](../lib/mutationen.ts) – auch die Frist, die
   * mit jeder Einstufung neu beginnt.
   */
  const planung = usePlanungSetzen(aufgabeId)
  /**
   * Der Rücksetzer sitzt in der Mutation, die **Meldung** gehört ins Fenster – deshalb der
   * zweite `onError` am Aufruf. Beide laufen: erst der Rücksetzer, dann der Hinweis. Ohne
   * ihn wäre die zurückspringende Stufe unerklärlich.
   */
  const setzen = {
    isPending: planung.isPending,
    mutate: (v: PlanungEingabe) => planung.mutate(v, { onError: beiFehler }),
  }

  /**
   * **Über den Größen-Vorschlag entscheiden – hier statt nur im Eingang** (E-400).
   *
   * Dieselbe Prozedur wie im Eingang (`eingang.triageUebernehmen`), damit das Übernehmen auch
   * dasselbe tut: Größe setzen **und** den Vorschlag als entschieden abhaken. Eine zweite
   * Schreibstelle hieße, dass ein hier übernommener Vorschlag im Eingang weiter offen stünde.
   */
  const queryClient = useQueryClient()
  const vorschlagEntscheiden = useMutation({
    mutationFn: (wert: string | null) =>
      trpc.eingang.triageUebernehmen.mutate({ taskId: aufgabeId, werte: { groesse: wert } }),
    onSuccess: async () => {
      await nebenBestandVerwerfen(queryClient)
      await queryClient.invalidateQueries({ queryKey: ['eingang'] })
    },
    onError: beiFehler,
  })

  /**
   * **Die Bearbeiter zählen mit** (Nils, 18.08.2026: *„Wenn schon ein Bearbeiter ausgewählt
   * ist, dann unter «kommen in Frage» den natürlich auch vorauswählen."*).
   *
   * Sie stehen nicht in `task_wunsch` – sie *sind* zugewiesen, das ist mehr als „kommt in
   * Frage". Aber „noch niemand" an einer Aufgabe zu lesen, die jemanden hat, ist offenkundig
   * falsch. Also werden sie hier dazugerechnet und beim Speichern mitgeschickt, ohne dass sie
   * jemand anklicken muss.
   *
   * **Alle, nicht nur der führende** (Nils, 19.08.2026: *„wenn mehrere bearbeiter aus gitlab
   * kommen dann auch alle in «kommen in Frage»"*). GitLab kennt mehrere Bearbeiter, der
   * Import legt sie seit dem 18.08. vollständig in `task_bearbeiter` ab – **22 von 249**
   * offenen Issues haben mehr als einen. Hier stand bis eben nur `assigneeId`: das Detail
   * zeigte oben *„Simon von der Assen, mit Davin Hertz, Christian Hunhoff"* und darunter
   * unter „kommen in Frage" allein Simon. Zwei Auskünfte über dieselbe Sache, die sich
   * widersprechen.
   *
   * **Abgeleitet statt nachgetragen.** Es wird nichts in `task_wunsch` geschrieben – weder
   * hier noch beim Import. Sonst hätte jeder Abgleich einen Namen zurückgeholt, den jemand
   * bewusst abgewählt hat (dasselbe Flattern im Fünf-Minuten-Takt, vor dem
   * `aufgabeEinpflegen` bei den quellgeführten Feldern warnt). Weil die Regel beim Lesen
   * greift, gilt sie ohne Nachzieh-Lauf auch für den ganzen Bestand.
   *
   * Der führende steht vorn; `assigneeId` kommt zusätzlich dazu, weil ihn bei NetCon die
   * Historie setzt und `task_bearbeiter` dann leer ist.
   */
  const ausQuelle = [
    ...(assigneeId ? [{ id: assigneeId, name: assigneeName ?? 'Bearbeiter' }] : []),
    ...bearbeiterAlle,
  ].filter(
    (p, i, alle) =>
      alle.findIndex((x) => x.id === p.id) === i && !wunschEntwickler.some((w) => w.id === p.id),
  )

  const gewaehlt = new Set([...wunschEntwickler.map((w) => w.id), ...ausQuelle.map((p) => p.id)])
  /** Wer aus der Quelle kommt – auch, wenn er zusätzlich als Wunsch steht (F-70). */
  const abgeleitet = new Set([
    ...(assigneeId ? [assigneeId] : []),
    ...bearbeiterAlle.map((b) => b.id),
  ])

  /** Für die Vorhersage: der Name zu einer Kennung, aus welcher der drei Listen auch immer. */
  const nameVon = (id: string): string =>
    personen.find((p) => p.id === id)?.displayName ??
    wunschEntwickler.find((w) => w.id === id)?.name ??
    ausQuelle.find((p) => p.id === id)?.name ??
    'Unbekannt'

  /** Die Frist des wirksamen Fensters – leer, wenn keines gesetzt ist oder der Anker fehlt. */
  const stand = zeitfensterStand(zeitfenster ?? zeitfensterGeerbt, zeitfensterAb)

  return (
    <div className="mt-3 rounded-lg border border-line px-2.5 py-2">
      {/**
       * **Fünf Stufen als eine Leiste** (Nils, 19.08.2026: *„Das Zeitfenster am besten mit
       * Buttons/Symbolen gestalten (sieht sonst komisch aus)"*).
       *
       * Zwischenstand war ein Auswahlfeld – weil fünf Knöpfe mit „Innerhalb 10 Tage" darauf
       * in zwei Reihen umbrachen und die Nebenangabe zum größten Block der Seite machten.
       * Mit den Kurzfassungen aus `ZEITFENSTER_KURZ` passen sie in eine Zeile, und damit
       * fällt der Umweg über das Aufklappen weg: die Stufe ist ein Klick statt drei.
       *
       * **Warum Wörter und keine Sinnbilder.** Für „Jetzt" gäbe es ein Sinnbild; für „10
       * Tage" gegen „30 Tage" keines, das man nicht erst lernen müsste. Ein Zeichen,
       * dessen Bedeutung im `title` steht, ist ein Rätsel mit Auflösung – die Leiste soll man
       * lesen, nicht entziffern. Die eine Stufe, die aus der Reihe fällt, trägt stattdessen
       * die Farbe: **Jetzt** ist rot, weil es die einzige ist, die etwas anderes verdrängt.
       *
       * Ein zweiter Klick auf dieselbe Stufe nimmt sie zurück – wie bei der Größe darunter.
       */}
      <div className="flex flex-wrap items-center gap-2 text-[11.5px]">
        <span className="w-[86px] shrink-0 font-semibold text-ink2">Zeitfenster</span>
        <div className="inline-flex overflow-hidden rounded-lg border border-line2">
          {ZEITFENSTER.map((z, i) => {
            const aktiv = zeitfenster === z
            /** Blass gefüllt: gilt, kommt aber vom Vorhaben – und darf hier nicht gelöscht aussehen. */
            const geerbt = !zeitfenster && zeitfensterGeerbt === z
            return (
              <button
                key={z}
                type="button"
                disabled={!darfAendern || setzen.isPending}
                aria-pressed={aktiv}
                title={`${ZEITFENSTER_LABELS[z]}${geerbt ? ' – vom Vorhaben' : ''}`}
                onClick={() => setzen.mutate({ zeitfenster: aktiv ? null : z })}
                className={`px-2 py-[3px] font-semibold disabled:cursor-default ${
                  i > 0 ? 'border-l border-line2' : ''
                } ${
                  aktiv
                    ? z === 'jetzt'
                      ? 'bg-pool-sofort text-white'
                      : 'bg-ink text-white'
                    : geerbt
                      ? 'bg-[#e8e6e1] text-ink2'
                      : 'text-ink2 hover:bg-[#f3f2ef]'
                } ${darfAendern ? 'cursor-pointer' : ''}`}
              >
                {ZEITFENSTER_KURZ[z]}
              </button>
            )
          })}
        </div>
        {!zeitfenster && (
          <span className="text-[11px] text-muted">
            {zeitfensterGeerbt ? 'vom Vorhaben' : 'nicht eingeordnet'}
          </span>
        )}
        {/**
         * **Die Frist neben der Stufe** (Nils, 20.08.2026: *„nicht dass das quasi immer auf 10
         * Tage steht und damit niemand weiß, wann denn jetzt diese 10 Tage sind"*).
         *
         * Gerechnet ab der Einstufung, nicht ab heute – sonst stünde hier jeden Tag ein
         * anderes Datum und nie ein abgelaufenes. Ohne Anker (Zeilen von vor der Spalte)
         * steht **nichts** statt eines erfundenen Termins.
         */}
        {/**
         * **Wenn etwas Stärkeres bindet, ist diese Frist nur noch eine Angabe**
         * ([E-308](../../../../docs/ENTSCHEIDUNGEN.md#e-308), 02.09.2026).
         *
         * Nils sah an einer Aufgabe „bis Mo., 07.09." und in „Mein Tag" „5 Tage überfällig" –
         * beides stimmte, aber nur eines galt: der Meilenstein des Vorhabens (28.08.) schlägt
         * das Zeitfenster ([E-147](../../../../docs/ENTSCHEIDUNGEN.md#e-147)). Die Zeile bleibt
         * stehen, weil sie die Stufe erklärt, sagt aber nicht mehr „bis", als wäre sie der
         * Termin – und „überfällig" darf sie dann gar nicht sagen, sonst stünde dasselbe Wort
         * zweimal mit zwei Daten da.
         */}
        {stand.datum !== null &&
          (zeitfensterBindet ? (
            <span
              className={`text-[11px] ${stand.abgelaufen ? 'font-semibold text-pool-sofort' : 'text-muted'}`}
              title={
                stand.abgelaufen
                  ? 'Die Frist aus dem Zeitfenster ist vorbei – neu einstufen startet sie neu.'
                  : 'Gerechnet ab dem Zeitpunkt der Einstufung, in Werktagen.'
              }
            >
              {stand.abgelaufen ? `überfällig seit ${stand.datum}` : `bis ${stand.datum}`}
            </span>
          ) : (
            <span
              className="text-[11px] text-muted"
              title="Es gilt ein früherer Termin – er steht oben bei „Fällig“."
            >
              Fenster bis {stand.datum}
            </span>
          ))}
      </div>

      {/**
       * **Klein, mittel, groß** (Nils, 17.08.2026). Drei Knöpfe statt eines Minutenfeldes:
       * `estimate_minutes` gibt es seit Phase 4 und **0 von 150** offenen Aufgaben tragen
       * eines. Eine ungenaue Angabe, die es gibt, schlägt eine genaue, die es nicht gibt.
       *
       * Nochmal klicken nimmt die Bewertung zurück.
       *
       * ## Ohne Bewertung ist **kein** Knopf gewählt (01.10.2026, [E-393](../../../../docs/ENTSCHEIDUNGEN.md#e-393))
       *
       * Hier stand: *„«mittel» ist blass, solange niemand es gesagt hat – es ist die
       * Rechengröße, nicht eine Entscheidung."* Die Absicht war richtig, die Form falsch. Nils,
       * mit Bildschirmfoto: *„Stimmt ja gar nicht, nicht bewertet darf den mittel button nicht
       * gedrückt halten."*
       *
       * Ein blass gefüllter Knopf **ist** die Gestalt eines gedrückten Knopfes – nur leiser.
       * Daneben stand gleichzeitig „nicht bewertet", und `aria-pressed` meldete `false`: Auge
       * und Vorlesesoftware sagten das Gegenteil voneinander, und keines von beiden war
       * verlässlich.
       *
       * Die Auskunft, die der blasse Knopf tragen sollte, ist eine **Aussage** und gehört in
       * Worte: „nicht bewertet – gilt als mittel". Genau so steht es auch im Ziehen-Fenster;
       * dieselbe Sache zweimal verschieden zu zeigen war der zweite Fehler.
       *
       * **Die Balken davor** (19.08.2026) sind das Sinnbild, das es beim Zeitfenster nicht
       * gibt: drei Stufen, aufsteigend, ohne Vokabel. Wer die Leiste einmal gesehen hat,
       * erkennt die Stufe am Umriss – und die Wörter bleiben trotzdem stehen, weil ein
       * Sinnbild allein raten lässt, welche Skala gemeint ist.
       */}
      <div className="mt-1.5 flex flex-wrap items-center gap-1.5 text-[11.5px]">
        <span className="w-[86px] shrink-0 font-semibold text-ink2">Größe</span>
        {AUFGABEN_GROESSEN.map((g) => (
          <button
            key={g}
            type="button"
            disabled={!darfAendern || setzen.isPending}
            onClick={() => setzen.mutate({ groesse: g === groesse ? null : g })}
            aria-pressed={g === groesse}
            className={`flex items-center gap-1.5 rounded-lg px-2 py-[3px] font-semibold disabled:cursor-default ${
              g === groesse
                ? 'bg-ink text-white'
                : 'border border-line2 text-ink2 hover:bg-[#f3f2ef]'
            } ${darfAendern ? 'cursor-pointer' : ''}`}
          >
            <GroesseBalken stufe={g} />
            {GROESSE_LABELS[g]}
          </button>
        ))}
      </div>

      {/**
       * **Womit der Plan rechnet** ([Z-19](../../../../docs/phasen/zusatz-19-tagesplan.md)
       * AP 19.1, [E-390](../../../../docs/ENTSCHEIDUNGEN.md#e-390)).
       *
       * Die Dauer entstand für den Tagesplan und war bis heute **nur dort** zu sehen – also
       * genau da, wo man sie nicht nachprüfen kann: Im Kalender sieht man, wie lang ein Block
       * ist, aber nicht, woher die Länge kommt.
       *
       * Hier steht beides, Zahl **und** Herkunft. Das ist nicht Schmuck: `2 h` allein lässt
       * offen, ob jemand geschätzt hat oder eine Vorgabe geraten hat – und das ist der
       * Unterschied, auf den es ankommt, wenn der Plan nicht aufgeht.
       *
       * An dieser Stelle stand vorher *„nicht bewertet – gilt als mittel"*. Das stimmte seit
       * E-390 nicht mehr: Ohne Größe rechnet Work mit **15 Minuten**, nicht mit zwei Stunden.
       * Ein falscher Hinweis an der Stelle, an der man nachschaut, ist schlimmer als keiner.
       */}
      <div className="mt-1.5 flex flex-wrap items-center gap-1.5 text-[11.5px]">
        <span className="w-[86px] shrink-0 font-semibold text-ink2">Plant mit</span>
        <span className="tabular font-semibold">{dauerText(dauer.minuten)}</span>
        <span className="text-[11px] text-muted">{dauerHerkunftText(dauer.herkunft)}</span>
      </div>

      {/**
       * **Der Größen-Vorschlag, dort wo er etwas ändert** ([E-400](../../../../docs/ENTSCHEIDUNGEN.md#e-400)).
       *
       * Vorschläge standen bis zum 02.10.2026 **nur im Eingang**. Verließ eine Aufgabe ihn, nahm
       * sie ihren Vorschlag mit, ohne ihn je gezeigt zu haben: 58 Stück verfielen so an
       * Aufgaben, die inzwischen erledigt sind ([F-93](../../../../docs/OFFENE-FRAGEN.md#f-93)).
       * Die KI hat gearbeitet, das Ergebnis hat nie jemand angesehen.
       *
       * Er steht **direkt unter der Zahl, die er ändern würde** – nicht oben bei den Stufen.
       * Wer hier nachschaut, fragt „womit rechnet der Plan"; die Antwort „die KI hielte es für
       * größer" gehört in dieselbe Zeile wie die Frage.
       *
       * **Nur, solange niemand entschieden hat** und die Stufe nicht ohnehin dieselbe ist.
       */}
      {groesseVorschlag && groesseVorschlag !== groesse && (
        <div className="mt-1.5 flex flex-wrap items-center gap-1.5 text-[11.5px]">
          <span className="w-[86px] shrink-0" />
          <span className="rounded-lg bg-accent-soft px-2 py-[3px] text-accent-deep">
            KI:{' '}
            <b className="font-semibold">
              {GROESSE_LABELS[groesseVorschlag as Aufgabengroesse] ?? groesseVorschlag}
            </b>
          </span>
          {groesseVorschlagBegruendung && (
            <span className="text-[11px] text-muted">{groesseVorschlagBegruendung}</span>
          )}
          {darfAendern && (
            <>
              <button
                type="button"
                disabled={vorschlagEntscheiden.isPending}
                onClick={() => vorschlagEntscheiden.mutate(groesseVorschlag)}
                className="cursor-pointer rounded-lg px-1.5 py-[3px] font-semibold text-accent-deep hover:bg-accent-soft disabled:cursor-default"
              >
                übernehmen
              </button>
              <button
                type="button"
                disabled={vorschlagEntscheiden.isPending}
                onClick={() => vorschlagEntscheiden.mutate(null)}
                className="cursor-pointer rounded-lg px-1.5 py-[3px] text-muted hover:bg-[#f3f2ef] disabled:cursor-default"
              >
                verwerfen
              </button>
            </>
          )}
        </div>
      )}

      {/** Persönliche Welt: entfällt (E-232). */}
      {!persoenlich && (
        <div className="mt-1.5 flex flex-wrap items-center gap-1.5 text-[11.5px]">
          <span className="w-[86px] shrink-0 font-semibold text-ink2">Kommen in Frage</span>
          {gewaehlt.size === 0 ? (
            <span className="text-muted">noch niemand</span>
          ) : (
            <>
              {ausQuelle.map((p) => (
                <span
                  key={p.id}
                  className="rounded-md bg-accent px-2 py-px text-white"
                  title={
                    p.id === assigneeId
                      ? 'Ist der Bearbeiter – zählt hier automatisch mit'
                      : 'Bearbeitet mit (aus der Quelle) – zählt hier automatisch mit'
                  }
                >
                  {p.name} ✓
                </span>
              ))}
              {wunschEntwickler.map((w) => (
                <span key={w.id} className="rounded-md bg-accent-soft px-2 py-px text-accent-deep">
                  {w.name}
                </span>
              ))}
            </>
          )}
          {darfAendern && (
            <button
              type="button"
              onClick={() => setWunschVorher(wunschOffen ? null : wunschEntwickler)}
              className="cursor-pointer text-accent-deep hover:underline"
            >
              {wunschOffen ? 'fertig' : 'ändern'}
            </button>
          )}
          {/** Wie beim Kommentar (01.10.2026): erst, wenn es etwas abzubrechen gibt. */}
          {wunschGeaendert && (
            <button
              type="button"
              disabled={setzen.isPending}
              onClick={() => {
                if (wunschVorher) setzen.mutate({ wunschEntwickler: wunschVorher })
                setWunschVorher(null)
              }}
              className="cursor-pointer text-muted hover:underline disabled:cursor-default"
            >
              abbrechen
            </button>
          )}
        </div>
      )}

      {/**
       * **Wer abnimmt** (Nils, 18.08.2026). Ohne Eintrag gilt der des Vorhabens; steht auch
       * dort keiner, wird die Aufgabe ohne Abnahme erledigt
       * ([E-106](../../../../docs/ENTSCHEIDUNGEN.md)).
       */}
      {!persoenlich && (
        <div className="mt-1.5 flex flex-wrap items-baseline gap-2 text-[11.5px]">
          <span className="w-[86px] shrink-0 font-semibold text-ink2">Nimmt ab</span>
          <div className="min-w-[180px] flex-1">
            {/**
             * **Das ist der Mit-Owner der Aufgabe** (Nils, 19.08.2026: *„Mit-Owner kann man
             * immer noch nicht anlegen oder wäre das der «Nimmt-Ab»?"*).
             *
             * Ja – hier wird er *zugewiesen*. **Angelegt** wird eine solche Person in den
             * Einstellungen unter „Benutzer" mit „+ Person anlegen" und der Rolle *Mit-Owner
             * (Fachbereich)*; sie steht in keinem Quellsystem und kommt deshalb über keinen
             * Import herein. Diese Liste zeigt jede aktive Person, nicht nur Mit-Owner:
             * abnehmen darf auch ein Kollege aus dem Team, und die Rolle entscheidet darüber,
             * was jemand *sonst* sieht – nicht darüber, wer eine Abnahme unterschreiben kann.
             */}
            {/**
             * **Drei Zustände, und „niemand" ist einer davon**
             * ([E-304](../../../../docs/ENTSCHEIDUNGEN.md#e-304)).
             *
             * Nils, 02.09.2026: *„Bei Aufgaben in Vorhaben mit Mit-Ownern da kann man den
             * Mit-Owner nicht entfernen. Der Mit-Owner soll natürlich nicht mit jeder Aufgabe
             * genervt werden müssen, wenn man das nicht möchte."*
             *
             * Vorher hatte die Auswahl an einer Aufgabe **im Vorhaben** gar keinen Eintrag für
             * „niemand": der leere Eintrag hieß dort „vom Vorhaben: …", und wer den Abnehmer
             * entfernte, bekam den des Vorhabens zurück. Jetzt stehen beide da, und sie sagen
             * verschiedene Dinge – ohne Vorhaben-Mit-Owner fallen sie zusammen, dann bleibt es
             * bei einem Eintrag.
             *
             * Der Wert `niemand` ist nur ein Schlüssel für dieses `<select>`; über die Leitung
             * gehen zwei Felder (`mitOwnerLeer` und `mitOwnerPersonId`), weil die Datenbank die
             * drei Zustände genauso trennt.
             */}
            <InlineWert
              wert={
                mitOwnerLeer
                  ? null
                  : mitOwnerPersonId
                    ? ([...abnahme.inWelt, ...abnahme.weitere].find(
                        (p) => p.id === mitOwnerPersonId,
                      )?.displayName ?? 'zugewiesen')
                    : vorhabenMitOwnerName
                      ? `${vorhabenMitOwnerName} (vom Vorhaben)`
                      : null
              }
              leer="niemand, keine Abnahme"
              darfAendern={darfAendern}
              bezeichnung="Wer abnimmt"
            >
              {(fertig) => (
                <AufklappSelect
                  wert={mitOwnerLeer ? 'niemand' : (mitOwnerPersonId ?? '')}
                  setzen={(v) =>
                    setzen.mutate({
                      mitOwnerLeer: v === 'niemand',
                      mitOwnerPersonId: v === 'niemand' || v === '' ? null : v,
                    })
                  }
                  fertig={fertig}
                  disabled={setzen.isPending}
                  ariaLabel="Mit-Owner, der abnimmt"
                  className="text-[11.5px]"
                >
                  <option value="">
                    {vorhabenMitOwnerName
                      ? `vom Vorhaben: ${vorhabenMitOwnerName}`
                      : '– niemand, keine Abnahme –'}
                  </option>
                  {vorhabenMitOwnerName && (
                    <option value="niemand">– niemand, keine Abnahme –</option>
                  )}
                  <PersonenOptionen inWelt={abnahme.inWelt} weitere={abnahme.weitere} />
                </AufklappSelect>
              )}
            </InlineWert>
          </div>
          {!mitOwnerLeer && (mitOwnerPersonId ?? vorhabenMitOwnerName) && (
            <span className="basis-full text-[11px] text-muted">
              Erledigt wird erst nach der Abnahme – und nicht vom Bearbeiter selbst.
            </span>
          )}
          {mitOwnerLeer && vorhabenMitOwnerName && (
            <span className="basis-full text-[11px] text-muted">
              Diese Aufgabe wird ohne Abnahme erledigt – {vorhabenMitOwnerName} hört nichts davon.
            </span>
          )}
        </div>
      )}

      {wunschOffen && (
        <div className="mt-1.5 flex max-h-[160px] flex-wrap gap-1 overflow-y-auto border-t border-line pt-1.5">
          {auswahl.map((p) =>
            /**
             * **Abgeleitetes lässt sich nicht abwählen** ([F-70](../../../../docs/OFFENE-FRAGEN.md),
             * 15.09.2026). Nils an GitLab !486: *„Hier bei der Aufgabe kann ich mich als
             * Wunsch-Entwickler nicht entfernen."* Er stand nicht in `task_wunsch`, sondern als
             * Bearbeiter in GitLab (E-151) – Abwählen löschte eine Zeile, die es nie gab, und
             * der Name blieb. Jetzt sagt der Knopf, woher der Name kommt, und ist gesperrt: der
             * Weg, ihn loszuwerden, führt in die Quelle, und das ist die Wahrheit.
             */
            abgeleitet.has(p.id) ? (
              <span
                key={p.id}
                title="Steht in der Quelle als Bearbeiter – zählt hier automatisch mit und lässt sich nur dort entfernen"
                className="cursor-not-allowed rounded-md bg-accent/60 px-2 py-px text-[11.5px] text-white"
              >
                {p.displayName} ✓
              </span>
            ) : (
              <button
                key={p.id}
                type="button"
                disabled={setzen.isPending}
                onClick={() => {
                  /**
                   * Geschrieben werden nur die **ausdrücklichen** Wünsche – nicht die abgeleiteten
                   * Namen aus der Quelle (F-70). Sonst würde der erste Klick alle Bearbeiter aus
                   * GitLab zu Wunsch-Zeilen machen, die danach niemand mehr loswird.
                   */
                  const explizit = new Set(wunschEntwickler.map((w) => w.id))
                  const ids = explizit.has(p.id)
                    ? [...explizit].filter((id) => id !== p.id)
                    : [...explizit, p.id]
                  /**
                   * **Die Namen reisen mit**, nicht nur die Kennungen: bis zur Antwort
                   * stünde an der Marke sonst eine Kennung (siehe `PlanungEingabe`).
                   */
                  setzen.mutate({ wunschEntwickler: ids.map((id) => ({ id, name: nameVon(id) })) })
                }}
                className={`cursor-pointer rounded-md px-2 py-px text-[11.5px] ${
                  gewaehlt.has(p.id)
                    ? 'bg-accent text-white'
                    : 'border border-line2 text-ink2 hover:bg-[#f3f2ef]'
                }`}
              >
                {p.displayName}
              </button>
            ),
          )}
          {auswahl.length === 0 && (
            <span className="text-[11.5px] text-muted">
              Niemand ist als Entwickler:in gekennzeichnet – Rollen stehen in der
              Benutzerverwaltung.
            </span>
          )}
        </div>
      )}
    </div>
  )
}

/**
 * **Drei aufsteigende Balken – klein, mittel, groß** (19.08.2026).
 *
 * `bg-current` und die Deckkraft statt fester Farben: der Knopf wechselt zwischen weißer
 * Schrift auf Schwarz und grauer auf Weiß, und ein Sinnbild, das dabei die Farbe *nicht*
 * mitwechselt, sieht auf einer der beiden Fassungen falsch aus. `aria-hidden`, weil daneben
 * das Wort steht – vorgelesen wäre es die zweite Nennung derselben Angabe.
 */
function GroesseBalken({ stufe }: { stufe: Aufgabengroesse }) {
  const gefuellt = { klein: 1, mittel: 2, gross: 3 }[stufe]
  return (
    <span className="inline-flex h-[9px] items-end gap-[1.5px]" aria-hidden="true">
      {[3, 6, 9].map((hoehe, i) => (
        <i
          key={hoehe}
          className="w-[2.5px] rounded-[1px] bg-current"
          style={{ height: hoehe, opacity: i < gefuellt ? 1 : 0.3 }}
        />
      ))}
    </span>
  )
}

/**
 * **Prioritätsvorschläge aus dem Fachbereich** (Phase 9, Konzept Kap. 5.8).
 *
 * Zwei Seiten desselben Vorgangs in einem Baustein, weil sie dieselbe Zeile teilen:
 *
 * - Der **Mit-Owner** schlägt vor – mit Begründung, sonst kann sie niemand beurteilen.
 * - Das **Team** antwortet – übernehmen oder verwerfen, beides sichtbar.
 *
 * ## Warum kein Vorschlag „im Hintergrund"
 *
 * Ein Vorschlag, den nur eine Liste in der Steuerung zeigt, erreicht die Aufgabe nie. Er
 * gehört dorthin, wo über sie entschieden wird – ins Detail, über die Beschreibung, wo ihn
 * auch sieht, wer aus einem ganz anderen Grund hier gelandet ist.
 */
function PrioBereich({
  aufgabeId,
  vorschlag,
  aktuell,
  fachbereich,
  neuLaden,
  beiFehler,
}: {
  aufgabeId: string
  vorschlag: { prioritaet: string; grund: string; von: string | null; am: Date | string } | null
  aktuell: string | null
  fachbereich: boolean
  neuLaden: () => void
  beiFehler: (e: Error) => void
}) {
  const [formularOffen, setFormularOffen] = useState(false)
  const [wunsch, setWunsch] = useState<TaskPriority>('A')
  const [grund, setGrund] = useState('')

  const vorschlagen = useMutation({
    mutationFn: () =>
      trpc.tasks.prioVorschlagen.mutate({ taskId: aufgabeId, prioritaet: wunsch, grund }),
    onSuccess: () => {
      setFormularOffen(false)
      setGrund('')
      neuLaden()
    },
    onError: beiFehler,
  })

  const entscheiden = useMutation({
    mutationFn: (uebernehmen: boolean) =>
      trpc.tasks.prioVorschlagEntscheiden.mutate({ taskId: aufgabeId, uebernehmen }),
    onSuccess: neuLaden,
    onError: beiFehler,
  })

  if (vorschlag) {
    return (
      <div className="mt-2 rounded-lg border border-[#e6d9b0] bg-[#fdf8e8] px-2.5 py-2 text-[12px]">
        <div className="flex flex-wrap items-baseline gap-1.5">
          <strong>Vorschlag: Priorität {vorschlag.prioritaet}</strong>
          <span className="text-ink2">
            von {vorschlag.von ?? 'jemandem'}
            {aktuell ? ` · steht auf ${aktuell}` : ' · bisher ohne Priorität'}
          </span>
        </div>
        <p className="mt-1 text-ink2 italic">„{vorschlag.grund}"</p>
        {fachbereich ? (
          /** Er sieht seinen eigenen Vorschlag – und dass er noch offen ist. */
          <p className="mt-1 text-[11px] text-muted">
            Wartet auf Antwort aus dem Team. Solange gilt die eingetragene Priorität.
          </p>
        ) : (
          <div className="mt-1.5 flex gap-1.5">
            <Button
              variant="primary"
              disabled={entscheiden.isPending}
              onClick={() => entscheiden.mutate(true)}
            >
              Übernehmen
            </Button>
            <Button disabled={entscheiden.isPending} onClick={() => entscheiden.mutate(false)}>
              Verwerfen
            </Button>
          </div>
        )}
      </div>
    )
  }

  /** Ohne offenen Vorschlag hat nur der Fachbereich hier etwas zu tun. */
  if (!fachbereich) return null

  if (!formularOffen) {
    return (
      <button
        type="button"
        onClick={() => setFormularOffen(true)}
        className="mt-2 cursor-pointer text-[12px] text-accent-deep hover:underline"
      >
        Priorität vorschlagen
      </button>
    )
  }

  return (
    <div className="mt-2 rounded-lg border border-line2 px-2.5 py-2">
      <div className="flex flex-wrap items-center gap-1.5 text-[12px]">
        <span className="text-ink2">Vorschlagen:</span>
        {TASK_PRIORITIES.map((p) => (
          <button
            key={p}
            type="button"
            onClick={() => setWunsch(p)}
            className={`cursor-pointer rounded-lg px-2.5 py-[3px] text-[11.5px] font-semibold ${
              p === wunsch ? 'bg-ink text-white' : 'border border-line2 text-ink2'
            }`}
          >
            {p}
          </button>
        ))}
      </div>
      {/**
       * **Die Begründung ist Pflicht**, und der Platzhalter sagt auch, welche Sorte gemeint
       * ist. „Ist wichtig" beantwortet nichts – wer entscheidet, braucht das, was er selbst
       * nicht weiß: den Termin, den Ausfall, die Zahl der Betroffenen.
       */}
      <textarea
        value={grund}
        onChange={(e) => setGrund(e.target.value)}
        rows={2}
        placeholder="Warum? Was hängt daran – ein Termin, ein Ausfall, wie viele Leute?"
        className="mt-1.5 w-full resize-y rounded-lg border border-line2 px-2 py-1.5 text-[12.5px] outline-none focus:border-accent"
      />
      <div className="mt-1.5 flex gap-1.5">
        <Button
          variant="primary"
          disabled={grund.trim().length < 3 || vorschlagen.isPending}
          onClick={() => vorschlagen.mutate()}
        >
          Vorschlagen
        </Button>
        <Button onClick={() => setFormularOffen(false)}>Abbrechen</Button>
      </div>
    </div>
  )
}

/**
 * **Die Arbeitswelt der Aufgabe – und der Weg in eine andere**
 * ([Z-07.17](../../../../docs/phasen/zusatz-07-workspaces.md), 26.08.2026).
 *
 * Nils: *„bzw. pro aufgabe ja eigentlich verteilen können die da reinkommt."*
 *
 * ## Warum das hier steht und nicht in der Feldliste darunter
 *
 * Weil es keine Eigenschaft neben Fälligkeit und Schätzung ist, sondern die Frage davor: *wessen
 * Arbeit ist das überhaupt?* Sie stand hier schon als Text; neu ist, dass man sie beantworten
 * kann, statt sie nur zu lesen.
 *
 * ## Warum die Auswahl erst auf Klick erscheint
 *
 * Ein Umzug ist selten und folgenreich – Pools werden gelöst, das Vorhaben womöglich auch, und
 * für die halbe Belegschaft verschwindet die Zeile aus der Ansicht. Ein Auswahlfeld, das immer
 * offensteht, lädt zum versehentlichen Verstellen ein. Bei einer einzigen eigenen Arbeitswelt
 * erscheint der Knopf gar nicht: es gäbe nichts zu wählen.
 */
function ArbeitsweltZeile({
  taskId,
  workspaceId,
  label,
  icon,
  art,
  darfAendern,
  istAdmin,
  neuLaden,
  beiFehler,
}: {
  taskId: string
  workspaceId: string
  label: string
  icon: string | null
  art: string | null
  darfAendern: boolean
  istAdmin: boolean
  neuLaden: () => void
  beiFehler: (e: Error) => void
}) {
  const queryClient = useQueryClient()
  const { data: welten = [] } = useQuery(workspacesQuery)
  const [offen, setOffen] = useState(false)
  const [umgezogen, setUmgezogen] = useState<{
    nach: string
    poolsGeloest: number
    vorhabenGeloest: boolean
    bearbeiterFremd: boolean
  } | null>(null)

  const umhaengen = useMutation({
    mutationFn: (ziel: string) =>
      trpc.tasks.workspaceSetzen.mutate({ id: taskId, workspaceId: ziel }),
    onSuccess: async (ergebnis) => {
      setOffen(false)
      if (ergebnis.geaendert) {
        setUmgezogen({
          nach: ergebnis.nach,
          poolsGeloest: ergebnis.poolsGeloest,
          vorhabenGeloest: ergebnis.vorhabenGeloest,
          bearbeiterFremd: ergebnis.bearbeiterFremd,
        })
      }
      neuLaden()
      /**
       * **Zwei Listen, nicht eine.** Die Aufgabe verschwindet aus jeder Ansicht der alten Welt
       * und erscheint in der neuen; Pools und Eingang sind beide betroffen. Ohne diese Zeilen
       * stünde sie in der offenen Liste noch da, bis jemand neu lädt.
       */
      await nebenBestandVerwerfen(queryClient)
      await queryClient.invalidateQueries({ queryKey: ['pools'] })
      await queryClient.invalidateQueries({ queryKey: ['eingang'] })
    },
    onError: beiFehler,
  })

  const andere = welten.filter((w) => w.id !== workspaceId)
  const zielName = umgezogen
    ? (welten.find((w) => w.id === umgezogen.nach)?.label ?? umgezogen.nach)
    : null

  return (
    <div className="mb-2">
      <div className="flex flex-wrap items-center gap-1.5 text-[11.5px]">
        <span>{icon || '📁'}</span>
        <span className="font-semibold">{label}</span>
        {art === 'persoenlich' && <span className="text-muted">· nur für dich</span>}
        {darfAendern && andere.length > 0 && !offen && (
          <button
            type="button"
            onClick={() => setOffen(true)}
            className="cursor-pointer rounded px-1 text-muted underline decoration-dotted underline-offset-2 hover:text-ink2"
          >
            umhängen
          </button>
        )}
      </div>

      {/**
       * **Ein Weg zurück** (Nils, 16.10.2026: *„das umhängen vom workspace … konnte man auch
       * nicht mehr abbrechen"*). Die Auswahl stand ohne die Hülle von `InlineWert` da: kein Esc,
       * kein Wegklicken, und „– wohin? –" erneut zu wählen löst kein `change` aus. Jetzt gilt
       * dasselbe wie bei jedem anderen Feld, plus ein sichtbares „abbrechen".
       */}
      {offen && (
        <div
          className="mt-1.5 flex flex-wrap items-center gap-1.5"
          onBlur={(e) => {
            if (!umhaengen.isPending && !e.currentTarget.contains(e.relatedTarget as Node | null))
              setOffen(false)
          }}
          onKeyDown={(e) => {
            if (e.key === 'Escape') {
              e.stopPropagation()
              setOffen(false)
            }
          }}
        >
          <AufklappSelect
            wert=""
            setzen={(v) => {
              if (v) umhaengen.mutate(v)
            }}
            fertig={() => setOffen(false)}
            disabled={umhaengen.isPending}
            ariaLabel="Arbeitswelt wechseln"
          >
            <option value="">– wohin? –</option>
            {andere.map((w) => (
              <option key={w.id} value={w.id}>
                {w.icon} {w.label}
              </option>
            ))}
          </AufklappSelect>
          <button
            type="button"
            disabled={umhaengen.isPending}
            onClick={() => setOffen(false)}
            className="cursor-pointer text-[11.5px] text-muted hover:underline disabled:cursor-default"
          >
            abbrechen
          </button>
        </div>
      )}

      {umgezogen && (
        <div className="mt-1.5 rounded-md border border-line bg-[#fafaf8] px-2 py-1.5 text-[11px]">
          <div className="flex items-baseline gap-1.5">
            <span className="flex-1">
              Liegt jetzt in <strong>{zielName}</strong>
              {umgezogen.poolsGeloest > 0 &&
                ` · ${umgezogen.poolsGeloest} Pool${umgezogen.poolsGeloest === 1 ? '' : 's'} gelöst`}
              {umgezogen.vorhabenGeloest && ' · Vorhaben gelöst'}
            </span>
            <button
              type="button"
              onClick={() => setUmgezogen(null)}
              aria-label="Hinweis schließen"
              className="cursor-pointer px-1 text-muted hover:text-ink2"
            >
              ×
            </button>
          </div>
          {/**
           * **Der Bearbeiter sieht seine Aufgabe dort nicht mehr** – der einzige Teil des Umzugs,
           * der jemand anderem etwas wegnimmt. Deshalb steht er in Warnfarbe und nicht als
           * Nebensatz: wer ihn übersieht, hinterlässt eine Aufgabe, an der jemand hängt, ohne sie
           * je wiederzufinden.
           */}
          {umgezogen.bearbeiterFremd && (
            <div className="mt-1 font-semibold text-warn">
              Der Bearbeiter ist in dieser Arbeitswelt kein Mitglied – für ihn ist die Aufgabe damit
              weg. Bitte neu zuweisen oder zuteilen lassen.
            </div>
          )}
          {istAdmin && <RegelAngebot taskId={taskId} zielWorkspaceId={umgezogen.nach} />}
        </div>
      )}
    </div>
  )
}

/**
 * **Der Einstieg zum Zusammenführen** ([Z-11](../../../../docs/phasen/zusatz-11-zusammenfuehren.md)).
 *
 * Eine Zeile, kein Knopf: die Frage *„gibt es die schon einmal?"* stellt sich selten, und ein
 * Knopf in der Kopfzeile stünde 99 von 100 Malen im Weg. Sie steht trotzdem sichtbar da und
 * nicht in einem Menü – wer eine Dublette gefunden hat, soll nicht erst suchen müssen, wo man
 * sie loswird.
 *
 * **Nach dem Verschmelzen wird die überlebende Aufgabe geöffnet.** Meistens ist das dieselbe und
 * es passiert nichts Sichtbares; hat man aber „andersherum" gewählt, ist die gerade offene
 * gelöscht – dann stünde hier ein leeres Detail, und das sähe aus wie ein Fehler statt wie ein
 * Erfolg.
 */
function DublettenZeile({
  taskId,
  titel,
  neuLaden,
}: {
  taskId: string
  titel: string
  neuLaden: () => void
}) {
  const [offen, setOffen] = useState(false)
  const oeffnen = useAufgabeOeffnen()

  return (
    <>
      <div className="mt-4 flex items-baseline gap-2">
        <h4 className="text-xs font-bold">Doppelt?</h4>
        <button
          type="button"
          onClick={() => setOffen(true)}
          className="cursor-pointer text-[11.5px] text-muted underline decoration-dotted underline-offset-2 hover:text-ink2"
        >
          mit anderer Aufgabe zusammenführen
        </button>
      </div>

      {offen && (
        <AufgabeZusammenfuehren
          aufgabeId={taskId}
          titel={titel}
          schliessen={() => setOffen(false)}
          fertig={(behaltenId) => {
            setOffen(false)
            neuLaden()
            if (behaltenId !== taskId) oeffnen(behaltenId)
          }}
        />
      )}
    </>
  )
}

/**
 * **„Soll das künftig automatisch dorthin?"** ([E-253](../../../../docs/ENTSCHEIDUNGEN.md#e-253)).
 *
 * Der vierte Teil des Verteil-Pakets: aus einer Handbewegung wird eine Regel, statt dass jemand
 * dreimal dasselbe sortiert und danach vor einer leeren Maske sitzt. Angeboten werden nur
 * **Kandidaten** – Absenderdomain, Absender, Bereich –, und welcher der richtige ist, entscheidet
 * der Mensch: „alles von dieser Firma" und „alles von dieser Person" sind sehr verschiedene
 * Aussagen.
 *
 * Erscheint nur für Admins und nur, wenn sich aus der Aufgabe überhaupt etwas Verallgemeinerbares
 * ableiten lässt. Eine leere Anlegen-Maske wäre kein Angebot, sondern eine Aufgabe.
 */
function RegelAngebot({ taskId, zielWorkspaceId }: { taskId: string; zielWorkspaceId: string }) {
  const queryClient = useQueryClient()
  const { data } = useQuery(verteilEntwuerfeQuery(taskId))
  const { data: welten = [] } = useQuery(workspacesQuery)
  const [angelegt, setAngelegt] = useState<string | null>(null)

  const anlegen = useMutation({
    mutationFn: (e: { feld: string; operator: string; wert: string; beschreibung: string }) =>
      trpc.verteilung.anlegen.mutate({
        name: `${e.beschreibung} → ${welten.find((w) => w.id === zielWorkspaceId)?.label ?? zielWorkspaceId}`.slice(
          0,
          120,
        ),
        feld: e.feld as 'absender',
        operator: e.operator as 'ist_gleich',
        wert: e.wert,
        /**
         * **Auf die Quelle beschränkt.** Eine Absenderregel ist eine Aussage über Mail; ohne
         * diese Zeile prüfte sie auch jedes GitLab-Issue – und der Bereich `netcon/web` sähe
         * einer Adresse nicht ähnlich genug, um aufzufallen.
         */
        sourceId: data?.sourceId ?? null,
        zielWorkspaceId,
      }),
    onSuccess: async (_, e) => {
      setAngelegt(e.beschreibung)
      await queryClient.invalidateQueries({ queryKey: ['verteilung'] })
    },
  })

  const entwuerfe = data?.entwuerfe ?? []
  if (entwuerfe.length === 0) return null

  if (angelegt) {
    return (
      <div className="mt-1.5 border-t border-line pt-1.5 text-muted">
        Regel angelegt: <strong>{angelegt}</strong> · sie greift ab dem nächsten Eingang.
      </div>
    )
  }

  return (
    <div className="mt-1.5 border-t border-line pt-1.5">
      <div className="text-muted">Künftig automatisch dorthin?</div>
      <div className="mt-1 flex flex-wrap gap-1">
        {entwuerfe.map((e) => (
          <button
            key={`${e.feld}-${e.wert}`}
            type="button"
            disabled={anlegen.isPending}
            onClick={() => anlegen.mutate(e)}
            className="cursor-pointer rounded-full border border-line px-2 py-0.5 hover:bg-[#f3f2ef] disabled:opacity-50"
          >
            {e.beschreibung}
          </button>
        ))}
      </div>
    </div>
  )
}

function VorhabenZeile({
  taskId,
  workspaceId,
  vorhabenId,
  vorhabenLabel,
  darfAendern,
  neuLaden,
}: {
  taskId: string
  workspaceId: string
  vorhabenId: string | null
  vorhabenLabel: string | null
  darfAendern: boolean
  neuLaden: () => void
}) {
  /** Die Vorhaben **der Aufgabe**, nicht die des Umschalters – siehe `poolsDerWeltQuery`. */
  const { data: vorhaben = [] } = useQuery(vorhabenDerWeltQuery(workspaceId))
  const zuordnen = useMutation({
    mutationFn: (ziel: string | null) =>
      trpc.vorhaben.aufgabenZuordnen.mutate({ taskIds: [taskId], vorhabenId: ziel }),
    onSuccess: neuLaden,
  })

  return (
    <div className="flex gap-2 border-b border-line py-1 last:border-b-0">
      <dt className="w-[112px] shrink-0 text-muted max-sm:w-[88px]">Vorhaben</dt>
      <dd className="min-w-0 flex-1">
        {vorhaben.length === 0 ? (
          <span className="text-muted">noch keine angelegt</span>
        ) : (
          <InlineWert
            wert={vorhabenLabel}
            leer="keinem"
            darfAendern={darfAendern}
            bezeichnung="Vorhaben"
          >
            {(fertig) => (
              <AufklappSelect
                wert={vorhabenId ?? ''}
                setzen={(v) => zuordnen.mutate(v || null)}
                fertig={fertig}
                disabled={zuordnen.isPending}
                ariaLabel="Vorhaben zuordnen"
              >
                <option value="">– keinem –</option>
                {vorhaben.map((v) => (
                  <option key={v.id} value={v.id}>
                    {v.label}
                  </option>
                ))}
              </AufklappSelect>
            )}
          </InlineWert>
        )}
      </dd>
    </div>
  )
}

/**
 * **Der Meilenstein, den die Aufgabe bedient** – setzbar und lösbar, nicht nur beim Anlegen
 * (09.09.2026, [E-335](../../../../docs/ENTSCHEIDUNGEN.md#e-335)).
 *
 * Zur Auswahl stehen nur die Meilensteine **dieses** Vorhabens – ein fremder wäre der stille
 * Fall, den der Server ohnehin abweist. Erreichte stehen mit dabei, gekennzeichnet: wer eine
 * Aufgabe nachträglich einem erreichten Meilenstein zuordnet, dokumentiert, nicht plant.
 *
 * Der Bezug wirkt auf den Termin: hat die Aufgabe keine eigene Fälligkeit, gilt das Datum
 * **ihres** Meilensteins (`wirksamerTermin`). Bis E-335 zog der nächste Meilenstein des
 * Vorhabens **alle** Aufgaben darin an sein Datum – ob sie ihn bedienten oder nicht.
 */
function MeilensteinZeile({
  taskId,
  vorhabenId,
  meilensteinId,
  meilensteinLabel,
  darfAendern,
  neuLaden,
  beiFehler,
}: {
  taskId: string
  vorhabenId: string
  meilensteinId: string | null
  meilensteinLabel: string | null
  darfAendern: boolean
  neuLaden: () => void
  beiFehler: (e: Error) => void
}) {
  const { data: meilensteine = [] } = useQuery(meilensteineQuery)
  const desVorhabens = meilensteine.filter((m) => m.vorhabenId === vorhabenId)
  const setzen = useMutation({
    mutationFn: (ziel: string | null) =>
      trpc.tasks.bearbeiten.mutate({ id: taskId, meilensteinId: ziel }),
    onSuccess: neuLaden,
    onError: beiFehler,
  })

  const termin = (t: string | null) =>
    t ? ` · ${new Date(`${t.slice(0, 10)}T12:00:00`).toLocaleDateString('de-DE')}` : ''

  return (
    <div className="flex gap-2 border-b border-line py-1 last:border-b-0">
      <dt className="w-[112px] shrink-0 text-muted max-sm:w-[88px]">Meilenstein</dt>
      <dd className="min-w-0 flex-1">
        {desVorhabens.length === 0 && !meilensteinId ? (
          <span className="text-muted">das Vorhaben hat noch keine</span>
        ) : (
          <InlineWert
            wert={meilensteinLabel}
            leer="keinen"
            darfAendern={darfAendern}
            bezeichnung="Meilenstein"
          >
            {(fertig) => (
              <AufklappSelect
                wert={meilensteinId ?? ''}
                setzen={(v) => setzen.mutate(v || null)}
                fertig={fertig}
                disabled={setzen.isPending}
                ariaLabel="Meilenstein zuordnen"
              >
                <option value="">– keinen –</option>
                {desVorhabens.map((m) => (
                  <option key={m.id} value={m.id}>
                    {m.label}
                    {termin(m.termin)}
                    {m.erreichtAm ? ' · erreicht' : ''}
                  </option>
                ))}
              </AufklappSelect>
            )}
          </InlineWert>
        )}
      </dd>
    </div>
  )
}

/**
 * **Pools an der Aufgabe – Mehrfachauswahl, deshalb Marken statt Auswahlfeld** (19.08.2026).
 *
 * Eine Aufgabe darf in mehreren Pools liegen ([E-23](../../../../docs/ENTSCHEIDUNGEN.md)), und
 * eine Mehrfachauswahl in einem `select` ist die unbedienbarste Form dieser Frage. Aufgeklappt
 * stehen dieselben Knöpfe wie im Dialog `PoolsSetzen`; jeder Klick schreibt sofort, weil es
 * nichts zu bestätigen gibt – „in einem Pool" ist keine Angabe, die man erst zu Ende denken
 * muss.
 */
function PoolsZeile({
  taskId,
  workspaceId,
  poolIds,
  darfAendern,
  neuLaden,
  beiFehler,
}: {
  taskId: string
  /** Die Arbeitswelt **der Aufgabe** – sie entscheidet, welche Pools zur Wahl stehen. */
  workspaceId: string
  poolIds: string[]
  darfAendern: boolean
  neuLaden: () => void
  beiFehler: (e: Error) => void
}) {
  /**
   * **Regale stehen hier zur Wahl, führen aber einen anderen Weg** (20.08.2026,
   * [E-191](../../../../docs/ENTSCHEIDUNGEN.md)).
   *
   * Ein Klick auf „Parkplatz" lief bis hierher in die Meldung *„«Parkplatz» wird nicht über
   * die Pool-Auswahl befüllt – dafür gibt es «Parken»"* – dieselbe Falle, die die Statusleiste
   * beim Fachbereich vermeidet: ein Knopf, der aussieht wie ein Angebot und vom Server
   * abgewiesen wird.
   *
   * Aufgelöst wird sie **nicht**, indem das Regal verschwindet, sondern indem der Klick tut,
   * was er verspricht: er ruft `eingang.parken` statt `tasks.poolsSetzen` und setzt damit
   * Regal *und* `zurueckgestellt`. Nils hat genau hier danach gesucht; die Auswahl war der
   * richtige Ort, nur die falsche Prozedur.
   *
   * **Und es ist der Ort, an dem ein Fehlklick nicht passiert:** die Zeile klappt erst auf
   * Klick auf. Ein Knopf oben in der Statusleiste wäre einen Daumenbreit von „Erledigt"
   * entfernt gewesen.
   */
  const { data: allePools = [] } = useQuery(poolsDerWeltQuery(workspaceId))
  const pools = nurVorraete(allePools)
  const regale = allePools.filter((p) => !p.ziehbar)
  /** Optimistisch wie die Planungsfelder – die Marke färbt sich beim Klick, nicht danach. */
  const poolsSetzen = usePoolsSetzen(taskId)
  const setzen = {
    isPending: poolsSetzen.isPending,
    mutate: (ids: string[]) => poolsSetzen.mutate(ids, { onError: beiFehler }),
  }
  const parken = useMutation({
    mutationFn: () => trpc.eingang.parken.mutate({ taskIds: [taskId] }),
    onSuccess: neuLaden,
    onError: beiFehler,
  })
  const zurueckholen = useMutation({
    mutationFn: () => trpc.eingang.zurueckInDenEingang.mutate({ taskIds: [taskId] }),
    onSuccess: neuLaden,
    onError: beiFehler,
  })

  /**
   * **Angezeigt wird aus `allePools`, gewählt aus `pools`.** Sonst stünde an einer geparkten
   * Aufgabe „in keinem" – der Parkplatz fehlt ja in der Auswahl –, und das wäre die eine
   * Auskunft, die hier falsch wäre.
   */
  const namen = poolIds
    .map((id) => allePools.find((p) => p.id === id))
    .filter((p) => p !== undefined)
    .map((p) => `${p.icon} ${p.label}`)

  /** Liegt sie im Regal, ist die Frage dieser Zeile die falsche – wie in `PoolsSetzen`. */
  const geparkt = allePools.some((p) => !p.ziehbar && poolIds.includes(p.id))

  return (
    <div className="flex gap-2 border-b border-line py-1 last:border-b-0">
      <dt className="w-[112px] shrink-0 text-muted max-sm:w-[88px]">Pools</dt>
      <dd className="min-w-0 flex-1">
        <InlineWert
          wert={namen.length > 0 ? namen.join(', ') : null}
          leer="in keinem"
          darfAendern={darfAendern}
          bezeichnung="Pools"
        >
          {() => (
            <div className="flex flex-wrap items-center gap-1">
              {pools.map((pool) => {
                const drin = poolIds.includes(pool.id)
                return (
                  <button
                    key={pool.id}
                    type="button"
                    disabled={setzen.isPending || geparkt}
                    aria-pressed={drin}
                    /**
                     * **Geparkt schließt die Vorräte aus.** Sonst hinge an derselben Aufgabe
                     * „ist geparkt" und „liegt im Ticket-Vorrat", und die großzügigere Regel
                     * gewinnt (`poolsErlaubenZiehen`) – sie wäre geparkt und trotzdem ziehbar.
                     */
                    title={geparkt ? 'Erst zurückholen, dann einordnen' : undefined}
                    onClick={() =>
                      setzen.mutate(
                        drin ? poolIds.filter((id) => id !== pool.id) : [...poolIds, pool.id],
                      )
                    }
                    className={`rounded-md px-1.5 py-px text-[11.5px] font-semibold ${
                      drin
                        ? 'bg-ink text-white'
                        : 'border border-line2 text-ink2 hover:bg-[#f3f2ef]'
                    } ${geparkt ? 'cursor-default opacity-40' : 'cursor-pointer'}`}
                  >
                    {pool.icon} {pool.label}
                  </button>
                )
              })}
              {pools.length === 0 && <span className="text-muted">keine Pools angelegt</span>}

              {/**
               * **Das Regal steht abgesetzt hinter einem Strich**, nicht in der Reihe der
               * Vorräte. Es beantwortet dieselbe Frage – *wo liegt diese Aufgabe* –, aber mit
               * einer anderen Folge: sie ist danach zurückgestellt und in keiner Auswertung
               * mehr. Ein Knopf, der optisch zu den anderen gehört und mehr tut als sie, wäre
               * genau die Art Überraschung, die man einmal erlebt und danach nicht mehr
               * traut.
               */}
              {/**
               * **Eine ganze Zeile, kein Strich daneben** (Nils, 21.08.2026: *„Kannst du eine
               * Zeile sogar zwischen «Rauschen» und Parkplatz machen, dass das noch besser
               * getrennt ist?"*).
               *
               * Der senkrechte Strich war für eine Reihe gedacht. In der schmalen Spalte
               * rechts brechen die Marken aber untereinander um, und dann stand er als
               * einzelnes Zeichen neben „Rauschen" – er sah nach Versehen aus statt nach
               * Grenze. `w-full` erzwingt in einem `flex-wrap` den Umbruch: der Parkplatz
               * beginnt immer eine neue Zeile, unabhängig davon, wie breit es gerade ist.
               */}
              {regale.length > 0 && (
                <span aria-hidden className="my-1 h-px w-full shrink-0 bg-line2" />
              )}
              {regale.map((regal) => {
                const drin = poolIds.includes(regal.id)
                return (
                  <button
                    key={regal.id}
                    type="button"
                    disabled={parken.isPending || zurueckholen.isPending}
                    aria-pressed={drin}
                    onClick={() => (drin ? zurueckholen.mutate() : parken.mutate())}
                    title={
                      drin
                        ? `Holt die Aufgabe vom ${regal.label} zurück in den Eingang`
                        : `Legt die Aufgabe auf den ${regal.label} und stellt sie zurück – sichtbar, aber nicht ziehbar und in keiner Auswertung`
                    }
                    className={`cursor-pointer rounded-md border px-1.5 py-px text-[11.5px] font-semibold ${
                      drin
                        ? 'border-ink bg-ink text-white'
                        : 'border-dashed border-line2 text-muted hover:bg-[#f3f2ef] hover:text-ink2'
                    }`}
                  >
                    {regal.icon} {drin ? `${regal.label} – zurückholen` : regal.label}
                  </button>
                )
              })}
            </div>
          )}
        </InlineWert>
      </dd>
    </div>
  )
}

/**
 * **Ein Termin, den Work selbst führt** (19.08.2026).
 *
 * `type="date"` statt eines Textfeldes: der Kalender des Browsers kennt die deutsche
 * Schreibweise, Feiertage und die Tastatur – und was er zurückgibt, ist immer ein gültiges
 * Datum. Gespeichert wird bei der Auswahl, nicht auf einen Knopf: ein Datumsfeld hat keinen
 * Zwischenzustand, den man noch überlegen müsste.
 *
 * **Mittags, nicht um Mitternacht.** `new Date('2026-08-20')` ist UTC-Mitternacht und wird in
 * unserer Zeitzone im Sommer zum 20.08. 02:00 – aber im Winter, bei einer Zeitzone westlich
 * von Greenwich oder bei einem Server in UTC-1 zum **19.08.** Zwölf Uhr mittags hält den Tag
 * in jeder Zeitzone fest.
 */
/**
 * **Priorität setzen – A, B, C oder keine** ([E-265](../../../../docs/ENTSCHEIDUNGEN.md#e-265)).
 *
 * Nils, 28.08.2026: *„Priorität ist immer optional. Wenn aber eine Priorität da ist, wird die
 * gewertet. Die ohne Priorität sind quasi die unpriorisiertesten."*
 *
 * ## Warum „keine" eine echte Wahl ist und nicht der Leerzustand
 *
 * Weil man sie **zurücknehmen** können muss. Eine Priorität, die man setzen, aber nicht wieder
 * entfernen kann, ist eine Falle: wer aus Versehen „A" wählt, hätte die Aufgabe dauerhaft vor
 * allen anderen. Die Sortierung stuft „ohne" hinter C ein (`poolVergleicher`), also ist das
 * Zurücknehmen kein Verlust von Information, sondern die Aussage *„nicht eingestuft"*.
 *
 * ## Warum Knöpfe und keine Auswahlliste
 *
 * Drei Werte plus „keine". Eine Auswahlliste braucht zwei Klicks und zeigt den aktuellen Wert
 * kleiner, als er ist – die Priorität ist die Angabe, wegen der jemand diese Zeile ansieht.
 */
function PrioZeile({
  taskId,
  priority,
  quellPrioritaet,
  quelle,
  darfAendern,
  neuLaden,
  beiFehler,
}: {
  taskId: string
  /** Die **wirksame** Priorität – Work, wenn Work etwas gesagt hat, sonst die Quelle. */
  priority: TaskPriority | null
  /** Was die Quelle sagt. Nur interessant, wenn es von `priority` abweicht. */
  quellPrioritaet: TaskPriority | null
  quelle: string
  darfAendern: boolean
  neuLaden: () => void
  beiFehler: (e: Error) => void
}) {
  const setzen = useMutation({
    mutationFn: (wert: TaskPriority | null) =>
      trpc.tasks.bearbeiten.mutate({ id: taskId, priority: wert }),
    onSuccess: neuLaden,
    onError: beiFehler,
  })

  return (
    <div className="flex gap-2 border-b border-line py-1 last:border-b-0">
      <dt className="w-[112px] shrink-0 text-muted max-sm:w-[88px]">Priorität</dt>
      <dd className="min-w-0 flex-1">
        {darfAendern ? (
          <div className="flex flex-wrap gap-1">
            {(['A', 'B', 'C'] as const).map((p) => (
              <button
                key={p}
                type="button"
                disabled={setzen.isPending}
                onClick={() => setzen.mutate(priority === p ? null : p)}
                aria-pressed={priority === p}
                title={
                  priority === p ? 'Noch einmal klicken nimmt die Priorität zurück' : undefined
                }
                className={cn(
                  'min-h-[28px] min-w-[34px] rounded-md px-2 text-[12px] font-semibold',
                  priority === p
                    ? 'bg-accent-soft text-accent-deep'
                    : 'text-ink2 hover:bg-[#f3f2ef]',
                )}
              >
                {p}
              </button>
            ))}
            {/**
             * **„ohne" steht als eigener Knopf da**, obwohl ein zweiter Klick auf die aktive
             * Stufe dasselbe tut. Der zweite Klick ist eine Kenntnis, kein Angebot – und wer
             * die Einstufung zurücknehmen will, sucht ein Wort, keine Geste.
             */}
            <button
              type="button"
              disabled={setzen.isPending || priority === null}
              onClick={() => setzen.mutate(null)}
              aria-pressed={priority === null}
              className={cn(
                'min-h-[28px] rounded-md px-2 text-[11.5px]',
                priority === null
                  ? 'bg-[#f2f1ed] font-semibold text-ink2'
                  : 'text-muted hover:bg-[#f3f2ef]',
              )}
            >
              ohne
            </button>
          </div>
        ) : priority ? (
          <PrioBadge priority={priority} />
        ) : (
          <span className="text-muted">ohne</span>
        )}
        {/**
         * **Der Hinweis erscheint nur bei einer Abweichung** – und dann als Feststellung, nicht
         * als Warnung. Work überlagert die Quelle absichtlich; wer die Zeile trotzdem liest,
         * fragt sich, warum in GitLab etwas anderes steht als hier. Die Antwort ist genau
         * dieser Satz.
         */}
        {quellPrioritaet !== null && quellPrioritaet !== priority && (
          <p className="mt-0.5 text-[11px] text-muted">
            {quelle} sagt {quellPrioritaet} – hier gilt die von Work.
          </p>
        )}
      </dd>
    </div>
  )
}

/**
 * **Die Adresse dieser Aufgabe – zum Kopieren und zum Öffnen im Tab**
 * ([E-311](../../../../docs/ENTSCHEIDUNGEN.md#e-311), 02.09.2026).
 *
 * Aus Ticket NCW-6X848M: *„Kann Aufgaben nicht teilen, also per Link"* und *„Aufgabendetails in
 * Tab öffnen können"*.
 *
 * Beides gab es technisch längst: `…?aufgabe=<Kennung>` öffnet seit dem 18.08.2026 jede Aufgabe,
 * die Erwähnungsmails benutzen genau diese Adresse. Sie stand nur **in keiner Adresszeile** –
 * also konnte man sie weder kopieren noch weitergeben.
 *
 * Seit `AufgabeKontext` die Adresse mitschreibt, steht sie oben im Browser. Diese zwei Knöpfe
 * sind der kurze Weg dorthin: einer legt sie in die Zwischenablage, einer macht einen Tab auf.
 *
 * ## Warum die Rückmeldung am Knopf steht und nicht in einer Meldung
 *
 * Weil „kopiert" eine Bestätigung von einer Sekunde ist. Ein Hinweisstreifen dafür wäre etwas,
 * das man wegklicken muss – für eine Auskunft, die man schon hat, sobald man sie liest.
 *
 * `navigator.clipboard` gibt es nur über HTTPS (und auf `localhost`). Fehlt es, bleibt der
 * Knopf trotzdem sinnvoll: die Adresse wird markiert angeboten, statt dass nichts passiert.
 */
/**
 * **Ein Kommentar – und der Weg, ihn nachzubessern**
 * ([E-314](../../../../docs/ENTSCHEIDUNGEN.md#e-314), 02.09.2026).
 *
 * Aus Ticket NCW-6X848M: *„Kommentare im Nachhinein noch bearbeiten können."*
 *
 * ## Warum eine eigene Komponente
 *
 * Weil die Zeile jetzt einen Zustand hat. Sie stand als sieben Zeilen JSX mitten in der Liste;
 * ein `useState` je Eintrag geht dort nicht, und ein gemeinsamer Zustand „welcher wird gerade
 * bearbeitet" wäre die Sorte Merker, die beim zweiten Feature auseinanderfällt.
 *
 * ## Wer den Knopf sieht
 *
 * Nur der Verfasser, und nur bei einem Kommentar, der sich ändern lässt (`aenderbar` – das
 * entscheidet der **Server**, weil dort auch die Sperre sitzt). Ein Kommentar aus einer Quelle
 * gehört einem Menschen dort; einer, der schon in einem NetCon-Ticket steht, ließe sich drüben
 * nicht nachziehen.
 *
 * ## „bearbeitet" steht dran
 *
 * Ohne den Vermerk wäre eine stille Änderung möglich – in einer Unterhaltung, auf die sich
 * jemand berufen könnte. Die Sekunde Toleraz gegen `createdAt` stammt vom Server: beim Anlegen
 * stehen beide Stempel auf derselben Zeit, aber nicht immer auf derselben Millisekunde.
 */
/**
 * **Den Titel ändern – aber nur, wo Work ihn führt**
 * ([E-331](../../../../docs/ENTSCHEIDUNGEN.md#e-331), 04.09.2026).
 *
 * Nils: *„Und ich möchte gerne die «Titel» von Aufgaben bearbeiten können (zumindest die in
 * Work erstellt wurden)."* – Der Halbsatz in der Klammer ist genau die Grenze, die es schon
 * gibt: `title` steht in `QUELLGEFUEHRTE_FELDER`, und der Server nimmt eine Änderung an einem
 * importierten Ticket nicht an. Er täte es zu Recht: der nächste Abgleich überschriebe sie
 * binnen Minuten, und der Fehler verschwände, bevor ihn jemand versteht.
 *
 * **Gefehlt hat nur die Oberfläche.** `tasks.bearbeiten` konnte `title` seit jeher; im Detail
 * stand er als reine Überschrift. Angeboten wird das Ändern deshalb genau dort, wo der Server
 * es ohnehin erlaubt – `quellfelderAenderbar` ist dieselbe Spalte, gegen die er prüft. Einen
 * Stift zu zeigen, der beim Klick eine Absage bringt, wäre schlechter als keiner.
 *
 * ## Ein Klick auf die Überschrift, kein zweiter Modus
 *
 * Kein Stift daneben: die Überschrift **ist** der Knopf (`title`-Hinweis am Element). Ein Stift
 * in einer Kopfzeile, die schon Status, Marken und Schließen trägt, wäre das fünfte anklickbare
 * Ding in einer Zeile – und das erste, das man sucht, wenn man es braucht, ist der Text selbst.
 *
 * Escape verwirft, Enter speichert, der Fokusverlust ebenfalls: ein Feld, das man nur mit der
 * Maus wieder los wird, hält den Bildschirm fest.
 */
function TitelZeile({
  taskId,
  titel,
  neuLaden,
  beiFehler,
}: {
  taskId: string
  titel: string
  neuLaden: () => void
  beiFehler: (e: Error) => void
}) {
  const [offen, setOffen] = useState(false)
  const [entwurf, setEntwurf] = useState(titel)

  const speichern = useMutation({
    mutationFn: (neu: string) => trpc.tasks.bearbeiten.mutate({ id: taskId, title: neu }),
    onSuccess: () => {
      setOffen(false)
      neuLaden()
    },
    onError: beiFehler,
  })

  /**
   * **Ungeändert heißt: nichts schicken.** Der Server nähme es an und täte nichts – aber der
   * Weg dorthin ist eine Anfrage, ein Ereignis-Diff und ein Neuladen für ein Escape, das
   * jemand als Enter getippt hat.
   */
  function fertig() {
    const neu = entwurf.trim()
    if (neu.length < 3 || neu === titel) {
      setEntwurf(titel)
      setOffen(false)
      return
    }
    speichern.mutate(neu)
  }

  if (!offen) {
    return (
      <h3
        className="-mx-1 cursor-text rounded px-1 text-sm font-bold break-words hover:bg-[#f3f2ef] max-sm:text-[17px] max-sm:leading-snug"
        title="Zum Ändern anklicken"
        onClick={() => {
          setEntwurf(titel)
          setOffen(true)
        }}
      >
        {titel}
      </h3>
    )
  }

  return (
    <input
      autoFocus
      value={entwurf}
      disabled={speichern.isPending}
      onChange={(e) => setEntwurf(e.target.value)}
      onBlur={fertig}
      onKeyDown={(e) => {
        if (e.key === 'Enter') {
          e.preventDefault()
          fertig()
        }
        if (e.key === 'Escape') {
          setEntwurf(titel)
          setOffen(false)
        }
      }}
      aria-label="Titel der Aufgabe"
      className="-mx-1 w-full rounded border border-accent px-1 text-sm font-bold max-sm:text-[17px]"
    />
  )
}

/**
 * **Zum Ziel springen und es kurz aufleuchten lassen** – der Weg zwischen Kommentar und
 * Unteraufgabe in beide Richtungen (E-340).
 */
function springeZu(elementId: string): void {
  const el = document.getElementById(elementId)
  if (!el) return
  el.scrollIntoView({ block: 'center', behavior: 'smooth' })
  el.classList.add('ring-2', 'ring-accent', 'rounded-md')
  window.setTimeout(() => el.classList.remove('ring-2', 'ring-accent', 'rounded-md'), 1600)
}

/**
 * **Eine Unteraufgabe mit allem, was an ihr hängt** (09.09.2026,
 * [E-340](../../../../docs/ENTSCHEIDUNGEN.md#e-340)).
 *
 * Nils: *„Kann man die Kommentare an die Unteraufgaben linken? Dass ersichtlich ist, dass die
 * dazugehören? Auch dass man neue Kommentare an Unteraufgaben machen kann sowie natürlich die
 * Unteraufgaben bearbeiten."* Drei Dinge, eine Zeile:
 *
 * 1. **Bearbeiten** – Titel und Beschreibung, aufgeklappt statt in einem Dialog.
 * 2. **Woher sie kommt** – „aus Kommentar von …" springt zum Kommentar; dort steht die Marke
 *    zurück hierher.
 * 3. **Ihr eigener Strang** – Kommentare an der Unteraufgabe stehen unter ihr, zugeklappt mit
 *    Zähler, und gehen **nicht** in GitLab oder NetCon: dort gibt es die Unteraufgabe nicht.
 */
function UnteraufgabeZeile({
  u,
  taskId,
  gast,
  kommentare,
  ausKommentar,
  umschalten,
  loeschen,
  personen,
  neuLaden,
  beiFehler,
}: {
  u: {
    id: string
    title: string
    beschreibung: string | null
    ausKommentarId: string | null
    isDone: boolean
    doneAt: Date | string | null
    doneByName: string | null
    markiertPersonId: string | null
    markiertName: string | null
  }
  taskId: string
  gast: boolean
  /** Wen man markieren kann – die Personen der Welt dieser Aufgabe (E-345). */
  personen: { id: string; displayName: string; isActive: boolean }[]
  kommentare: {
    id: string
    body: string
    createdAt: Date | string
    authorName: string | null
  }[]
  ausKommentar: { id: string; authorName: string | null; createdAt: Date | string } | null
  umschalten: (isDone: boolean) => void
  loeschen: () => void
  neuLaden: () => void
  beiFehler: (e: Error) => void
}) {
  /**
   * **Eine Zeile, bis jemand hinsieht** (10.09.2026, [E-341](../../../../docs/ENTSCHEIDUNGEN.md#e-341)).
   * Nils, vor NCW-6X848M mit 14 Unteraufgaben: *„das sieht komisch und voll aus oder?"* –
   * drei Zeilen je Unteraufgabe waren eine Wand. Zugeklappt: Häkchen, Titel, Zähler. Alles
   * andere – Beschreibung, Herkunft, Strang, Bearbeiten, Löschen – erst nach dem Aufklappen.
   */
  const [offen, setOffen] = useState(false)
  const [bearbeiten, setBearbeiten] = useState(false)
  const [titel, setTitel] = useState(u.title)
  const [beschreibung, setBeschreibung] = useState(u.beschreibung ?? '')
  const [neuer, setNeuer] = useState('')
  const erwaehnbar = useErwaehnbarePersonen()

  const speichern = useMutation({
    mutationFn: () =>
      trpc.tasks.unteraufgabeBearbeiten.mutate({
        id: u.id,
        title: titel.trim(),
        beschreibung: beschreibung.trim() ? beschreibung : null,
      }),
    onSuccess: () => {
      setBearbeiten(false)
      neuLaden()
    },
    onError: beiFehler,
  })
  /**
   * **Markieren, kein Bearbeiter** (14.09.2026, [E-345](../../../../docs/ENTSCHEIDUNGEN.md#e-345)).
   * Esther: *„z.B Simon zu markieren, weil er dafür zuständig ist"* – Nils: *„markieren
   * reicht"*. Die Person steht in der Zeile und bekommt eine Erwähnung im Strang; der Server
   * schreibt sie. Die Auswahl ist ein Auswahlfeld, das nur auf Klick erscheint – wie die
   * anderen Felder im Detail: geschlossen ist der Normalzustand.
   */
  const [markiertWahl, setMarkiertWahl] = useState(false)
  const markieren = useMutation({
    mutationFn: (personId: string | null) =>
      trpc.tasks.unteraufgabeMarkieren.mutate({ id: u.id, personId }),
    onSuccess: () => {
      setMarkiertWahl(false)
      neuLaden()
    },
    onError: beiFehler,
  })
  const kommentieren = useMutation({
    mutationFn: () =>
      trpc.tasks.kommentarSchreiben.mutate({ taskId, body: neuer.trim(), subtaskId: u.id }),
    onSuccess: () => {
      setNeuer('')
      neuLaden()
    },
    onError: beiFehler,
  })

  const datum = (d: Date | string) =>
    new Date(d).toLocaleString('de-DE', {
      day: '2-digit',
      month: '2-digit',
      hour: '2-digit',
      minute: '2-digit',
    })
  const hatMehr = Boolean(u.beschreibung || ausKommentar || kommentare.length > 0)

  return (
    <div id={`unteraufgabe-${u.id}`} className="border-b border-line text-[12.5px] last:border-b-0">
      <div className="flex items-center gap-2 py-1">
        <input
          type="checkbox"
          checked={u.isDone}
          disabled={gast}
          onChange={(e) => umschalten(e.target.checked)}
          className={gast ? '' : 'cursor-pointer'}
          aria-label={u.title}
        />
        {bearbeiten ? (
          <input
            value={titel}
            onChange={(e) => setTitel(e.target.value)}
            aria-label="Titel der Unteraufgabe"
            autoFocus
            className="min-w-0 flex-1 rounded-[9px] border border-line2 bg-surface px-[9px] py-1 text-[12.5px]"
          />
        ) : (
          /* Der Titel klappt auf – ein Knopf, der wie Text aussieht, damit die Zeile Zeile bleibt. */
          <button
            type="button"
            onClick={() => setOffen((v) => !v)}
            aria-expanded={offen}
            title={u.title}
            className={cn(
              'min-w-0 flex-1 cursor-pointer truncate text-left hover:text-accent-deep',
              u.isDone && 'text-muted line-through',
            )}
          >
            {u.title}
          </button>
        )}
        {u.markiertName && !bearbeiten && (
          <span
            title={`${u.markiertName} ist hier markiert`}
            className="shrink-0 rounded-md bg-accent-soft px-1.5 py-px text-[10.5px] font-semibold text-accent-deep"
          >
            @{u.markiertName}
          </span>
        )}
        <span className="ml-auto flex shrink-0 items-center gap-1.5 text-[11px] text-muted">
          {/* Wer abgehakt hat, und wann – Esther: „cool zu sehen, wann er das abgehakt hat". */}
          {u.isDone && u.doneAt && (
            <span title={`Abgehakt von ${u.doneByName ?? 'unbekannt'}`}>
              ✓ {u.doneByName ?? 'unbekannt'} · {datum(u.doneAt)}
            </span>
          )}
          {kommentare.length > 0 && (
            <button
              type="button"
              onClick={() => setOffen(true)}
              aria-label={`Kommentare zu „${u.title}"`}
              className="cursor-pointer hover:text-accent-deep"
            >
              💬 {kommentare.length}
            </button>
          )}
          {!bearbeiten && (
            <button
              type="button"
              onClick={() => setOffen((v) => !v)}
              aria-label={`${u.title} ${offen ? 'zuklappen' : 'aufklappen'}`}
              className={cn(
                'cursor-pointer px-0.5 hover:text-accent-deep',
                !hatMehr && !offen && 'opacity-60',
              )}
            >
              {offen ? <ChevronDown size={13} /> : <ChevronRight size={13} />}
            </button>
          )}
        </span>
      </div>

      {(offen || bearbeiten) && (
        <div className="mb-2 ml-5">
          {bearbeiten ? (
            <>
              <MarkdownFeld
                wert={beschreibung}
                setzen={setBeschreibung}
                zeilen={3}
                beiAbsenden={() => speichern.mutate()}
              />
              <div className="mt-1.5 flex items-center gap-1.5">
                <Button
                  variant="primary"
                  size="sm"
                  disabled={!titel.trim() || speichern.isPending}
                  onClick={() => speichern.mutate()}
                >
                  Speichern
                </Button>
                <Button size="sm" onClick={() => setBearbeiten(false)}>
                  Abbrechen
                </Button>
              </div>
            </>
          ) : (
            <>
              {u.beschreibung && (
                <div className="rounded-md border border-line bg-page px-2 py-1 text-[12px] text-ink2">
                  <Markdown text={u.beschreibung} kompakt />
                </div>
              )}
              <div className="mt-1 flex flex-wrap items-center gap-x-3 gap-y-1 text-[11px] text-muted">
                {ausKommentar && (
                  <button
                    type="button"
                    onClick={() => springeZu(`kommentar-${ausKommentar.id}`)}
                    className="cursor-pointer hover:text-accent-deep hover:underline"
                  >
                    aus Kommentar von {ausKommentar.authorName ?? 'unbekannt'} ·{' '}
                    {datum(ausKommentar.createdAt)}
                  </button>
                )}
                {!gast && (
                  <button
                    type="button"
                    onClick={() => {
                      setTitel(u.title)
                      setBeschreibung(u.beschreibung ?? '')
                      setBearbeiten(true)
                    }}
                    aria-label={`${u.title} bearbeiten`}
                    className="cursor-pointer hover:text-accent-deep hover:underline"
                  >
                    ✎ bearbeiten
                  </button>
                )}
                {!gast && !markiertWahl && (
                  <button
                    type="button"
                    onClick={() => setMarkiertWahl(true)}
                    aria-label={`Jemanden an „${u.title}" markieren`}
                    className="cursor-pointer hover:text-accent-deep hover:underline"
                  >
                    @ {u.markiertName ? 'anders markieren' : 'markieren'}
                  </button>
                )}
                {!gast && markiertWahl && (
                  <select
                    autoFocus
                    aria-label={`Wen an „${u.title}" markieren`}
                    defaultValue={u.markiertPersonId ?? ''}
                    disabled={markieren.isPending}
                    onChange={(e) => markieren.mutate(e.target.value || null)}
                    onBlur={() => setMarkiertWahl(false)}
                    className="rounded-md border border-line2 bg-surface px-1.5 py-px text-[11px] text-ink"
                  >
                    <option value="">niemand</option>
                    {personen
                      .filter((p) => p.isActive)
                      .sort((a, b) => a.displayName.localeCompare(b.displayName, 'de'))
                      .map((p) => (
                        <option key={p.id} value={p.id}>
                          {p.displayName}
                        </option>
                      ))}
                  </select>
                )}
                {!gast && u.markiertName && !markiertWahl && (
                  <button
                    type="button"
                    onClick={() => markieren.mutate(null)}
                    className="cursor-pointer hover:text-accent-deep hover:underline"
                  >
                    Markierung entfernen
                  </button>
                )}
                {!gast && (
                  <button
                    type="button"
                    onClick={loeschen}
                    aria-label={`${u.title} entfernen`}
                    className="cursor-pointer hover:text-pool-sofort hover:underline"
                  >
                    ✕ entfernen
                  </button>
                )}
              </div>
            </>
          )}

          {!bearbeiten && (
            <div className="mt-1.5 border-l-2 border-line2 pl-3">
              {kommentare.map((k) => (
                <div key={k.id} className="py-1">
                  <div className="text-[11px] text-muted">
                    <strong className="text-ink2">{k.authorName ?? 'unbekannt'}</strong>{' '}
                    {datum(k.createdAt)}
                  </div>
                  <div className="text-[12px]">
                    <Markdown text={k.body} kompakt />
                  </div>
                </div>
              ))}
              {!gast && (
                <form
                  onSubmit={(e) => {
                    e.preventDefault()
                    if (neuer.trim()) kommentieren.mutate()
                  }}
                  className="mt-1"
                  aria-label={`Kommentar zu „${u.title}" schreiben`}
                >
                  <MarkdownFeld
                    wert={neuer}
                    setzen={setNeuer}
                    zeilen={2}
                    erwaehnbar={erwaehnbar}
                    beiAbsenden={() => {
                      if (neuer.trim()) kommentieren.mutate()
                    }}
                  />
                  <div className="mt-1 flex items-center gap-1.5">
                    <Button
                      type="submit"
                      size="sm"
                      disabled={!neuer.trim() || kommentieren.isPending}
                    >
                      Kommentieren
                    </Button>
                    <span className="text-[11px] text-muted">
                      bleibt in Work – geht nicht ins Ticket
                    </span>
                  </div>
                </form>
              )}
            </div>
          )}
        </div>
      )}
    </div>
  )
}

function Kommentarzeile({
  kommentar: k,
  neuLaden,
  beiFehler,
  antworten,
  antwortetGerade,
  alsUnteraufgabe,
  unteraufgabe,
}: {
  /** Die Unteraufgabe, die aus diesem Kommentar entstand (E-340) – dann Marke statt Knopf. */
  unteraufgabe?: { id: string; title: string; isDone: boolean } | null
  /**
   * **Aus diesem Kommentar eine Unteraufgabe machen** (E-338) – `undefined`, wenn der Aufrufer
   * keine anlegen darf. Der Titel kommt aus der ersten Zeile, siehe `unteraufgabenTitel`.
   */
  alsUnteraufgabe?: ((kommentarId: string) => void) | undefined
  /** Setzt das Antwortziel – das Formular springt daraufhin unter diesen Kommentar (E-329). */
  antworten: () => void
  /**
   * **Läuft die Antwort gerade an diesen Kommentar?** Dann steht das Formular direkt darunter,
   * und der Knopf wäre eine zweite Einladung zu etwas, das schon offen ist – er wird zur
   * Zustandsanzeige ([E-329](../../../../docs/ENTSCHEIDUNGEN.md#e-329)).
   */
  antwortetGerade?: boolean
  kommentar: {
    id: string
    body: string
    createdAt: Date | string
    authorPersonId: string | null
    authorName: string | null
    quelle: string | null
    aenderbar: boolean
    bearbeitetAm: Date | string | null
    /** `true`: aus dem Ticket, und **der Melder liest mit** (E-321). */
    oeffentlich: boolean
    /** Nur der Anfang – das Ganze steht im Quellsystem (E-321). */
    gekuerzt: boolean
    /**
     * **Der Text ist der Betreff oder Dateiname einer Mail, nicht ihr Inhalt**
     * ([E-330](../../../../docs/ENTSCHEIDUNGEN.md#e-330)). Der steckt in der Datei im Ticket.
     */
    maildatei: boolean
    /** Worauf dieser Kommentar antwortet – `null` bei einem gewöhnlichen (E-322). */
    antwortAuf: string | null
    subtaskId?: string | null
  }
  neuLaden: () => void
  beiFehler: (e: Error) => void
}) {
  const { data: sitzung } = useQuery(sitzungQuery)
  /**
   * **Auch beim Bearbeiten erwähnt `@` jemanden** (Nils, 16.09.2026). Der Server benachrichtigt
   * nur, wer neu dazukommt – eine schon genannte Person bekommt keine zweite Mail.
   */
  const erwaehnbar = useErwaehnbarePersonen()
  const [bearbeiten, setBearbeiten] = useState(false)
  const [entwurf, setEntwurf] = useState('')

  const speichern = useMutation({
    mutationFn: () => trpc.tasks.kommentarBearbeiten.mutate({ id: k.id, body: entwurf }),
    onSuccess: () => {
      setBearbeiten(false)
      neuLaden()
    },
    onError: beiFehler,
  })

  const meiner = sitzung?.id !== undefined && k.authorPersonId === sitzung.id

  return (
    <div
      /**
       * **Ein externer Eintrag sieht anders aus, nicht nur anders beschriftet**
       * ([E-332](../../../../docs/ENTSCHEIDUNGEN.md#e-332), 04.09.2026).
       *
       * Nils, mit Bildschirmfoto: *„das sieht man nicht, dass das extern ist. Das solltest du
       * doch besser kennzeichnen."* Die Marke allein war zu wenig – sie steht am Ende einer
       * Zeile mit Name, Datum und Herkunft, also genau dort, wo man beim Lesen nicht hinsieht.
       *
       * Jetzt trägt der **ganze Eintrag** die Farbe: roter Balken links, leicht getönte
       * Fläche. Das ist es, was Nils am 04.09. mit *„und die dann rot ist"* meinte – nicht ein
       * rotes Wort, sondern eine rote Zeile.
       */
      id={`kommentar-${k.id}`}
      className={
        k.oeffentlich
          ? 'my-1 rounded-r-lg border-l-[3px] border-pool-sofort bg-pool-sofort-bg/60 py-2 pr-2 pl-2.5'
          : 'border-b border-line py-2 last:border-b-0'
      }
    >
      <div className="flex items-baseline gap-2 text-[11px] text-muted">
        <strong className="text-ink2">{k.authorName ?? 'unbekannt'}</strong>
        {new Date(k.createdAt).toLocaleString('de-DE', {
          day: '2-digit',
          month: '2-digit',
          hour: '2-digit',
          minute: '2-digit',
        })}
        {/**
         * **Die Herkunft steht an der Zeile, nicht in der Sortierung.** Ein Kommentar aus
         * GitLab und einer aus Work gehören in dieselbe Unterhaltung (Konzept Kap. 6.2) – wer
         * sie liest, soll sie nicht aus zwei Spalten zusammensetzen müssen. Aber *woher* er
         * kam, ändert, wie man ihn liest: im Quellsystem sehen ihn auch Kunden.
         */}
        {k.quelle && <SourceBadge source={k.quelle} />}
        {/**
         * **„Extern" heißt: der Melder liest mit** ([E-321](../../../../docs/ENTSCHEIDUNGEN.md#e-321),
         * 04.09.2026).
         *
         * Nils: *„sodass da dann bspw «NetCon Ticket : Extern» steht und die dann rot ist."*
         *
         * Die Herkunftsmarke daneben sagt **woher**, diese sagt **wer es sieht** – und das ist
         * die Angabe, die eine Antwort richtig oder falsch macht. Rot ist sie nicht als
         * Warnung vor einem Fehler, sondern weil es die einzige Farbe in dieser Liste ist, die
         * man nicht überliest.
         *
         * Interne Einträge tragen **keine** Gegenmarke: sie sind der Normalfall, und eine
         * Marke an jeder Zeile ist keine Marke mehr.
         */}
        {k.oeffentlich && (
          <span
            className="rounded-md bg-pool-sofort px-1.5 py-[1px] text-[10px] font-bold tracking-[0.04em] text-white uppercase"
            title="Öffentlich im Ticket – der Melder sieht diesen Eintrag."
          >
            Extern
          </span>
        )}
        {k.bearbeitetAm && (
          <span title={`Zuletzt geändert am ${new Date(k.bearbeitetAm).toLocaleString('de-DE')}`}>
            · bearbeitet
          </span>
        )}
        {unteraufgabe && (
          /* Die Verbindung in die eine Richtung (E-340) – klein, einzeilig, mit Stand (E-341);
             der Titel steht im Tooltip. Die andere Richtung steht an der Unteraufgabe. */
          <button
            type="button"
            onClick={() => springeZu(`unteraufgabe-${unteraufgabe.id}`)}
            title={`Daraus wurde die Unteraufgabe „${unteraufgabe.title}"${unteraufgabe.isDone ? ' – erledigt' : ''}`}
            className={cn(
              'shrink-0 cursor-pointer rounded px-1.5 py-[1px] text-[10.5px] font-semibold hover:underline',
              unteraufgabe.isDone ? 'bg-page text-muted' : 'bg-accent-soft text-accent-deep',
            )}
          >
            {unteraufgabe.isDone ? '✓' : '↗'} Unteraufgabe
          </button>
        )}
        <span className="ml-auto flex shrink-0 items-center gap-2">
          {/**
           * **„Antworten" steht an jedem Kommentar** (E-322) – auch an denen aus dem
           * Quellsystem: gerade auf eine Kundenmail will man antworten, und in Work
           * geschrieben geht sie über das Rückschreiben ohnehin dorthin zurück.
           */}
          <button
            type="button"
            onClick={antworten}
            aria-expanded={antwortetGerade ?? false}
            className={
              antwortetGerade
                ? 'rounded bg-accent-soft px-1.5 py-0.5 text-[11px] font-semibold text-accent-deep'
                : 'cursor-pointer text-[11px] text-accent-deep hover:underline'
            }
          >
            {antwortetGerade ? 'antwortet …' : 'antworten'}
          </button>
          {alsUnteraufgabe &&
            !unteraufgabe &&
            !k.antwortAuf &&
            !bearbeiten &&
            unteraufgabenTitel(k.body) !== '' && (
              <button
                type="button"
                onClick={() => alsUnteraufgabe(k.id)}
                title={`Als Unteraufgabe anlegen – der Kommentar wird die Beschreibung, vorläufiger Titel: „${unteraufgabenTitel(k.body)}"`}
                className="cursor-pointer text-[11px] text-accent-deep hover:underline"
              >
                → Unteraufgabe
              </button>
            )}
          {meiner && k.aenderbar && !bearbeiten && (
            <button
              type="button"
              onClick={() => {
                setEntwurf(k.body)
                setBearbeiten(true)
              }}
              className="cursor-pointer text-[11px] text-accent-deep hover:underline"
            >
              bearbeiten
            </button>
          )}
        </span>
      </div>

      {bearbeiten ? (
        <div className="mt-1">
          <MarkdownFeld
            wert={entwurf}
            setzen={setEntwurf}
            zeilen={4}
            erwaehnbar={erwaehnbar}
            beiAbsenden={() => speichern.mutate()}
          />
          <div className="mt-1.5 flex flex-wrap items-center gap-1.5">
            <Button
              variant="primary"
              disabled={!entwurf.trim() || speichern.isPending}
              onClick={() => speichern.mutate()}
            >
              Speichern
            </Button>
            <Button onClick={() => setBearbeiten(false)}>Abbrechen</Button>
            {/**
             * **Die Folge steht dran, bevor sie eintritt.** 49 von 49 Work-Kommentaren an
             * GitLab-Aufgaben stehen als Notiz im Issue – die Änderung geht dorthin mit. Wer
             * das erst hinterher erfährt, hat es nicht gewollt.
             */}
            <span className="text-[11px] text-muted">
              Die Änderung wird ins Quellsystem nachgezogen, wenn der Kommentar dort steht.
            </span>
          </div>
        </div>
      ) : (
        <div className="mt-1 text-[12.5px]">
          <Markdown text={k.body} kompakt />
          {/**
           * **Der Lesemodus steht unter dem Kommentar, nicht in der Kopfzeile** (E-384). Oben
           * liegen Name, Datum, Herkunft, „antworten" und „bearbeiten" – ein sechstes Ding
           * dort fände niemand. Und gebraucht wird er erst, nachdem man gesehen hat, wie lang
           * der Text ist.
           */}
          <LesemodusKnopf
            text={k.body}
            titel={`Kommentar von ${k.authorName ?? 'unbekannt'}`}
            /** Im Lesemodus liegt die Kopfzeile des Kommentars hinter der Ebene – also steht sie oben drin. */
            meta={new Date(k.createdAt).toLocaleString('de-DE', {
              day: '2-digit',
              month: '2-digit',
              year: 'numeric',
              hour: '2-digit',
              minute: '2-digit',
            })}
          />
          {/**
           * **Ein abgeschnittener Text sagt, dass er abgeschnitten ist** (E-321). Ohne diesen
           * Satz liest man einen Mailverlauf, der mitten im Wort endet, und hält ihn für
           * vollständig – oder für kaputt.
           */}
          {/**
           * **Bei einer Maildatei steht hier der Betreff, nicht die Mail** (E-330) – und dann
           * ist „gekürzt" die falsche Auskunft: nichts ist abgeschnitten, es ist nur nicht der
           * Text. Deshalb schließen die beiden Sätze einander aus.
           */}
          {k.maildatei ? (
            <p className="mt-1 text-[11px] text-muted italic">
              Nachricht des Melders – Betreff bzw. Dateiname. Der Text steht im Ticket.
            </p>
          ) : (
            k.gekuerzt && (
              <p className="mt-1 text-[11px] text-muted italic">
                Gekürzt – der vollständige Verlauf steht im Ticket.
              </p>
            )
          )}
        </div>
      )}
    </div>
  )
}

/**
 * **Bis wann auf die Antwort gewartet wird** ([E-315](../../../../docs/ENTSCHEIDUNGEN.md#e-315),
 * 02.09.2026).
 *
 * Aus Ticket NCW-6X848M: *„Es fehlt ein Status «Warten auf Antwort» oder so."*
 *
 * Die Wiedervorlage ist das, was diesen Status vom Parken unterscheidet: beim Parken legt man
 * etwas weg, hier behält man einen Faden in der Hand. Am gesetzten Tag steht die Aufgabe ganz
 * vorn im Vorlauf ([E-357](../../../../docs/ENTSCHEIDUNGEN.md#e-357)).
 *
 * **Tage statt Kalender** – seit E-357 „in 1 … 5 Tagen" plus eine und zwei Wochen, dieselben
 * Zahlen wie am Kommentarfeld. Nils nutzt das Warten für kurze Schleifen („angefragt, in drei
 * Tagen nachsehen"); „nächsten Monat" ging über das freie Feld daneben ohnehin schneller.
 */
/** „1 T … 5 T", dann eine und zwei Wochen – siehe `WartenBis`. */
const WARTEN_VORGABEN = [
  ...WIEDERVORLAGE_TAGE.map((t) => ({ schluessel: `t${t}`, label: `${t} T`, tage: t })),
  { schluessel: 'woche', label: '1 Wo', tage: 7 },
  { schluessel: 'zweiwochen', label: '2 Wo', tage: 14 },
]

function WartenBis({
  taskId,
  wartenBis,
  darfAendern,
  beiFehler,
}: {
  taskId: string
  wartenBis: string | null
  darfAendern: boolean
  beiFehler: (e: Error) => void
}) {
  const setzen = useStatusSetzen()

  const speichern = (tag: string | null) => {
    setzen.mutate(
      { id: taskId, status: 'warten', wartenBis: tag },
      { onError: (fehler) => beiFehler(fehler as Error) },
    )
  }

  if (!darfAendern) {
    return (
      <span className="basis-full text-[11.5px] text-muted">
        {wartenBis
          ? `Nachfassen am ${new Date(`${wartenBis}T12:00:00`).toLocaleDateString('de-DE')}`
          : 'Ohne Wiedervorlage – es erinnert nichts daran.'}
      </span>
    )
  }

  return (
    <div className="basis-full">
      <div className="flex flex-wrap items-center gap-1.5 text-[11.5px]">
        <span className="text-muted">Nachfassen</span>
        {WARTEN_VORGABEN.map((v) => {
          const tag = vorgabeZuTag(v.tage, new Date())
          return (
            <button
              key={v.schluessel}
              type="button"
              onClick={() => speichern(tag)}
              className={`cursor-pointer rounded-md border px-2 py-[2px] ${
                wartenBis === tag
                  ? 'border-accent bg-accent-soft text-accent-deep'
                  : 'border-line2 text-ink2 hover:bg-[#f3f2ef]'
              }`}
            >
              {v.label}
            </button>
          )
        })}
        <input
          type="date"
          value={wartenBis ?? ''}
          aria-label="Nachfassen am"
          onChange={(e) => speichern(e.target.value || null)}
          className="rounded-md border border-line2 bg-surface px-1.5 py-0.5 text-[11.5px]"
        />
        {wartenBis && (
          <button
            type="button"
            onClick={() => speichern(null)}
            className="cursor-pointer text-[11px] text-muted hover:text-pool-sofort"
          >
            ohne
          </button>
        )}
      </div>
      {!wartenBis && (
        <p className="mt-1 text-[11px] text-muted">
          Ohne Wiedervorlage erinnert nichts daran – die Aufgabe wartet dann still und kommt nicht
          in den Vorlauf zurück.
        </p>
      )}
    </div>
  )
}

function TeilenKnopf({ taskId }: { taskId: string }) {
  const [kopiert, setKopiert] = useState(false)

  /** Absolut, nicht relativ: eine Adresse, die man weitergibt, muss ohne Kontext funktionieren. */
  const adresse = (() => {
    const u = new URL(window.location.href)
    u.searchParams.set('aufgabe', taskId)
    return u.toString()
  })()

  const kopieren = async () => {
    try {
      await navigator.clipboard.writeText(adresse)
      setKopiert(true)
      window.setTimeout(() => setKopiert(false), 1500)
    } catch {
      /** Ohne Zwischenablage (kein HTTPS): die Adresse wenigstens zeigen. */
      window.prompt('Adresse dieser Aufgabe:', adresse)
    }
  }

  return (
    <>
      <button
        type="button"
        onClick={() => void kopieren()}
        title="Adresse dieser Aufgabe kopieren"
        aria-label={kopiert ? 'Adresse kopiert' : 'Adresse dieser Aufgabe kopieren'}
        className="cursor-pointer rounded-lg px-2 py-1 text-[11.5px] text-ink2 hover:bg-[#f3f2ef]"
      >
        {kopiert ? '✓ kopiert' : '🔗 Link'}
      </button>
      {/**
       * **Das bekannte Zeichen statt eines Pfeils** (Nils, 04.09.2026: *„das «Pfeil-Symbol» …
       * mit dem Symbol, was für neues Fenster ist (Viereck mit dem Pfeil), das ist denke ich
       * bekannter"*).
       *
       * Er hat recht: ein nackter Pfeil heißt in verschiedenen Programmen verschiedenes –
       * „weiter", „ausklappen", „senden". Das Viereck mit dem herauszeigenden Pfeil heißt seit
       * Jahren genau eine Sache, und man muss sie nicht lesen.
       */}
      <a
        href={adresse}
        target="_blank"
        rel="noreferrer"
        title="In einem neuen Tab öffnen"
        aria-label="In einem neuen Tab öffnen"
        className="flex cursor-pointer items-center rounded-lg px-2 py-1 text-ink2 hover:bg-[#f3f2ef]"
      >
        <ExternalLink className="size-[13px]" />
      </a>
    </>
  )
}

/**
 * **Das Datumsfeld speichert beim Verlassen, nicht beim ersten Tastendruck** (09.09.2026,
 * [E-337](../../../../docs/ENTSCHEIDUNGEN.md#e-337)).
 *
 * Christian Hunhoff: *„Wenn man die Fälligkeit … ändert, funktioniert die Tastatureingabe
 * nicht. Bzw. die Eingabe wird direkt nach dem Ändern der ersten Zahl übernommen."* Genau so
 * war es gebaut: `onChange` speicherte und schloss. Ein Datumsfeld meldet aber jede gültige
 * Zwischenstufe – wer „11" in den Tag tippt, hatte den 11. des alten Monats gespeichert, bevor
 * er den Monat erreichte. Jetzt gilt: **Enter oder Verlassen übernimmt, Escape verwirft.**
 * Die Kalenderauswahl per Maus läuft über denselben Weg: wer danach woanders hinklickt,
 * verlässt das Feld.
 */
function FaelligEingabe({
  start,
  laeuft,
  uebernehmen,
  children,
}: {
  start: string
  laeuft: boolean
  uebernehmen: (wert: string) => void
  children?: ReactNode
}) {
  const [entwurf, setEntwurf] = useState(start)
  return (
    <div className="flex items-center gap-1.5">
      <input
        type="date"
        autoFocus
        value={entwurf}
        disabled={laeuft}
        aria-label="Fällig am"
        onChange={(e) => setEntwurf(e.target.value)}
        /**
         * **Focus moving to a sibling (the "löschen" button) is not leaving the field.** Committing
         * here would close the editor and unmount the button before its click lands – the
         * delete silently did nothing.
         */
        onBlur={(e) => {
          if (e.currentTarget.parentElement?.contains(e.relatedTarget)) return
          uebernehmen(entwurf)
        }}
        onKeyDown={(e) => {
          if (e.key === 'Enter') {
            e.preventDefault()
            uebernehmen(entwurf)
          } else if (e.key === 'Escape') {
            e.preventDefault()
            uebernehmen(start)
          }
        }}
        className="rounded-md border border-line2 bg-surface px-1.5 py-0.5 text-[12px]"
      />
      {children}
    </div>
  )
}

function FaelligZeile({
  taskId,
  dueAt,
  terminWirksam,
  darfAendern,
  neuLaden,
  beiFehler,
}: {
  taskId: string
  dueAt: Date | string | null
  /**
   * **Was für diese Aufgabe wirklich gilt** ([E-308](../../../../docs/ENTSCHEIDUNGEN.md#e-308)) –
   * gerechnet vom Server, damit hier dieselbe Antwort steht wie in „Mein Tag".
   */
  terminWirksam: {
    datum: Date | string
    quelle: 'faellig' | 'meilenstein' | 'zieltermin' | 'zeitfenster'
    herkunft: string | null
  } | null
  darfAendern: boolean
  neuLaden: () => void
  beiFehler: (e: Error) => void
}) {
  const setzen = useMutation({
    mutationFn: (wert: Date | null) => trpc.tasks.bearbeiten.mutate({ id: taskId, dueAt: wert }),
    onSuccess: neuLaden,
    onError: beiFehler,
  })

  const datum = dueAt ? new Date(dueAt) : null

  /**
   * **Der Satz für den Fall „kein eigener Termin, aber es gilt einer".**
   *
   * Nur, wenn der wirksame Termin **nicht** aus der Fälligkeit kommt – sonst stünde dasselbe
   * Datum zweimal in derselben Zeile. Der Meilenstein wird beim Namen genannt: „aus Meilenstein"
   * wäre die halbe Auskunft und die Rückfrage „welchem?" die zwangsläufige Folge.
   */
  const geerbterTermin = (() => {
    if (!terminWirksam || terminWirksam.quelle === 'faellig') return null
    const wann = new Date(terminWirksam.datum).toLocaleDateString('de-DE')
    if (terminWirksam.quelle === 'meilenstein') {
      return terminWirksam.herkunft
        ? `es gilt der Meilenstein „${terminWirksam.herkunft}": ${wann}`
        : `es gilt der Meilenstein: ${wann}`
    }
    if (terminWirksam.quelle === 'zieltermin')
      return `es gilt der Zieltermin des Vorhabens: ${wann}`
    return `es gilt das Zeitfenster: ${wann}`
  })()

  /** `toISOString` wäre wieder UTC – hier zählt der Tag, den der Mensch sieht. */
  const fuerFeld = datum
    ? `${datum.getFullYear()}-${String(datum.getMonth() + 1).padStart(2, '0')}-${String(datum.getDate()).padStart(2, '0')}`
    : ''

  return (
    <div className="flex gap-2 border-b border-line py-1 last:border-b-0">
      <dt className="w-[112px] shrink-0 text-muted max-sm:w-[88px]">Fällig</dt>
      <dd className="min-w-0 flex-1">
        <InlineWert
          wert={datum ? datum.toLocaleDateString('de-DE') : null}
          /**
           * **Ohne eigenen Termin steht hier, welcher stattdessen gilt**
           * ([E-308](../../../../docs/ENTSCHEIDUNGEN.md#e-308), 02.09.2026).
           *
           * „kein Termin" war die falsche Auskunft, sobald ein Meilenstein oder ein Zieltermin
           * greift: die Aufgabe hatte sehr wohl einen, nur nicht hier. Nils las an derselben
           * Aufgabe „kein Termin" und in „Mein Tag" „5 Tage überfällig".
           */
          leer={geerbterTermin ? `kein eigener – ${geerbterTermin}` : 'kein Termin'}
          darfAendern={darfAendern}
          bezeichnung="Fälligkeit"
        >
          {(fertig) => (
            <FaelligEingabe
              start={fuerFeld}
              laeuft={setzen.isPending}
              uebernehmen={(wert) => {
                if (wert !== fuerFeld) setzen.mutate(wert ? new Date(`${wert}T12:00:00`) : null)
                fertig()
              }}
            >
              {datum && (
                <button
                  type="button"
                  /** Keep focus in the date input – Safari never focuses buttons on click. */
                  onMouseDown={(e) => e.preventDefault()}
                  onClick={() => {
                    setzen.mutate(null)
                    fertig()
                  }}
                  className="cursor-pointer text-[11px] text-muted hover:text-pool-sofort"
                >
                  löschen
                </button>
              )}
            </FaelligEingabe>
          )}
        </InlineWert>
      </dd>
    </div>
  )
}

/**
 * Hier stand bis zum 01.09.2026 `KlonZeile` – das Feld „Arbeitsklon" aus Phase 9.
 *
 * Nils hat es zweimal abbestellt. Am 28.08.2026: *„Aus den Aufgabendetails kann der «Arbeitsklon»
 * raus. Das Feature brauch ich nicht mehr. (gar nicht mehr)"* – ausgebaut samt Spalte
 * ([E-279](../../../../docs/ENTSCHEIDUNGEN.md#e-279)). Es kam durch eine parallele Sitzung wieder
 * herein, die auf einem älteren Stand aufsetzte, und stand am 01.09.2026 wieder im
 * Aufgabendetail. Nils: *„Arbeitsklon ist immer noch vorhanden … Sorge dafür das das überall
 * rausfliegt, das feature haben wir herausgenommen."* Endgültig weg –
 * [E-297](../../../../docs/ENTSCHEIDUNGEN.md#e-297).
 *
 * Was bleibt, ist [E-90](../../../../docs/ENTSCHEIDUNGEN.md#e-90): die Fork-Sandbox ist nur ein
 * Klon, aus dem nichts zurückführt. Ein Feld dafür braucht es nicht.
 */

/**
 * **Die Minutenschätzung – die es weiterhin gibt, nur selten** (19.08.2026).
 *
 * Geschätzt wird in Work seit dem 17.08. mit *klein, mittel, groß*
 * ([E-78](../../../../docs/ENTSCHEIDUNGEN.md)); die Minuten stehen daneben, weil sie aus
 * NetCon-Tickets kommen können und niemand eine vorhandene Zahl verlieren soll. Änderbar
 * gehört sie trotzdem: eine Zahl, die man sieht und nicht korrigieren kann, ist schlimmer
 * als keine.
 *
 * Deshalb **Stunden im Feld, Minuten in der Datenbank**: „90 Minuten" tippt niemand, „1,5"
 * schon. Krumme Werte bleiben erhalten – gerundet wird nur die Anzeige, nicht der Bestand.
 */
function SchaetzungZeile({
  taskId,
  minuten,
  darfAendern,
  neuLaden,
  beiFehler,
}: {
  taskId: string
  minuten: number | null
  darfAendern: boolean
  neuLaden: () => void
  beiFehler: (e: Error) => void
}) {
  const setzen = useMutation({
    mutationFn: (wert: number | null) =>
      trpc.tasks.bearbeiten.mutate({ id: taskId, estimateMinutes: wert }),
    onSuccess: neuLaden,
    onError: beiFehler,
  })

  const speichern = (eingabe: string, fertig: () => void) => {
    const text = eingabe.trim().replace(',', '.')
    const stunden = text === '' ? null : Number(text)
    if (stunden !== null && (!Number.isFinite(stunden) || stunden <= 0)) {
      beiFehler(new Error('Die Schätzung braucht eine Zahl größer als null – oder gar nichts.'))
      return
    }
    const neu = stunden === null ? null : Math.round(stunden * 60)
    if (neu !== minuten) setzen.mutate(neu)
    fertig()
  }

  const anzeige =
    minuten === null
      ? null
      : minuten < 60
        ? `${minuten} Minuten`
        : `${(minuten / 60).toLocaleString('de-DE', { maximumFractionDigits: 1 })} Stunden`

  return (
    <div className="flex gap-2 border-b border-line py-1 last:border-b-0">
      <dt className="w-[112px] shrink-0 text-muted max-sm:w-[88px]">Schätzung</dt>
      <dd className="min-w-0 flex-1">
        <InlineWert wert={anzeige} leer="keine" darfAendern={darfAendern} bezeichnung="Schätzung">
          {(fertig) => (
            <SchaetzungFeld
              start={
                minuten === null
                  ? ''
                  : String(Math.round((minuten / 60) * 10) / 10).replace('.', ',')
              }
              speichern={(eingabe) => speichern(eingabe, fertig)}
              pending={setzen.isPending}
            />
          )}
        </InlineWert>
      </dd>
    </div>
  )
}

/**
 * Eigener Baustein, damit der Entwurf **im Feld** lebt und nicht in der Zeile darüber: so
 * beginnt er bei jedem Aufklappen beim gespeicherten Wert, ohne dass ihn jemand
 * zurücksetzen müsste. Ein Entwurf, der ein Aufklappen überdauert, ist der Anfang der Frage
 * „warum steht da noch die Zahl von vorhin".
 */
/**
 * **Beschriftung, Platzhalter und Breite standen bis zum 01.09.2026 als Parameter hier**
 * ([E-297](../../../../docs/ENTSCHEIDUNGEN.md#e-297)).
 *
 * Sie kamen am 28.08.2026 dazu, damit der „Arbeitsklon" dasselbe Textfeld benutzen konnte, ohne
 * sich Bildschirmlesern als „Schätzung in Stunden" vorzustellen. Mit dem Feld sind sie weg: ein
 * Parameter mit genau einem möglichen Wert beschreibt keine Wahl, sondern eine vergessene
 * Aufräumarbeit.
 */
function SchaetzungFeld({
  start,
  speichern,
  pending,
}: {
  start: string
  speichern: (eingabe: string) => void
  pending: boolean
}) {
  const [entwurf, setEntwurf] = useState(start)

  return (
    <div className="flex items-center gap-1.5">
      <input
        type="text"
        inputMode="decimal"
        autoFocus
        value={entwurf}
        disabled={pending}
        aria-label="Schätzung in Stunden"
        placeholder="z. B. 1,5"
        onChange={(e) => setEntwurf(e.target.value)}
        onKeyDown={(e) => {
          if (e.key === 'Enter') {
            e.preventDefault()
            speichern(entwurf)
          }
        }}
        onBlur={() => speichern(entwurf)}
        className="w-[70px] rounded-md border border-line2 bg-surface px-1.5 py-0.5 text-[12px]"
      />
      <span className="text-[11px] text-muted">Stunden – leer heißt: keine</span>
    </div>
  )
}

function Zeile({ bezeichnung, wert }: { bezeichnung: string; wert: string }) {
  return (
    <div className="flex gap-2 border-b border-line py-1 last:border-b-0">
      <dt className="w-[112px] shrink-0 text-muted max-sm:w-[88px]">{bezeichnung}</dt>
      <dd className="min-w-0 break-words">{wert}</dd>
    </div>
  )
}

/**
 * **Der Satz, der zum Statuswechsel dazugeschrieben wurde** (21.09.2026,
 * [E-367](../../../../docs/ENTSCHEIDUNGEN.md#e-367)).
 *
 * Getrennt von `verlaufsText`, weil er **keine Ergänzung der Zeile** ist, sondern eine eigene:
 * die Zeile sagt, was passiert ist, die Notiz sagt es mit den Worten eines Menschen.
 * Aneinandergehängt wäre ein langer Satz hinter „In Arbeit → Review/Abnahme" nicht mehr als
 * Zitat zu erkennen – und genau das ist er.
 *
 * ## Warum nicht alles in Anführungszeichen steht
 *
 * In `payload.notiz` schreiben **zwei** Absender. Menschen über `StatusNotiz` – das sind die
 * Review-Sätze, um die es Nils geht. Und Work selbst: *„Geparkt – nur zur Kenntnis"*,
 * *„Aus dem Parkplatz zurückgeholt"*, *„Ohne Bearbeiter in den Vorrat gelegt"*. Die in
 * Anführungszeichen zu setzen hieße, Work einen Menschen sprechen zu lassen – dieselbe
 * Fehlerklasse wie das *„(ohne Vermerk)"* weiter oben, nur andersherum.
 *
 * Unterschieden wird über **`notizFrage` selbst**, nicht über eine zweite Liste von
 * Übergängen: wo eine Frage gestellt wurde, hat ein Mensch geantwortet. Kommt ein vierter
 * Übergang dazu, stimmt diese Stelle ohne Zutun mit.
 */
function verlaufsNotiz(e: {
  type: string
  payload?: unknown
}): { text: string; zitat: boolean } | null {
  if (!e.payload || typeof e.payload !== 'object') return null
  const p = e.payload as { notiz?: unknown; von?: unknown; nach?: unknown }
  const text = typeof p.notiz === 'string' ? p.notiz.trim() : ''
  if (text === '') return null

  const nach = typeof p.nach === 'string' ? (p.nach as TaskStatus) : null
  const von = typeof p.von === 'string' ? (p.von as TaskStatus) : undefined
  /** `hatAbnehmer: true`: ohne Abnehmer wird nicht gefragt – dann gäbe es hier nichts zu zeigen. */
  const zitat = e.type === 'status_changed' && nach !== null && notizFrage(nach, von, true) !== null
  return { text, zitat }
}

/** Bei Statuswechseln „Bereit → In Arbeit" statt nur „Status geändert". */
function verlaufsText(
  e: { type: string; payload?: unknown },
  poolName: (id: string) => string = (id) => id,
  weltName: (id: string) => string = (id) => id,
): string {
  const label = (TASK_EVENT_TYPE_LABELS as Record<string, string | undefined>)[e.type] ?? e.type

  /**
   * **Der Umzug in eine andere Arbeitswelt** (Z-07.17) – „Arbeitswelt gewechselt" allein wäre
   * die halbe Auskunft. Die Frage, die diese Zeile beantworten muss, lautet *wo war sie
   * vorher* – bei einer Aufgabe, die plötzlich in „Marketing" auftaucht, ist das die einzige,
   * die jemand hat.
   */
  if (e.type === 'workspace_changed' && e.payload && typeof e.payload === 'object') {
    const p = e.payload as { vonWorkspace?: unknown; nachWorkspace?: unknown }
    if (typeof p.nachWorkspace === 'string') {
      const nach = weltName(p.nachWorkspace)
      const von = typeof p.vonWorkspace === 'string' ? weltName(p.vonWorkspace) : null
      return von ? `${von} → ${nach}` : `→ ${nach}`
    }
  }

  /**
   * **Die zusammengeführte Aufgabe** (Z-11) – „Zusammengeführt" allein wäre hier die schlechteste
   * mögliche Auskunft. Diese eine Zeile ist der **einzige** Ort, an dem nach dem Verschmelzen
   * noch steht, dass es die andere Aufgabe je gab: ihre Zeile ist gelöscht, ihr Verlauf hängt
   * jetzt hier und ist vom eigenen nicht mehr zu unterscheiden. Also steht ihr Titel dabei.
   */
  if (e.type === 'merged' && e.payload && typeof e.payload === 'object') {
    const p = e.payload as {
      vonTitel?: unknown
      vonSchluessel?: unknown
      kommentare?: unknown
      verloren?: unknown
    }
    if (typeof p.vonTitel === 'string') {
      const kennung = typeof p.vonSchluessel === 'string' ? ` (${p.vonSchluessel})` : ''
      const n =
        typeof p.kommentare === 'number' && p.kommentare > 0 ? `, ${p.kommentare} Komm.` : ''
      /**
       * **Was nicht übernommen wurde, steht dabei** (E-276). Die Vorschau hat es vorher
       * angekündigt; ohne diese Zeile wäre es danach nirgends mehr nachzulesen, und dann wäre
       * die Ankündigung ein Versprechen gewesen, das nur bis zum Klick hielt.
       */
      const verloren =
        Array.isArray(p.verloren) && p.verloren.length > 0
          ? ` · nicht übernommen: ${p.verloren.map(String).join(', ')}`
          : ''
      return `Aufgenommen: „${p.vonTitel}"${kennung}${n}${verloren}`
    }
  }

  /**
   * **Wer die Aufgabe in den Pool gelegt hat – und warum** (28.08.2026).
   *
   * Drei Wege führen in einen Pool: eine Hand, eine Triage-Regel, der Nachfluss aus einem
   * Vorhaben. Im Verlauf sahen alle drei gleich aus („Pool geändert"), und beim Abnahmegespräch
   * zu Phase 8 hat mich das eine falsche Zahl kosten lassen: ich hielt vierzehn Regeltreffer
   * für Nachflüsse. Seither trägt jedes Ereignis seinen Grund.
   */
  if (e.type === 'pool_changed' && e.payload && typeof e.payload === 'object') {
    const p = e.payload as {
      grund?: unknown
      regel?: unknown
      vorhaben?: unknown
      nachPools?: unknown
    }
    const ziel = Array.isArray(p.nachPools) ? p.nachPools.map((x) => poolName(String(x))) : []
    const wohin = ziel.length > 0 ? ` → ${ziel.join(', ')}` : ''
    if (p.grund === 'nachfluss') {
      const woher = typeof p.vorhaben === 'string' ? ` aus „${p.vorhaben}"` : ''
      return `Nachgeflossen${woher}${wohin}`
    }
    if (typeof p.regel === 'string') return `Regel „${p.regel}"${wohin}`
  }

  /**
   * **Woher die Aufgabe kam** (Nils, 19.08.2026: *„wird denn am Verlauf gespeichert, aus
   * welchem Pool die kamen?"*).
   *
   * Gespeichert war es längst – `payload.ausPools` steht seit Phase 4 im `pulled`-Ereignis und
   * ist die Grundlage des Zurückgebens, das die Aufgabe genau dorthin zurücklegt. Nur
   * **gelesen** hat es niemand: der Verlauf zeigte „Gezogen" und verschwieg das Einzige, was
   * an dieser Zeile erklärungsbedürftig ist.
   *
   * Mit den Beschriftungen der Pools, nicht mit ihren Kennungen: `tickets` steht so in der
   * Datenbank, aber im Verlauf soll stehen, was auch an der Ansicht steht.
   */
  if (
    (e.type === 'pulled' || e.type === 'assigned') &&
    e.payload &&
    typeof e.payload === 'object'
  ) {
    const aus = (e.payload as { ausPools?: unknown }).ausPools
    if (Array.isArray(aus) && aus.length > 0) {
      const namen = aus.filter((p): p is string => typeof p === 'string').map(poolName)
      if (namen.length > 0) return `${label} aus ${namen.join(' und ')}`
    }
  }

  /**
   * **Rückschreib-Vermerke sagen, was sie sind** (Phase 5) – „Bearbeitet" wäre hier
   * nichtssagend, und ausgerechnet diese Zeilen beantworten die Frage „warum steht das in
   * GitLab anders".
   *
   * Der Konflikt (AP 5.5) steht **vorn**: Work hat gewonnen, aber jemand hat etwas
   * verloren, und diese Zeile ist die einzige Spur davon im Verlauf.
   */
  if (e.type === 'updated' && e.payload && typeof e.payload === 'object') {
    const p = e.payload as {
      rueckgeschrieben?: unknown
      zielStatus?: unknown
      zielKonto?: unknown
      konflikt?: { quelleWar?: string; workLas?: string }
    }
    if (typeof p.rueckgeschrieben === 'string') {
      const was =
        p.rueckgeschrieben === 'assignee'
          ? `Zuweisung ${typeof p.zielKonto === 'string' ? p.zielKonto : ''}`.trim()
          : p.rueckgeschrieben === 'comment'
            ? 'Kommentar'
            : p.rueckgeschrieben
      const hinweis = p.konflikt?.quelleWar
        ? ` · überschrieb ${TASK_STATUS_LABELS[p.konflikt.quelleWar as TaskStatus] ?? p.konflikt.quelleWar} aus der Quelle`
        : ''
      return `Nach GitLab geschrieben: ${was}${hinweis}`
    }
  }

  /**
   * **Prioritätswechsel aus dem Ticket-Journal** (18.08.2026) – mit den Worten der Quelle.
   *
   * „Mittel → Hoch" steht so im Ticket. `B → A` wäre die Work-Übersetzung und verlangt, dass
   * jemand die Abbildung im Kopf hat – im Verlauf, der erklären soll, was passiert ist, ist
   * das die falsche Richtung.
   *
   * **Work-eigene Wechsel tragen `von`/`nach`** und werden hier in Work-Buchstaben
   * geschrieben – dort *ist* `A` das Wort, es gibt keine Übersetzung zu verlieren. „ohne"
   * ausgeschrieben, weil ein leerer Platz hinter dem Pfeil wie ein Anzeigefehler aussieht.
   */
  if (e.type === 'priority_changed' && e.payload && typeof e.payload === 'object') {
    const p = e.payload as {
      vonQuelle?: unknown
      nachQuelle?: unknown
      von?: unknown
      nach?: unknown
    }
    if (typeof p.vonQuelle === 'string' && typeof p.nachQuelle === 'string') {
      return `Priorität ${p.vonQuelle} → ${p.nachQuelle}`
    }
    if ('nach' in p) {
      const stufe = (w: unknown) => (typeof w === 'string' ? w : 'ohne')
      return `Priorität ${stufe(p.von)} → ${stufe(p.nach)}`
    }
  }

  /**
   * **Ein Statuswechsel, den die Quelle nie gemeldet hat** (18.08.2026).
   *
   * Er entsteht im Importeur, wenn das Statusfeld der Quelle springt, ohne dass ein
   * Quellereignis ihn erklärt – der Fall von NetCon #37166. Ohne diese Zeile endet der
   * Verlauf auf „Geschlossen", während oben „Bereit" steht.
   *
   * *„ohne Vermerk"* steht ausdrücklich dabei: die Zeile ist eine Schlussfolgerung von Work
   * und keine Meldung der Quelle. Sie so aussehen zu lassen wie ein gemeldeter Wechsel hieße,
   * eine Beobachtung als Beleg auszugeben.
   */
  if (e.type === 'updated' && e.payload && typeof e.payload === 'object') {
    const p = e.payload as { quellwechsel?: { von?: unknown; zu?: unknown } }
    const zu = typeof p.quellwechsel?.zu === 'string' ? p.quellwechsel.zu : null
    if (zu) {
      const von = typeof p.quellwechsel?.von === 'string' ? p.quellwechsel.von : null
      const zuLabel = TASK_STATUS_LABELS[zu as TaskStatus] ?? zu
      const vonLabel = von ? (TASK_STATUS_LABELS[von as TaskStatus] ?? von) : null
      return `Quelle: ${vonLabel ? `${vonLabel} → ` : ''}${zuLabel} (ohne Vermerk)`
    }
  }

  if (e.type !== 'status_changed' || !e.payload || typeof e.payload !== 'object') return label

  const p = e.payload as { von?: unknown; nach?: unknown }
  const nach = typeof p.nach === 'string' ? p.nach : null
  if (!nach) return label
  const von = typeof p.von === 'string' ? p.von : null
  const nachLabel = TASK_STATUS_LABELS[nach as TaskStatus] ?? nach
  const vonLabel = von ? (TASK_STATUS_LABELS[von as TaskStatus] ?? von) : null
  return vonLabel ? `${vonLabel} → ${nachLabel}` : `→ ${nachLabel}`
}

/**
 * **Steht das, was Work sagt, auch im Quellsystem?** (Phase 5, AP 5.5)
 *
 * Vier Zustände, und der vierte ist der wichtige:
 *
 * | | |
 * |---|---|
 * | **in Sync** | Work und Quelle sagen dasselbe |
 * | **wartet** | etwas ist offen und wird beim nächsten Lauf geschrieben |
 * | **gehindert** | etwas ist offen und wird **nie** geschrieben |
 * | **Fehler** | ein Schreibvorgang ist gescheitert |
 *
 * „Gehindert" unter „wartet" zu führen wäre eine Lüge mit Ablaufdatum: es wartet nicht, es
 * passiert nie – weil das Projekt nicht freigegeben ist oder der Status in GitLab kein
 * Label hat. Genau daraus entsteht die Aufgabe, die seit Mai „gleich fertig" ist.
 *
 * Die Zeile erscheint **nur, wenn es etwas zu sagen gibt**. „In Sync" ist der Normalfall
 * und braucht keine Zeile; wer bei jeder Aufgabe „alles in Ordnung" liest, liest es nach
 * zwei Tagen nicht mehr.
 */
function SyncZeile({ taskId }: { taskId: string }) {
  const { data } = useQuery(syncZustandQuery(taskId))
  if (!data || data.zustand === 'sync') return null

  const farbe = data.zustand === 'fehler' ? 'text-pool-sofort' : 'text-ink2'
  const text =
    data.zustand === 'fehler'
      ? 'Fehler beim Zurückschreiben'
      : data.zustand === 'gehindert'
        ? `geht nicht zurück – ${data.text}`
        : `wartet auf Rückschreiben (${data.offen})`

  return (
    <div className="flex gap-2 border-t border-line py-1">
      <dt className="w-[112px] shrink-0 text-muted max-sm:w-[88px]">Quellsystem</dt>
      <dd className={farbe} title={data.text ?? undefined}>
        {text}
      </dd>
    </div>
  )
}
